<template>
  <div class="package-table">
    <!-- Баннер: есть неотправленные в 1С ДС — все действия заблокированы -->
    <q-banner
      v-if="hasUnsentDs"
      class="unsent-ds-banner tw-mb-3"
      dense
      rounded
    >
      <template #avatar>
        <q-icon name="lock" color="white" />
      </template>
      <div class="tw-flex tw-flex-col tw-gap-1">
        <div class="tw-font-semibold">
          Редактирование сметы заблокировано: есть неотправленные в 1С ДС
        </div>
        <div class="tw-text-sm">
          Отправьте следующие ДС в 1С, после этого блокировка снимется:
          <ul class="tw-m-0 tw-pl-5">
            <li
              v-for="ds in unsentDsList"
              :key="ds.contractor_agreement_list_id"
            >
              №{{ ds.agreement_num || ds.contractor_agreement_list_id }}
              <span v-if="ds.document_date" class="tw-text-xs tw-opacity-80">
                ({{ formatDate(ds.document_date) }})
              </span>
              <span v-if="ds.error" class="tw-text-xs tw-text-red-200">
                — {{ ds.error }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </q-banner>

    <!-- Шапка с итогом и кнопками -->
    <div class="smeta-action-bar tw-flex tw-items-center tw-justify-between tw-mb-4">
      <div class="tw-flex tw-items-center tw-gap-3">
        <q-btn
          v-if="hasPendingChanges"
          color="positive"
          icon="save"
          label="Сохранить изменения"
          unelevated
          :disable="isLoading"
          @click="openSaveDialog"
        />
        <q-btn
          v-if="hasPendingChanges"
          flat
          color="negative"
          icon="cancel"
          label="Отменить все"
          :disable="isLoading"
          @click="resetPendingChanges"
        />
        <!-- Кнопка-меню «Обновить цену» (СМР multi-select / ТМЦ multi-select) -->
        <q-btn-dropdown
          v-if="isEditable && !smrSelectionMode && !tmcSelectionMode && !hasPendingChanges"
          color="primary"
          icon="sync"
          label="Обновить цену"
          unelevated
          :disable="isLoading || tmcLoading"
          :loading="tmcLoading"
        >
          <q-list>
            <q-item clickable v-close-popup @click="enterSmrSelectionMode">
              <q-item-section avatar><q-icon name="construction" size="18px" /></q-item-section>
              <q-item-section>Обновить цену СМР</q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="enterTmcSelectionMode">
              <q-item-section avatar><q-icon name="category" size="18px" /></q-item-section>
              <q-item-section>Обновить цену ТМЦ</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
        <!-- Баннер режима выбора СМР -->
        <template v-if="smrSelectionMode">
          <div class="smr-selection-banner">
            <q-icon name="rule" size="18px" />
            <span>Выберите работы для обновления цен СМР. Выбрано: {{ smrSelectedKeys.size }}</span>
          </div>
          <q-btn
            flat
            color="primary"
            :icon="isAllSmrSelected ? 'deselect' : 'select_all'"
            :label="isAllSmrSelected ? 'Снять выбор' : `Выбрать все (${smrSelectableScopeRows.length})`"
            :disable="smrApplying || !smrSelectableScopeRows.length"
            @click="toggleSelectAllSmr"
          >
            <q-tooltip v-if="!smrSelectableScopeRows.length">
              Нет доступных работ для выбора (все заблокированы)
            </q-tooltip>
          </q-btn>
          <q-btn
            color="positive"
            icon="check"
            :label="`Применить (${smrSelectedKeys.size})`"
            unelevated
            :disable="!smrSelectedKeys.size || smrApplying"
            :loading="smrApplying"
            @click="applySmrSelection"
          />
          <q-btn
            flat
            color="negative"
            icon="close"
            label="Отменить"
            :disable="smrApplying"
            @click="exitSmrSelectionMode"
          />
        </template>
        <!-- Баннер режима выбора ТМЦ -->
        <template v-if="tmcSelectionMode">
          <div class="tmc-selection-banner">
            <q-icon name="category" size="18px" />
            <span>Выберите ТМЦ для обновления цен. Выбрано: {{ tmcSelectedKeys.size }}</span>
          </div>
          <q-btn
            flat
            color="teal-8"
            :icon="isAllTmcSelected ? 'deselect' : 'select_all'"
            :label="isAllTmcSelected ? 'Снять выбор' : `Выбрать все (${tmcSelectableScopeRows.length})`"
            :disable="tmcApplying || !tmcSelectableScopeRows.length"
            @click="toggleSelectAllTmc"
          >
            <q-tooltip v-if="!tmcSelectableScopeRows.length">
              Нет ТМЦ с расхождением цен (или все заблокированы)
            </q-tooltip>
          </q-btn>
          <q-btn
            color="positive"
            icon="check"
            :label="`Применить (${tmcSelectedKeys.size})`"
            unelevated
            :disable="!tmcSelectedKeys.size || tmcApplying"
            :loading="tmcApplying"
            @click="applyTmcSelection"
          />
          <q-btn
            flat
            color="negative"
            icon="close"
            label="Отменить"
            :disable="tmcApplying"
            @click="exitTmcSelectionMode"
          />
        </template>
        <!-- Групповое редактирование (из master) -->
        <q-btn-dropdown
          v-if="isEditable && !smrSelectionMode && !tmcSelectionMode && !hasPendingChanges"
          color="secondary"
          icon="edit_note"
          label="Групповое редактирование"
          unelevated
          :disable="isLoading || !data.length"
        >
          <q-list>
            <q-item clickable v-close-popup @click="openBulkPtDialog">
              <q-item-section avatar><q-icon name="swap_horiz" size="18px" /></q-item-section>
              <q-item-section>Изменить тип закупа материала</q-item-section>
            </q-item>
          </q-list>
          <q-list>
            <q-item clickable v-close-popup @click="openBulkNormDialog">
              <q-item-section avatar><q-icon name="straighten" size="18px" /></q-item-section>
              <q-item-section>Обновить норму расхода</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </div>
      <div v-if="!!data?.length" class="smeta-totals">
        <div class="smeta-totals__main">
          <div class="tw-flex tw-flex-col tw-items-end">
            <span class="smeta-totals__label">с НДС</span>
            <span><Amount :value="amount" :round="2" /> тг.</span>
          </div>
          <div class="smeta-totals__divider" />
          <div class="tw-flex tw-flex-col tw-items-end">
            <span class="smeta-totals__label">без НДС</span>
            <span><Amount :value="amountWithoutNds" :round="2" /> тг.</span>
          </div>
        </div>
        <div class="smeta-totals__detail">
          <span>СМР без НДС: <Amount :value="totalWorkSumNoNds" :round="2" /> тг.</span>
          <span class="smeta-totals__dot" />
          <span>ТМЦ без НДС: <Amount :value="totalMaterialSumNoNds" :round="2" /> тг.</span>
        </div>
      </div>
    </div>

    <q-linear-progress v-if="isLoading" indeterminate />

    <div ref="tableWrapperRef" style="position: relative;">
    <CustomTable
      :title="tableTitle"
      :rowsData="visibleData"
      :customHeader="true"
      :custom-body="true"
      :small="true"
      :stickyHeader="true"
      :height="310"
    >
      <template #customHeader="innerProps">
        <q-tr>
          <q-th align="center" class="th-main">Подъезд<br/>Этаж</q-th>
          <q-th align="left" class="th-main">Работа</q-th>
          <q-th align="right" class="th-smr"><div class="th-group-label">СМР</div>Объем</q-th>
          <q-th align="right" class="th-smr split-header">
            <div class="split-header-top">Цена с НДС</div>
            <div class="split-header-bottom">Цена без НДС</div>
          </q-th>
          <q-th align="right" class="th-smr split-header">
            <div class="split-header-top">Сумма с НДС</div>
            <div class="split-header-bottom">Сумма без НДС</div>
          </q-th>
          <q-th align="left" class="th-mat"><div class="th-group-label">Материал</div>Наименование</q-th>
          <q-th align="right" class="th-mat">Кол-во</q-th>
          <!-- <q-th align="right" class="th-mat">Перерасход</q-th> -->
          <q-th align="right" class="th-mat" style="font-size:11px; color:#888;">Норма</q-th>
          <q-th align="right" class="th-mat split-header">
            <div class="split-header-top">Цена с НДС</div>
            <div class="split-header-bottom">Цена без НДС</div>
          </q-th>
          <q-th align="right" class="th-mat split-header">
            <div class="split-header-top">Сумма с НДС</div>
            <div class="split-header-bottom">Сумма без НДС</div>
          </q-th>
          <q-th align="right" class="th-main split-header">
            <div class="split-header-top">Итого с НДС</div>
            <div class="split-header-bottom">Итого без НДС</div>
          </q-th>
          <q-th align="center" class="th-main">Статус</q-th>
          <q-th align="center" class="th-main">Действия</q-th>
        </q-tr>
      </template>

      <template #customBody="props">
        <!-- Заголовочная строка группы работ + pending work additions -->
        <template v-if="props.row.isGroupHeader">
          <q-tr class="group-header-row" :class="{ 'group-header-row--new': props.row.isVirtualGroup }">
            <q-td colspan="13" class="group-header-cell">
              <div class="group-header-inner">
                <span>
                  {{ props.row.work_set_check_group_name || 'Без группы' }}
                  <span
                    v-if="props.row.isVirtualGroup && !getWorkAdditionsForGroup(props.row.work_set_check_group_id, props.row.floor_map_id).length"
                    class="group-floor-hint"
                  >
                    (П.{{ props.row.entrance ?? '—' }} Эт.{{ props.row.level_num ?? '—' }})
                  </span>
                </span>
                <div class="tw-flex tw-items-center tw-gap-2">
                  <q-badge v-if="props.row.isVirtualGroup" color="positive" label="новая группа" class="tw-text-xs" />
                  <span v-if="isGroupLockedByForeman(props.row)" class="group-locked-badge">
                    <q-icon name="lock" size="14px" />
                    Заблокирована: есть вызов ИТР
                  </span>
                  <q-btn
                    v-if="isEditable && props.row.isVirtualGroup"
                    flat dense no-caps
                    icon="construction"
                    label="Добавить СМР"
                    size="sm"
                    color="positive"
                    @click.stop="openAddWorkDialog(props.row)"
                  />
                  <q-btn
                    v-if="isEditable && props.row.isVirtualGroup"
                    flat dense round
                    icon="close"
                    size="sm"
                    color="negative"
                    @click.stop="removeNewGroup(props.row)"
                  >
                    <q-tooltip>Отменить добавление группы</q-tooltip>
                  </q-btn>
                  <q-btn
                    v-if="isEditable && !props.row.isVirtualGroup"
                    flat dense round
                    icon="playlist_add"
                    size="sm"
                    color="indigo-7"
                    @click.stop="openAddGroupDialog(props.row.floor_map_id)"
                  >
                    <q-tooltip>Добавить группу работ</q-tooltip>
                  </q-btn>
                </div>
              </div>
            </q-td>
          </q-tr>
          <!-- Pending work additions с rowspan (как существующие работы) -->
          <template
            v-for="wa in getWorkAdditionsForGroup(props.row.work_set_check_group_id, props.row.floor_map_id)"
            :key="'wa_' + wa._idx"
          >
            <q-tr
              v-for="(vrow, vIdx) in getPendingWorkRows(wa)"
              :key="'wa_' + wa._idx + '_' + vIdx"
              class="pending-addition-row"
            >
              <!-- Столбцы 1–5: Работа (только первая строка, с rowspan) -->
              <template v-if="vrow.isFirst">
                <!-- 1: Подъезд/Этаж -->
                <q-td :rowspan="vrow.totalRows" class="pending-addition-cell" />
                <!-- 2: Конструктив — название работы -->
                <q-td :rowspan="vrow.totalRows" class="pending-addition-cell">
                  <div class="tw-flex tw-items-center tw-gap-1">
                    <q-icon name="add_circle" color="positive" size="14px" />
                    <q-badge color="green" label="СМР" class="pending-addition-badge" />
                    <span class="pending-addition-name">{{ vrow.wa.work_set_name }}</span>
                    <span v-if="vrow.wa.unit_name" class="unit-inline">({{ vrow.wa.unit_name }})</span>
                  </div>
                  <span v-if="vrow.totalRows > 1" class="multi-material-badge">
                    {{ vrow.totalRows }} мат.
                  </span>
                </q-td>
                <!-- 3: Кол-во СМР -->
                <q-td :rowspan="vrow.totalRows" class="pending-addition-cell" style="text-align: right;">
                  <q-input
                    :model-value="vrow.wa.qty"
                    @update:model-value="updateWorkAdditionQty(vrow.wa._idx, $event)"
                    inputmode="decimal"
                    dense outlined
                    style="max-width: 100px;"
                    input-class="text-right"
                    hide-bottom-space
                  />
                </q-td>
                <!-- 4: Цена СМР -->
                <q-td :rowspan="vrow.totalRows" class="pending-addition-cell" style="text-align: right;">
                  <div class="split-data" v-if="vrow.wa.work_set_price != null">
                    <div class="split-data-top">{{ vrow.wa.work_set_price.toFixed(2) }}</div>
                    <div class="split-data-bottom">{{ vrow.wa.work_set_price_no_nds.toFixed(2) }}</div>
                  </div>
                </q-td>
                <!-- 5: Сумма СМР -->
                <q-td :rowspan="vrow.totalRows" class="pending-addition-cell" style="text-align: right;">
                  <div class="split-data" v-if="toNum(vrow.wa.qty) != null && vrow.wa.work_set_price != null">
                    <div class="split-data-top">{{ (toNum(vrow.wa.qty) * vrow.wa.work_set_price).toFixed(2) }}</div>
                    <div class="split-data-bottom">{{ (toNum(vrow.wa.qty) * vrow.wa.work_set_price_no_nds).toFixed(2) }}</div>
                  </div>
                </q-td>
              </template>

              <!-- Столбцы 6–10: Материал -->
              <template v-if="vrow.ma">
                <!-- 6: Наименование -->
                <q-td class="pending-addition-cell" style="position: relative;">
                  <div class="tw-flex tw-items-center tw-gap-1">
                    <q-icon name="add_circle" color="positive" size="14px" />
                    <q-badge color="teal" label="ТМЦ" class="pending-addition-badge" />
                    <span class="pending-addition-name" style="color: #00695c;">{{ vrow.ma.material_name }}</span>
                    <span v-if="vrow.ma.material_unit_name" class="unit-inline">({{ vrow.ma.material_unit_name }})</span>
                  </div>
                  <div class="purchase-type-row">
                    <span
                      class="purchase-type-tag"
                      :class="{ 'purchase-type-tag--active': vrow.ma.purchase_type_id === 1, 'purchase-type-tag--disabled': vrow.ma.isAvrLocked || !isEditable }"
                      @click.stop="isEditable && !vrow.ma.isAvrLocked && setPendingMaPurchaseType(vrow.ma._idx, 1)"
                    >Подряд</span>
                    <span
                      class="purchase-type-tag"
                      :class="{ 'purchase-type-tag--active': vrow.ma.purchase_type_id === 2, 'purchase-type-tag--disabled': !isEditable }"
                      @click.stop="isEditable && !vrow.ma.isAvrLocked && setPendingMaPurchaseType(vrow.ma._idx, 2)"
                    >Команда проекта</span>
                    <q-icon v-if="vrow.ma.isAvrLocked" name="lock" size="12px" color="orange" class="tw-ml-1">
                      <q-tooltip>Тип закупки заблокирован: есть АВР или КС2В</q-tooltip>
                    </q-icon>
                  </div>
                  <q-btn
                    flat dense round
                    icon="close"
                    size="xs"
                    color="negative"
                    class="pending-ma-remove-btn"
                    @click="removeMaterialAddition(vrow.ma._idx)"
                  >
                    <q-tooltip>Отменить добавление ТМЦ</q-tooltip>
                  </q-btn>
                  <q-btn
                    v-if="isEditable && !isCopyToFloorsHidden"
                    flat dense round
                    icon="content_copy"
                    size="xs"
                    color="indigo-5"
                    class="pending-ma-copy-btn"
                    @click="openCopyMaterialToFloorsDialog({ ...vrow.ma, __isPendingAddition: true })"
                  >
                    <q-tooltip>Скопировать в такие же работы на этажах</q-tooltip>
                  </q-btn>
                </q-td>
                <!-- 7: Кол-во -->
                <q-td class="pending-addition-cell" style="text-align: right;">
                  <q-input
                    :model-value="vrow.ma.qty"
                    @update:model-value="updateMaterialAdditionQty(vrow.ma._idx, $event)"
                    inputmode="decimal"
                    dense outlined
                    style="max-width: 100px;"
                    input-class="text-right"
                    hide-bottom-space
                  />
                </q-td>
                <!-- 8: Норма -->
                <q-td class="pending-addition-cell" style="text-align: right;">
                  <span
                    v-if="toNum(vrow.ma.qty) && vrow.ma.work_qty > 0"
                    class="norm-cell"
                    :title="`Норма: ${(toNum(vrow.ma.qty) / vrow.ma.work_qty).toFixed(6)} на ед. работы`"
                  >
                    {{ (toNum(vrow.ma.qty) / vrow.ma.work_qty).toFixed(6) }}
                  </span>
                </q-td>
                <!-- 9: Цена -->
                <q-td class="pending-addition-cell" style="text-align: right;">
                  <div class="split-data" v-if="vrow.ma.material_price != null">
                    <div class="split-data-top">{{ vrow.ma.material_price.toFixed(2) }}</div>
                    <div class="split-data-bottom">{{ vrow.ma.material_price_no_nds.toFixed(2) }}</div>
                  </div>
                </q-td>
                <!-- 10: Сумма -->
                <q-td class="pending-addition-cell" style="text-align: right;">
                  <div class="split-data" v-if="toNum(vrow.ma.qty) != null && vrow.ma.material_price != null">
                    <div class="split-data-top">{{ (toNum(vrow.ma.qty) * vrow.ma.material_price).toFixed(2) }}</div>
                    <div class="split-data-bottom">{{ (toNum(vrow.ma.qty) * vrow.ma.material_price_no_nds).toFixed(2) }}</div>
                  </div>
                </q-td>
              </template>
              <!-- Если материалов нет — пустые 5 ячеек -->
              <template v-else>
                <q-td class="pending-addition-cell" colspan="5" />
              </template>

              <!-- Столбцы 11–13: Итого/Статус/Действия (только первая строка, с rowspan) -->
              <template v-if="vrow.isFirst">
                <!-- 11: Итого -->
                <q-td :rowspan="vrow.totalRows" class="pending-addition-cell" style="text-align: right;">
                  <div class="split-data" v-if="hasPendingWorkAdditionTotalDisplay(vrow.wa)">
                    <div class="split-data-top">{{ getPendingWorkAdditionTotalWithNds(vrow.wa).toFixed(2) }}</div>
                    <div class="split-data-bottom">{{ getPendingWorkAdditionTotalNoNds(vrow.wa).toFixed(2) }}</div>
                  </div>
                </q-td>
                <!-- 12: Статус -->
                <q-td :rowspan="vrow.totalRows" class="pending-addition-cell" />
                <!-- 13: Действия -->
                <q-td :rowspan="vrow.totalRows" class="pending-addition-cell" style="text-align: center;">
                  <div class="tw-flex tw-items-center tw-justify-center tw-gap-1">
                    <q-btn
                      flat dense round
                      icon="add"
                      size="xs"
                      color="teal"
                      @click="openAddMaterialForPendingWork(vrow.wa)"
                    >
                      <q-tooltip>Добавить ТМЦ</q-tooltip>
                    </q-btn>
                    <q-btn
                      v-if="isEditable && !isCopyToFloorsHidden"
                      flat dense no-caps
                      label="Действие"
                      icon-right="arrow_drop_down"
                      size="xs"
                      color="indigo-5"
                      class="pending-action-menu-btn"
                    >
                      <q-menu>
                        <q-list dense style="min-width: 220px;">
                          <q-item clickable v-close-popup @click="openCopyWorkToFloorsDialog({ ...vrow.wa, __isPendingAddition: true })">
                            <q-item-section avatar><q-icon name="construction" size="18px" /></q-item-section>
                            <q-item-section>Скопировать СМР на этажи</q-item-section>
                          </q-item>
                          <q-item clickable v-close-popup @click="openCopyTmcToFloorsFromPendingWork(vrow.wa)">
                            <q-item-section avatar><q-icon name="category" size="18px" /></q-item-section>
                            <q-item-section>Скопировать ТМЦ на этажи</q-item-section>
                          </q-item>
                        </q-list>
                      </q-menu>
                    </q-btn>
                    <q-btn
                      flat dense round
                      icon="close"
                      size="xs"
                      color="negative"
                      @click="removeWorkAddition(vrow.wa._idx)"
                    >
                      <q-tooltip>Отменить добавление</q-tooltip>
                    </q-btn>
                  </div>
                </q-td>
              </template>
            </q-tr>
          </template>
        </template>

        <q-tr
          v-else
          :class="{
            'row-pending-delete': isWorkPendingDelete(props.row) || isMaterialPendingDelete(props.row),
          }"
        >
          <!-- Подъезд / Этаж (+ чек-бокс multi-update СМР слева) -->
          <custom-table-cell
            v-if="props.row.work_rowspan"
            :rowspan="getEffectiveRowspan(props.row)"
            align="center"
            vertical-align="center"
          >
            <div class="entrance-cell">
              <div v-if="smrSelectionMode" class="smr-select-cell">
                <q-checkbox
                  :model-value="smrSelectedKeys.has(smrSelectionKey(props.row))"
                  @update:model-value="(val) => onSmrCheckboxToggle(props.row, val)"
                  :disable="isSmrRowBlocked(props.row)"
                  dense
                />
                <q-icon
                  v-if="isSmrRowBlocked(props.row)"
                  name="lock"
                  size="14px"
                  color="grey-6"
                  class="smr-select-lock"
                >
                  <q-tooltip>{{ getSmrBlockReason(props.row) }}</q-tooltip>
                </q-icon>
              </div>
              <span class="entrance-label">П.{{ props.row.entrance }}</span>
              <span class="floor-label">Эт.{{ props.row.level_num }}</span>
            </div>
          </custom-table-cell>

          <!-- Конструктив + Ед. изм. -->
          <custom-table-cell
            v-if="props.row.work_rowspan"
            :rowspan="getEffectiveRowspan(props.row)"
            align="left"
            vertical-align="center"
            :class="{
              'cell-changed': isWorkChanged(props.row),
              'cell-pending-delete': isWorkPendingDelete(props.row),
            }"
          >
            <div class="work-cell-wrap">
              <span
                :class="canEditWork(props.row) ? 'clickable-cell' : ''"
                @click="openEditWorkDialog(props.row)"
                :title="hasAvr(props.row) ? 'Нельзя изменить: есть АВР или КС2В' : isLockedByForeman(props.row) ? getLockReason(props.row) : hasOperFactWork(props.row) ? 'Замена запрещена (есть опер-факт)' : hasProcess(props.row) ? 'Идёт процесс приёмки — изменения снимка приёмки пересчитаются' : 'Нажмите для смены вида работы'"
              >
                <span class="cell-text-block">
                  <span v-if="isWorkChanged(props.row)" class="old-value">{{ props.row.wname }}</span>
                  <span :class="{ 'new-value': isWorkChanged(props.row) }">
                    {{ getDisplayWorkName(props.row) }}
                  </span>
                  <span class="unit-inline">{{ getDisplayUnitName(props.row) }}</span>
                </span>
                <span v-if="getEffectiveRowspan(props.row) > 1" class="multi-material-badge">
                  {{ getEffectiveRowspan(props.row) }} мат.
                </span>
                <q-icon v-if="canEditWork(props.row)" name="edit" size="12px" class="edit-icon" />
                <q-icon v-if="isLockedByForeman(props.row)" name="lock" size="12px" class="edit-icon" color="red" />
                <q-icon v-if="hasAvr(props.row)" name="lock" size="12px" class="edit-icon" color="orange" style="opacity:1">
                  <q-tooltip>Есть АВР или КС2В — изменение заблокировано</q-tooltip>
                </q-icon>
                <q-icon v-else-if="hasOperFactWork(props.row)" name="lock" size="12px" class="edit-icon" color="grey-7" style="opacity:1">
                  <q-tooltip>Есть опер-факт по работе — изменение заблокировано</q-tooltip>
                </q-icon>
              </span>
              <q-btn
                v-if="hasWorkPending(props.row)"
                flat dense round
                icon="close"
                size="xs"
                class="revert-btn"
                color="orange"
                @click.stop="revertWorkChange(props.row)"
              >
                <q-tooltip>{{ isWorkChanged(props.row) ? 'Вернуть оригинальную работу' : 'Отменить обновление цены работы' }}</q-tooltip>
              </q-btn>
              <q-btn
                v-if="isWorkPendingDelete(props.row)"
                flat dense round
                icon="close"
                size="xs"
                class="revert-btn"
                color="orange"
                @click.stop="toggleDeleteWork(props.row)"
              >
                <q-tooltip>Отменить удаление работы</q-tooltip>
              </q-btn>
              <q-btn
                v-if="!isWorkPendingDelete(props.row) && canDeleteWork(props.row)"
                flat dense round
                icon="delete_outline"
                size="xs"
                class="delete-btn"
                color="grey-5"
                title="Пометить конструктив на удаление"
                @click.stop="toggleDeleteWork(props.row)"
              />
            </div>
          </custom-table-cell>

          <!-- СМР: Кол-во -->
          <custom-table-cell
            v-if="props.row.work_rowspan"
            :rowspan="getEffectiveRowspan(props.row)"
            align="right"
            vertical-align="center"
            :class="{ 'cell-changed': isWorkQtyChanged(props.row) }"
          >
            <span class="qty-cell">
              <span class="qty-values">
                <span v-if="isWorkQtyChanged(props.row)" class="old-value">{{ props.row.wqty }}</span>
                <span :class="{ 'new-value': isWorkQtyChanged(props.row) }">
                  {{ getDisplayWorkQty(props.row) }}
                </span>
              </span>
              <q-btn
                v-if="!isLockedByForeman(props.row)"
                flat dense round
                icon="edit"
                size="xs"
                class="qty-edit-btn"
                @click="openEditQtyDialog('work', props.row)"
              />
              <q-btn
                v-if="isWorkQtyChanged(props.row)"
                flat dense round
                icon="close"
                size="xs"
                class="revert-btn"
                color="orange"
                @click.stop="revertWorkQtyChange(props.row)"
              >
                <q-tooltip>Вернуть оригинальное кол-во</q-tooltip>
              </q-btn>
            </span>
          </custom-table-cell>

          <!-- СМР: Цена (с НДС / без НДС) -->
          <custom-table-cell
            v-if="props.row.work_rowspan"
            :rowspan="getEffectiveRowspan(props.row)"
            align="right"
            vertical-align="center"
            :class="{ 'cell-changed': isWorkPriceNdsDiffers(props.row) || isWorkPriceNoNdsDiffers(props.row) }"
          >
            <div class="split-data">
              <div class="split-data-top">
                <div v-if="isWorkPriceNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.wprc" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayWorkPrice(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="getDisplayWorkPrice(props.row)" :round="2" />
              </div>
              <div class="split-data-bottom">
                <div v-if="isWorkPriceNoNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.wprc_no_nds" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayWorkPriceWithoutNds(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="getDisplayWorkPriceWithoutNds(props.row)" :round="2" />
              </div>
            </div>
          </custom-table-cell>

          <!-- СМР: Сумма (с НДС / без НДС) -->
          <custom-table-cell
            v-if="props.row.work_rowspan"
            :rowspan="getEffectiveRowspan(props.row)"
            align="right"
            vertical-align="center"
            :class="{ 'cell-changed': isWorkSumNdsDiffers(props.row) || isWorkSumNoNdsDiffers(props.row) }"
          >
            <div class="split-data">
              <div class="split-data-top">
                <div v-if="isWorkSumNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.wsum" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayWorkSum(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="props.row.wsum" :round="2" />
              </div>
              <div class="split-data-bottom">
                <div v-if="isWorkSumNoNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.wsum_no_nds" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayWorkSumWithoutNds(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="props.row.wsum_no_nds" :round="2" />
              </div>
            </div>
          </custom-table-cell>

          <!-- Материал: Наименование + Ед. изм. -->
          <custom-table-cell
            align="left"
            vertical-align="center"
            class="material-name-cell"
            :class="{
              'cell-changed': props.row.material_id && isMaterialChanged(props.row),
              'cell-pending-delete': props.row.material_id && isMaterialPendingDelete(props.row),
            }"
          >
            <template v-if="props.row.material_id">
              <div v-if="tmcSelectionMode" class="tmc-select-cell">
                <q-checkbox
                  :model-value="tmcSelectedKeys.has(tmcSelectionKey(props.row))"
                  @update:model-value="(val) => onTmcCheckboxToggle(props.row, val)"
                  :disable="isTmcRowBlocked(props.row) || isTmcRowSame(props.row)"
                  dense
                />
                <q-icon
                  v-if="isTmcRowBlocked(props.row)"
                  name="lock"
                  size="14px"
                  color="grey-6"
                  class="tmc-select-lock"
                >
                  <q-tooltip>{{ getTmcBlockReason(props.row) }}</q-tooltip>
                </q-icon>
                <q-icon
                  v-else-if="isTmcRowSame(props.row)"
                  name="check_circle"
                  size="14px"
                  color="grey-5"
                  class="tmc-select-lock"
                >
                  <q-tooltip>Цена ТМЦ актуальна</q-tooltip>
                </q-icon>
              </div>
              <div class="work-cell-wrap">
                <span
                  :class="canEditMaterial(props.row) ? 'clickable-cell' : ''"
                  @click="openEditMaterialDialog(props.row)"
                  :title="hasAvr(props.row) ? 'Нельзя изменить: есть АВР или КС2В' : isLockedByForeman(props.row) ? getLockReason(props.row) : hasOperFactMaterial(props.row) ? 'Замена запрещена (есть опер-факт)' : hasProcess(props.row) ? 'Идёт процесс приёмки — изменения снимка приёмки пересчитаются' : 'Нажмите для смены материала'"
                >
                  <span class="cell-text-block">
                    <span v-if="isMaterialChanged(props.row)" class="old-value">{{ props.row.mname }}</span>
                    <span :class="{ 'new-value': isMaterialChanged(props.row) }">
                      {{ getDisplayMaterialName(props.row) }}
                    </span>
                    <span class="unit-inline">{{ props.row.munt }}</span>
                  </span>
                  <q-icon v-if="canEditMaterial(props.row)" name="edit" size="12px" class="edit-icon" />
                  <q-icon v-if="isLockedByForeman(props.row)" name="lock" size="12px" class="edit-icon" color="red" />
                  <q-icon v-if="hasAvr(props.row)" name="lock" size="12px" class="edit-icon" color="orange" style="opacity:1">
                    <q-tooltip>Есть АВР или КС2В — изменение заблокировано</q-tooltip>
                  </q-icon>
                  <q-icon v-else-if="hasOperFactMaterial(props.row)" name="lock" size="12px" class="edit-icon" color="grey-7" style="opacity:1">
                    <q-tooltip>Есть опер-факт по материалу — изменение заблокировано</q-tooltip>
                  </q-icon>
                </span>
                <q-btn
                  v-if="hasMaterialPending(props.row)"
                  flat dense round
                  icon="close"
                  size="xs"
                  class="revert-btn"
                  color="orange"
                  @click.stop="revertMaterialChange(props.row)"
                >
                  <q-tooltip>{{ isMaterialChanged(props.row) ? 'Вернуть оригинальный материал' : 'Отменить обновление цены материала' }}</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="isMaterialPendingDelete(props.row)"
                  flat dense round
                  icon="close"
                  size="xs"
                  class="revert-btn"
                  color="orange"
                  @click.stop="toggleDeleteMaterial(props.row)"
                >
                  <q-tooltip>Отменить удаление материала</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="!isMaterialPendingDelete(props.row) && canDeleteMaterial(props.row)"
                  flat dense round
                  icon="delete_outline"
                  size="xs"
                  class="delete-btn"
                  color="grey-5"
                  title="Пометить материал на удаление"
                  @click.stop="toggleDeleteMaterial(props.row)"
                />
              </div>
              <div v-if="getDisplayPurchaseTypeId(props.row)" class="purchase-type-row">
                <span
                  class="purchase-type-tag"
                  :class="{ 'purchase-type-tag--active': getDisplayPurchaseTypeId(props.row) === 1, 'purchase-type-tag--changed': isPurchaseTypeChanged(props.row), 'purchase-type-tag--disabled': isLockedAny(props.row) || hasOperFactMaterial(props.row) || !isEditable }"
                  :title="hasOperFactMaterial(props.row) ? 'Нельзя изменить тип закупки: по материалу есть опер-факт' : (isLockedAny(props.row) ? `Нельзя изменить тип закупки: на эту работу ${getLockedReason(props.row)}` : '')"
                  @click.stop="isEditable && !isLockedByForeman(props.row) && (hasOperFactMaterial(props.row) || !isLockedAny(props.row)) && setPurchaseType(props.row, 1)"
                >Подряд</span>
                <span
                  class="purchase-type-tag"
                  :class="{ 'purchase-type-tag--active': getDisplayPurchaseTypeId(props.row) === 2, 'purchase-type-tag--changed': isPurchaseTypeChanged(props.row), 'purchase-type-tag--disabled': isLockedAny(props.row) || hasOperFactMaterial(props.row) || !isEditable }"
                  :title="hasOperFactMaterial(props.row) ? 'Нельзя изменить тип закупки: по материалу есть опер-факт' : (isLockedAny(props.row) ? `Нельзя изменить тип закупки: на эту работу ${getLockedReason(props.row)}` : '')"
                  @click.stop="isEditable && !isLockedByForeman(props.row) && (hasOperFactMaterial(props.row) || !isLockedAny(props.row)) && setPurchaseType(props.row, 2)"
                >Команда проекта</span>
              </div>
            </template>
            <span v-else class="tw-text-gray-300 tw-text-xs">—</span>
          </custom-table-cell>

          <!-- Материал: Кол-во -->
          <custom-table-cell
            align="right"
            vertical-align="center"
            :class="{ 'cell-changed': props.row.material_id && isMaterialQtyChanged(props.row) }"
          >
            <span v-if="props.row.material_id" class="qty-cell">
              <span class="qty-values">
                <span v-if="isMaterialQtyChanged(props.row)" class="old-value">{{ props.row.mqty }}</span>
                <span :class="{ 'new-value': isMaterialQtyChanged(props.row) }">
                  {{ getDisplayMaterialQty(props.row) }}
                </span>
              </span>
              <q-btn
                v-if="!isLockedByForeman(props.row)"
                flat dense round
                icon="edit"
                size="xs"
                class="qty-edit-btn"
                @click="openEditQtyDialog('material', props.row)"
              />
              <q-btn
                v-if="isMaterialQtyChanged(props.row)"
                flat dense round
                icon="close"
                size="xs"
                class="revert-btn"
                color="orange"
                @click.stop="revertMaterialQtyChange(props.row)"
              >
                <q-tooltip>Вернуть оригинальное кол-во</q-tooltip>
              </q-btn>
            </span>
          </custom-table-cell>

          <!-- Материал: Норма расхода -->
          <custom-table-cell
            align="right"
            vertical-align="center"
            :class="{ 'cell-changed': props.row.material_id && isNormRatioChanged(props.row) }"
          >
            <span
              v-if="props.row.material_id && getDisplayNormRatio(props.row) != null"
              class="norm-cell"
              :title="isNormRatioChanged(props.row)
                ? `Норма: ${formatNormRatio(getOriginalNormRatio(props.row))} → ${formatNormRatio(getDisplayNormRatio(props.row))} на ед. работы`
                : `Норма: ${formatNormRatio(getDisplayNormRatio(props.row))} на ед. работы`"
            >
              <span v-if="isNormRatioChanged(props.row)" class="cell-text-block">
                <span class="old-value">{{ formatNormRatio(getOriginalNormRatio(props.row)) }}</span>
                <span class="new-value">{{ formatNormRatio(getDisplayNormRatio(props.row)) }}</span>
              </span>
              <template v-else>
                {{ formatNormRatio(getDisplayNormRatio(props.row)) }}
              </template>
            </span>
          </custom-table-cell>

          <!-- Материал: Цена (с НДС / без НДС) -->
          <custom-table-cell
            align="right"
            vertical-align="center"
            :class="{ 'cell-changed': isMaterialPriceNdsDiffers(props.row) || isMaterialPriceNoNdsDiffers(props.row) }"
          >
            <div class="split-data">
              <div class="split-data-top">
                <div v-if="isMaterialPriceNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.mprc" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayMaterialPrice(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="getDisplayMaterialPrice(props.row)" :round="2" />
              </div>
              <div class="split-data-bottom">
                <div v-if="isMaterialPriceNoNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.mprc_no_nds" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayMaterialPriceWithoutNds(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="getDisplayMaterialPriceWithoutNds(props.row)" :round="2" />
              </div>
            </div>
          </custom-table-cell>

          <!-- Материал: Сумма (с НДС / без НДС) -->
          <custom-table-cell
            align="right"
            vertical-align="center"
            :class="{ 'cell-changed': isMaterialSumNdsDiffers(props.row) || isMaterialSumNoNdsDiffers(props.row) }"
          >
            <div class="split-data" v-if="props.row.material_id">
              <div class="split-data-top">
                <div v-if="isMaterialSumNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.msum" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayMaterialSum(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="getDisplayMaterialSum(props.row)" :round="2" />
              </div>
              <div class="split-data-bottom">
                <div v-if="isMaterialSumNoNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.msum_no_nds" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayMaterialSumWithoutNds(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="getDisplayMaterialSumWithoutNds(props.row)" :round="2" />
              </div>
            </div>
          </custom-table-cell>

          <!-- Итого (с НДС / без НДС) -->
          <custom-table-cell
            v-if="props.row.work_rowspan"
            :rowspan="getEffectiveRowspan(props.row)"
            align="right"
            vertical-align="center"
            :class="{ 'cell-changed': isTotalNdsDiffers(props.row) || isTotalNoNdsDiffers(props.row) }"
          >
            <div class="split-data">
              <div class="split-data-top">
                <div v-if="isTotalNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.ttl" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayTotal(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="props.row.ttl" :round="2" />
              </div>
              <div class="split-data-bottom">
                <div v-if="isTotalNoNdsDiffers(props.row)" class="changed-val">
                  <span class="changed-val__old"><Amount :value="props.row.ttl_no_nds" :round="2" /></span>
                  <span class="changed-val__new"><Amount :value="getDisplayTotalWithoutNds(props.row)" :round="2" /></span>
                </div>
                <Amount v-else :value="props.row.ttl_no_nds" :round="2" />
              </div>
            </div>
          </custom-table-cell>

          <!-- Статус: АВР + НУ + ОКК + Оплачено -->
          <custom-table-cell
            v-if="props.row.work_rowspan"
            :rowspan="getEffectiveRowspan(props.row)"
            align="left"
            vertical-align="center"
          >
            <div class="status-cell">
              <!-- Опер-факт -->
              <div v-if="props.row.oper_fact_sum > 0" class="status-row">
                <span class="status-label">ОФ</span>
                <Amount :value="props.row.oper_fact_sum" :round="2" class="status-amount" />
              </div>
              <!-- НУ -->
              <div v-if="props.row.has_foreman_call" class="status-row">
                <span class="status-label">НУ</span>
                <q-icon
                  :name="props.row.is_foreman_checked ? 'task_alt' : 'pending'"
                  :color="props.row.is_foreman_checked ? 'positive' : 'warning'"
                  size="16px"
                >
                  <q-tooltip>{{ props.row.is_foreman_checked ? 'Принято' : 'Ожидает' }} · {{ formatDate(props.row.foreman_call_date) }}</q-tooltip>
                </q-icon>
              </div>
              <!-- ОКК -->
              <div v-if="props.row.has_okk_call" class="status-row">
                <span class="status-label">ОКК</span>
                <q-icon
                  :name="props.row.is_okk_checked ? 'task_alt' : 'pending'"
                  :color="props.row.is_okk_checked ? 'positive' : 'warning'"
                  size="16px"
                >
                  <q-tooltip>{{ props.row.is_okk_checked ? 'Принято' : 'Ожидает' }} · {{ formatDate(props.row.okk_call_date) }}</q-tooltip>
                </q-icon>
              </div>
              <!-- АВР / КС2В -->
              <div v-if="props.row.avr_count > 0" class="status-row">
                <span class="status-label">АВР/КС2В</span>
                <q-badge
                  color="teal"
                  :label="props.row.avr_count"
                  class="tw-cursor-pointer"
                  @click="openAvrListDialog(props.row)"
                >
                  <q-tooltip>Показать список АВР / КС2В</q-tooltip>
                </q-badge>
                <Amount :value="props.row.avr_call_sum" :round="2" class="status-amount" />
              </div>
              <!-- Оплачено -->
              <div v-if="props.row.paid_total > 0" class="status-row">
                <span class="status-label">Опл.</span>
                <Amount :value="props.row.paid_total" :round="2" class="status-amount" />
              </div>
            </div>
          </custom-table-cell>

          <!-- Действия -->
          <custom-table-cell
            v-if="props.row.work_rowspan"
            :rowspan="getEffectiveRowspan(props.row)"
            align="center"
            vertical-align="center"
          >
            <div class="actions-cell">
              <q-btn
                v-if="isEditable && !isWorkPendingDelete(props.row) && !isLockedByForeman(props.row)"
                flat dense no-caps
                icon-right="arrow_drop_down"
                label="Действие"
                size="xs"
                color="primary"
                class="action-add-btn"
              >
                <q-menu>
                  <q-list dense style="min-width: 220px;">
                    <q-item clickable v-close-popup @click="openAddWorkDialog(props.row)">
                      <q-item-section avatar><q-icon name="construction" size="18px" /></q-item-section>
                      <q-item-section>Добавить СМР</q-item-section>
                    </q-item>
                    <q-item clickable v-close-popup @click="openAddMaterialDialog(props.row)">
                      <q-item-section avatar><q-icon name="category" size="18px" /></q-item-section>
                      <q-item-section>Добавить ТМЦ</q-item-section>
                    </q-item>
                    <q-separator v-if="!isCopyToFloorsHidden" />
                    <q-item
                      v-if="isEditable && !isCopyToFloorsHidden"
                      clickable
                      v-close-popup
                      @click="openCopyWorkToFloorsDialog(props.row)"
                    >
                      <q-item-section avatar><q-icon name="construction" size="18px" /></q-item-section>
                      <q-item-section>Скопировать СМР на этажи</q-item-section>
                    </q-item>
                    <q-item
                      v-if="isEditable && !isCopyToFloorsHidden"
                      clickable
                      v-close-popup
                      @click="openCopyTmcToFloorsFromSavedWork(props.row)"
                    >
                      <q-item-section avatar><q-icon name="category" size="18px" /></q-item-section>
                      <q-item-section>Скопировать ТМЦ на этажи</q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
          </custom-table-cell>
        </q-tr>
        <!-- Pending material additions — только после последней строки работы -->
        <!-- Столбцы 1–5 (работа) и 11–13 (итого/статус/действия) уже заняты rowspan -->
        <q-tr
          v-for="ma in (props.row.isLastInWork ? getMaterialAdditionsForWork(props.row.work_set_id, props.row.work_set_check_group_id, props.row.floor_map_id) : [])"
          :key="'ma_' + ma._idx"
          class="pending-addition-row"
        >
          <!-- 6: Наименование материала -->
          <q-td class="pending-addition-cell" style="position: relative;">
            <div class="tw-flex tw-items-center tw-gap-1">
              <q-icon name="add_circle" color="positive" size="14px" />
              <q-badge color="teal" label="ТМЦ" class="pending-addition-badge" />
              <q-badge v-if="ma.willCreateAddwork" color="orange" label="Доп работа" class="pending-addition-badge">
                <q-tooltip>Будет создана новая группа «Доп работы» (есть АВР или КС2В)</q-tooltip>
              </q-badge>
              <span class="pending-addition-name" style="color: #00695c;">{{ ma.material_name }}</span>
              <span v-if="ma.material_unit_name" class="unit-inline">({{ ma.material_unit_name }})</span>
            </div>
            <div class="purchase-type-row">
              <span
                class="purchase-type-tag"
                :class="{ 'purchase-type-tag--active': ma.purchase_type_id === 1, 'purchase-type-tag--disabled': ma.isAvrLocked || !isEditable }"
                @click.stop="isEditable && !ma.isAvrLocked && setPendingMaPurchaseType(ma._idx, 1)"
              >Подряд</span>
              <span
                class="purchase-type-tag"
                :class="{ 'purchase-type-tag--active': ma.purchase_type_id === 2, 'purchase-type-tag--disabled': !isEditable }"
                @click.stop="isEditable && !ma.isAvrLocked && setPendingMaPurchaseType(ma._idx, 2)"
              >Команда проекта</span>
              <q-icon v-if="ma.isAvrLocked" name="lock" size="12px" color="orange" class="tw-ml-1">
                <q-tooltip>Тип закупки заблокирован: есть АВР или КС2В</q-tooltip>
              </q-icon>
            </div>
            <q-btn
              flat dense round
              icon="close"
              size="xs"
              color="negative"
              class="pending-ma-remove-btn"
              @click="removeMaterialAddition(ma._idx)"
            >
              <q-tooltip>Отменить добавление</q-tooltip>
            </q-btn>
            <q-btn
              v-if="isEditable && !isCopyToFloorsHidden"
              flat dense round
              icon="content_copy"
              size="xs"
              color="indigo-5"
              class="pending-ma-copy-btn"
              @click="openCopyMaterialToFloorsDialog({ ...ma, __isPendingAddition: true })"
            >
              <q-tooltip>Скопировать в такие же работы на этажах</q-tooltip>
            </q-btn>
          </q-td>
          <!-- 7: Кол-во материала -->
          <q-td class="pending-addition-cell" style="text-align: right;">
            <q-input
              :model-value="ma.qty"
              @update:model-value="updateMaterialAdditionQty(ma._idx, $event)"
              inputmode="decimal"
              dense outlined
              style="max-width: 100px;"
              input-class="text-right"
              hide-bottom-space
            />
          </q-td>
          <!-- 8: Норма расхода = кол-во ТМЦ / кол-во СМР -->
          <q-td class="pending-addition-cell" style="text-align: right;">
            <span
              v-if="toNum(ma.qty) && ma.work_qty > 0"
              class="norm-cell"
              :title="`Норма: ${(toNum(ma.qty) / ma.work_qty).toFixed(6)} на ед. работы`"
            >
              {{ (toNum(ma.qty) / ma.work_qty).toFixed(6) }}
            </span>
          </q-td>
          <!-- 9: Цена материала -->
          <q-td class="pending-addition-cell" style="text-align: right;">
            <div class="split-data" v-if="ma.material_price != null">
              <div class="split-data-top">{{ ma.material_price.toFixed(2) }}</div>
              <div class="split-data-bottom">{{ ma.material_price_no_nds.toFixed(2) }}</div>
            </div>
          </q-td>
          <!-- 10: Сумма материала -->
          <q-td class="pending-addition-cell" style="text-align: right;">
            <div class="split-data" v-if="toNum(ma.qty) != null && ma.material_price != null">
              <div class="split-data-top">{{ (toNum(ma.qty) * ma.material_price).toFixed(2) }}</div>
              <div class="split-data-bottom">{{ (toNum(ma.qty) * ma.material_price_no_nds).toFixed(2) }}</div>
            </div>
          </q-td>
        </q-tr>
        <!-- /v-else обычная строка -->
      </template>
    </CustomTable>
    <div v-if="renderedCount < dataWithPendingGroups.length" class="tw-text-center tw-py-3 tw-text-gray-400 tw-text-sm">
      Загрузка строк… ({{ renderedCount }} из {{ dataWithPendingGroups.length }})
    </div>
    <q-inner-loading :showing="isLoading" label="Загрузка..." />
    </div>

    <!-- Модалка: редактирование Кол-во -->
    <q-dialog v-model="editQtyDialog.open" persistent>
      <q-card style="min-width: 340px;">
        <q-card-section>
          <div class="text-h6">
            {{ editQtyDialog.type === 'work' ? 'Изменить Кол-во СМР' : 'Изменить Кол-во материала' }}
          </div>
          <div class="tw-text-sm tw-text-gray-500 tw-mt-1" v-if="editQtyDialog.label">
            {{ editQtyDialog.label }}
          </div>
        </q-card-section>
        <q-card-section v-if="editQtyDialog.hasAvr" class="tw-pt-0">
          <q-banner dense rounded class="bg-orange-1 text-orange-9 tw-text-xs">
            <template v-slot:avatar>
              <q-icon name="info" color="orange" />
            </template>
            На эту группу работ есть АВР или КС2В. Дельта свыше текущего объёма
            будет вынесена в новую группу <b>«Доп работы»</b>. Уменьшение запрещено.
          </q-banner>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model.number="editQtyDialog.value"
            type="number"
            :step="editQtyDialog.type === 'material' && isPieceMaterial(editQtyDialog.row) ? '1' : 'any'"
            :hint="editQtyDialog.type === 'material' && isPieceMaterial(editQtyDialog.row) ? 'Штучный материал — только целые значения' : ''"
            label="Новое кол-во"
            outlined
            autofocus
            @keyup.enter="confirmEditQty"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" @click="editQtyDialog.open = false" />
          <q-btn color="primary" label="Подтвердить" @click="confirmEditQty" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: смена работы -->
    <q-dialog v-model="editWorkDialog.open" persistent>
      <q-card style="min-width: 500px; max-width: 700px;">
        <q-card-section>
          <div class="text-h6">Выбрать вид работы</div>
          <div class="tw-text-sm tw-text-gray-500 tw-mt-1">
            Текущий: {{ editWorkDialog.currentName }}
          </div>
        </q-card-section>
        <q-card-section>
          <q-linear-progress v-if="editWorkDialog.loading" indeterminate />
          <div v-else-if="!editWorkDialog.list.length" class="tw-text-gray-400 tw-text-sm">
            Нет доступных вариантов
          </div>
          <q-list v-else bordered separator>
            <q-item
              v-for="ws in editWorkDialog.list"
              :key="ws.work_set_id"
              clickable
              :active="ws.work_set_id === editWorkDialog.row?.work_set_id"
              @click="confirmEditWork(ws)"
            >
              <q-item-section>
                <q-item-label>{{ ws.work_set_name }}</q-item-label>
                <q-item-label caption>{{ ws.unit_name }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-item-label class="text-weight-medium">
                  {{ ws.work_set_price != null ? Number(ws.work_set_price).toFixed(2) + ' тг' : '—' }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Закрыть" @click="editWorkDialog.open = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: смена материала -->
    <q-dialog v-model="editMaterialDialog.open" persistent>
      <q-card style="min-width: 500px; max-width: 700px;">
        <q-card-section>
          <div class="text-h6">Выбрать материал</div>
          <div class="tw-text-sm tw-text-gray-500 tw-mt-1">
            Текущий: {{ editMaterialDialog.currentName }}
          </div>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="editMaterialDialog.search"
            label="Поиск материала"
            outlined
            clearable
            debounce="400"
            @update:model-value="searchMaterials"
          >
            <template #append>
              <q-icon name="search" />
            </template>
          </q-input>
          <q-linear-progress v-if="editMaterialDialog.loading" indeterminate class="tw-mt-2" />
          <q-list v-if="editMaterialDialog.results.length" bordered separator class="tw-mt-2">
            <q-item
              v-for="mat in editMaterialDialog.results"
              :key="mat.material_id"
              clickable
              @click="confirmEditMaterial(mat)"
            >
              <q-item-section>
                <q-item-label>{{ mat.material_name }}</q-item-label>
                <q-item-label caption v-if="mat.price > 0" class="tw-text-blue-600">
                  {{ mat.price?.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} тг
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
          <div
            v-else-if="!editMaterialDialog.loading && editMaterialDialog.search"
            class="tw-text-gray-400 tw-text-sm tw-mt-2"
          >
            Ничего не найдено
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Закрыть" @click="editMaterialDialog.open = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: список АВР по строке сметы -->
    <q-dialog v-model="avrListDialog.open">
      <q-card style="min-width: 580px; max-width: 800px;">
        <q-card-section class="tw-flex tw-items-center tw-justify-between">
          <div class="text-h6">АВР / КС2В по строке: {{ avrListDialog.workSetName }}</div>
          <q-btn flat round icon="close" @click="avrListDialog.open = false" />
        </q-card-section>
        <q-card-section>
          <q-linear-progress v-if="avrListDialog.loading" indeterminate />
          <div v-else-if="!avrListDialog.list.length" class="tw-text-gray-400 tw-text-sm">
            Документы не найдены
          </div>
          <q-markup-table v-else flat bordered dense>
            <thead>
              <tr>
                <th class="text-left">Тип</th>
                <th class="text-left">Номер</th>
                <th class="text-center">Дата</th>
                <th class="text-right">Сумма, тг</th>
                <th class="text-center">Подписан</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="avr in avrListDialog.list" :key="avr.floor_map_document_id">
                <td>{{ avr.document_type_name || (avr.document_type_code === 'K2SV' ? 'КС-2В' : 'АВР') }}</td>
                <td>{{ avr.avr_code || '—' }}</td>
                <td class="text-center">{{ formatDate(avr.avr_date) || '—' }}</td>
                <td class="text-right">
                  <Amount :value="avr.avr_sum" :round="2" /> тг
                </td>
                <td class="text-center">
                  <q-icon
                    :name="avr.is_signed ? 'task_alt' : 'radio_button_unchecked'"
                    :color="avr.is_signed ? 'positive' : 'grey'"
                    size="18px"
                  >
                    <q-tooltip>{{ avr.is_signed ? `Подписан ${formatDate(avr.sign_date)}` : 'Не подписан' }}</q-tooltip>
                  </q-icon>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Модалка: сохранение с выбором причины -->
    <q-dialog v-model="saveDialog.open" persistent>
      <q-card style="min-width: 420px;">
        <q-card-section>
          <div class="text-h6">Сохранить изменения сметы</div>
          <div class="tw-text-sm tw-text-gray-500 tw-mt-1">
            Будет создана новая версия сметы
          </div>
        </q-card-section>
        <q-card-section class="tw-flex tw-flex-col tw-gap-4">
          <q-select
            v-model="saveDialog.reason"
            :options="saveDialog.reasonOptions"
            option-label="reason_name"
            option-value="reason_code"
            label="Причина пересчёта *"
            outlined
            emit-value
            map-options
            :loading="saveDialog.reasonsLoading"
          />
          <q-input
            v-model="saveDialog.comments"
            label="Комментарий *"
            outlined
            type="textarea"
            rows="3"
            :rules="[val => !!val && val.trim().length > 0 || 'Обязательное поле']"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" @click="saveDialog.open = false" />
          <q-btn
            color="primary"
            label="Сохранить"
            :disable="!saveDialog.reason || !saveDialog.comments || !saveDialog.comments.trim()"
            :loading="isSaving"
            @click="confirmSave"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: добавление СМР -->
    <q-dialog v-model="addWorkDialog.open" persistent>
      <q-card style="min-width: 500px; max-width: 700px;">
        <q-card-section>
          <div class="text-h6">Добавить СМР</div>
          <div class="tw-text-sm tw-text-gray-500 tw-mt-1">
            Группа: {{ addWorkDialog.groupName }}
          </div>
        </q-card-section>
        <q-card-section v-if="addWorkDialog.willCreateAddwork" class="tw-pt-0">
          <q-banner rounded class="bg-orange-1 text-orange-9">
            <template #avatar>
              <q-icon name="warning" color="orange" />
            </template>
            На эту группу работ есть АВР/КС2В или начат процесс приёмки. Добавленная работа будет добавлена в группу <b>«Доп работа»</b> по этой группе работ.
          </q-banner>
        </q-card-section>
        <q-card-section>
          <q-linear-progress v-if="addWorkDialog.loading" indeterminate />
          <div v-else-if="!addWorkDialog.list.length" class="tw-text-gray-400 tw-text-sm">
            Нет доступных работ в этой группе
          </div>
          <q-list v-else bordered separator style="max-height: 400px; overflow-y: auto;">
            <q-item
              v-for="ws in addWorkDialog.list"
              :key="ws.work_set_id"
              clickable
              @click="confirmAddWork(ws)"
            >
              <q-item-section>
                <q-item-label>{{ ws.work_set_name }}</q-item-label>
                <q-item-label caption>{{ ws.unit_name }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-item-label class="text-weight-medium">
                  {{ ws.work_set_price != null ? Number(ws.work_set_price).toFixed(2) + ' тг' : '—' }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Закрыть" @click="addWorkDialog.open = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: добавление ТМЦ -->
    <q-dialog v-model="addMaterialDialog.open" persistent>
      <q-card style="min-width: 500px; max-width: 700px;">
        <q-card-section>
          <div class="text-h6">Добавить ТМЦ</div>
          <div class="tw-text-sm tw-text-gray-500 tw-mt-1">
            Работа: {{ addMaterialDialog.workName }}
          </div>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="addMaterialDialog.search"
            label="Поиск материала"
            outlined
            clearable
            debounce="400"
            @update:model-value="searchAddMaterials"
          >
            <template #append>
              <q-icon name="search" />
            </template>
          </q-input>
          <q-linear-progress v-if="addMaterialDialog.loading" indeterminate class="tw-mt-2" />
          <q-list v-if="addMaterialDialog.results.length" bordered separator class="tw-mt-2" style="max-height: 400px; overflow-y: auto;">
            <q-item
              v-for="mat in addMaterialDialog.results"
              :key="mat.material_id"
              clickable
              @click="confirmAddMaterial(mat)"
            >
              <q-item-section>
                <q-item-label>{{ mat.material_name }}</q-item-label>
                <q-item-label caption>{{ mat.material_unit_name }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-item-label class="text-weight-medium">
                  {{ mat.price != null ? Number(mat.price).toFixed(2) + ' тг' : '—' }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
          <div
            v-else-if="!addMaterialDialog.loading && addMaterialDialog.search"
            class="tw-text-gray-400 tw-text-sm tw-mt-2"
          >
            Ничего не найдено
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Закрыть" @click="addMaterialDialog.open = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <!-- Модалка: выбор группы работ для добавления -->
    <q-dialog v-model="addGroupDialog.open" style="max-width: 560px;">
      <q-card style="min-width: 480px; max-width: 560px; width: 100%;">
        <q-card-section class="tw-flex tw-items-center tw-justify-between tw-pb-2">
          <div class="text-h6">Добавить группу работ</div>
          <q-btn flat round dense icon="close" @click="closeAddGroupDialog" />
        </q-card-section>

        <q-card-section style="padding-top: 0;">
          <q-input
            v-model="addGroupDialog.search"
            dense outlined clearable
            placeholder="Поиск по наименованию..."
            class="tw-mb-2"
            debounce="200"
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>
          <q-linear-progress v-if="addGroupDialog.groupsLoading" indeterminate class="tw-mb-2" />
          <div v-else-if="!filteredAvailableGroups.length" class="tw-text-gray-400 tw-text-sm tw-py-4 tw-text-center">
            {{ addGroupDialog.search ? 'Ничего не найдено' : 'Все группы уже добавлены на этом этаже' }}
          </div>
          <q-list v-else bordered separator style="max-height: 420px; overflow-y: auto;">
            <q-item
              v-for="g in filteredAvailableGroups"
              :key="g.work_set_check_group_id"
              clickable
              @click="selectGroupForAddGroup(g)"
            >
              <q-item-section>
                <q-item-label>{{ g.work_set_check_group_name }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Закрыть" no-caps @click="closeAddGroupDialog" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: групповое редактирование — изменение типа закупа материала -->
    <q-dialog v-model="bulkPtDialog.open" persistent>
      <q-card class="bulk-pt-card">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Групповое редактирование: тип закупа материала</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section v-if="bulkPtDialog.drafts.length" class="bulk-pt-drafts-section">
          <div class="bulk-pt-drafts-title">
            В очереди: {{ bulkPtDialog.drafts.length }}
          </div>
          <div class="bulk-pt-drafts-list">
            <div
              v-for="d in bulkPtDialog.drafts"
              :key="d.id"
              class="bulk-pt-draft-item"
            >
              <span class="bulk-pt-draft-name" :title="d.material_name">
                {{ d.material_name || '—' }}
              </span>
              <q-icon name="arrow_forward" size="14px" class="tw-mx-1" color="grey-6" />
              <span
                class="bulk-pt-pt-badge"
                :class="d.purchase_type_id === 1 ? 'bulk-pt-pt-badge--contractor' : 'bulk-pt-pt-badge--team'"
              >
                {{ d.purchase_type_id === 1 ? 'Подряд' : 'Команда проекта' }}
              </span>
              <span class="bulk-pt-draft-count">этажей: {{ d.floor_map_ids.length }}</span>
              <q-btn
                flat dense round
                icon="close"
                size="xs"
                color="negative"
                @click="removeBulkPtDraft(d.id)"
              >
                <q-tooltip>Убрать из очереди</q-tooltip>
              </q-btn>
            </div>
          </div>
        </q-card-section>

        <q-stepper
          v-model="bulkPtDialog.step"
          animated
          flat
          alternative-labels
          color="primary"
          header-nav
          class="bulk-pt-stepper"
        >
          <!-- Шаг 1: материал -->
          <q-step :name="1" title="Материал" icon="inventory_2" :done="bulkPtDialog.step > 1" :header-nav="true">
            <q-input
              v-model="bulkPtDialog.searchTerm"
              dense outlined clearable
              placeholder="Поиск по наименованию материала..."
              class="tw-mb-3"
              debounce="200"
            >
              <template #prepend>
                <q-icon name="search" />
              </template>
            </q-input>
            <div
              v-if="!bulkPtFilteredMaterialOptions.length"
              class="tw-text-gray-400 tw-text-sm tw-py-6 tw-text-center"
            >
              {{ bulkPtDialog.searchTerm ? 'Ничего не найдено' : 'Нет материалов, доступных для группового редактирования' }}
            </div>
            <q-list v-else bordered separator class="bulk-pt-list">
              <q-item
                v-for="opt in bulkPtFilteredMaterialOptions"
                :key="opt.material_id"
                clickable
                :active="bulkPtDialog.selectedMaterialId === opt.material_id"
                active-class="bulk-pt-item--active"
                @click="onSelectBulkPtMaterial(opt.material_id)"
              >
                <q-item-section>
                  <q-item-label>{{ opt.material_name || '—' }}</q-item-label>
                  <q-item-label caption v-if="opt.unit_name">{{ opt.unit_name }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge color="blue-grey-4" text-color="white" :label="`${opt.rowsAvailable} из ${opt.rows.length}`">
                    <q-tooltip>Доступно строк / всего строк по этому материалу</q-tooltip>
                  </q-badge>
                </q-item-section>
              </q-item>
            </q-list>
          </q-step>

          <!-- Шаг 2: подъезды и этажи -->
          <q-step
            :name="2"
            title="Где применить"
            icon="location_on"
            :done="bulkPtDialog.step > 2"
            :header-nav="!!bulkPtDialog.selectedMaterialId"
          >
            <div
              v-if="!bulkPtLocations.length"
              class="tw-text-gray-400 tw-text-sm tw-py-6 tw-text-center"
            >
              Нет доступных этажей для выбранного материала
            </div>
            <div v-else class="bulk-pt-locations">
              <div
                v-for="entrItem in bulkPtLocations"
                :key="entrItem.entrance ?? 'none'"
                class="bulk-pt-entrance"
              >
                <div class="bulk-pt-entrance-head">
                  <q-checkbox
                    :model-value="bulkPtEntranceState(entrItem) === 'partial' ? null : (bulkPtEntranceState(entrItem) === 'all')"
                    :indeterminate-value="null"
                    :toggle-indeterminate="false"
                    dense
                    @update:model-value="toggleBulkPtEntrance(entrItem)"
                  />
                  <span class="bulk-pt-entrance-title">Подъезд {{ entrItem.entrance ?? '—' }}</span>
                  <span class="bulk-pt-entrance-meta">
                    {{ entrItem.floors.filter(f => !f.allLocked).length }} доступно
                    <span v-if="entrItem.floors.some(f => f.allLocked)">
                      / {{ entrItem.floors.filter(f => f.allLocked).length }} заблокировано
                    </span>
                  </span>
                </div>
                <div class="bulk-pt-floors">
                  <div
                    v-for="floor in entrItem.floors"
                    :key="floor.floor_map_id"
                    class="bulk-pt-floor"
                    :class="{ 'bulk-pt-floor--locked': floor.allLocked }"
                  >
                    <q-checkbox
                      :model-value="bulkPtDialog.selectedFloorMapIds.has(floor.floor_map_id)"
                      :disable="floor.allLocked"
                      dense
                      @update:model-value="(val) => toggleBulkPtFloor(floor, val)"
                    />
                    <span class="bulk-pt-floor-label">Этаж {{ floor.level_num ?? '—' }}</span>
                    <span
                      v-if="floor.currentPt === 1"
                      class="bulk-pt-pt-badge bulk-pt-pt-badge--contractor"
                    >Подряд</span>
                    <span
                      v-else-if="floor.currentPt === 2"
                      class="bulk-pt-pt-badge bulk-pt-pt-badge--team"
                    >Команда проекта</span>
                    <span
                      v-else-if="floor.currentPt === 'mixed'"
                      class="bulk-pt-pt-badge bulk-pt-pt-badge--mixed"
                    >Смешано</span>
                    <q-icon v-if="floor.allLocked" name="lock" size="14px" color="grey-6" class="tw-ml-auto">
                      <q-tooltip>{{ floor.lockReason || 'Заблокировано' }}</q-tooltip>
                    </q-icon>
                  </div>
                </div>
              </div>
            </div>
          </q-step>

          <!-- Шаг 3: тип закупа -->
          <q-step
            :name="3"
            title="Тип закупа"
            icon="swap_horiz"
            :header-nav="!!bulkPtDialog.selectedMaterialId && bulkPtDialog.selectedFloorMapIds.size > 0"
          >
            <div class="tw-text-sm tw-text-gray-600 tw-mb-4">
              Выберите новый тип закупа для отмеченных строк (выбрано этажей: {{ bulkPtDialog.selectedFloorMapIds.size }}):
            </div>
            <div class="bulk-pt-pt-choices">
              <button
                type="button"
                class="bulk-pt-pt-choice"
                :class="{ 'bulk-pt-pt-choice--active': bulkPtDialog.newPurchaseTypeId === 1 }"
                @click="bulkPtDialog.newPurchaseTypeId = 1"
              >
                <q-icon name="engineering" size="22px" />
                <span class="bulk-pt-pt-choice-title">Подряд</span>
              </button>
              <button
                type="button"
                class="bulk-pt-pt-choice"
                :class="{ 'bulk-pt-pt-choice--active': bulkPtDialog.newPurchaseTypeId === 2 }"
                @click="bulkPtDialog.newPurchaseTypeId = 2"
              >
                <q-icon name="group" size="22px" />
                <span class="bulk-pt-pt-choice-title">Команда проекта</span>
              </button>
            </div>
          </q-step>
        </q-stepper>

        <q-card-actions align="right" class="bulk-pt-actions">
          <q-btn
            flat
            label="Назад"
            v-if="bulkPtDialog.step > 1"
            @click="goBulkPtBack"
          />
          <q-btn
            color="primary"
            label="Далее"
            unelevated
            v-if="bulkPtDialog.step < 3"
            :disable="!bulkPtCanGoNext"
            @click="goBulkPtNext"
          />
          <template v-else>
            <q-btn
              flat
              color="primary"
              icon="add"
              label="Добавить ещё"
              :disable="!bulkPtCanAddDraft"
              @click="addBulkPtDraft"
            >
              <q-tooltip v-if="!bulkPtCanAddDraft">
                Заполните материал, этажи и тип закупа, чтобы добавить в очередь
              </q-tooltip>
            </q-btn>
            <q-btn
              color="positive"
              icon="check"
              unelevated
              :label="bulkPtPendingCount > 1 ? `Применить (${bulkPtPendingCount})` : 'Применить'"
              :disable="!bulkPtCanApply"
              @click="applyBulkPurchaseType"
            />
          </template>
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: групповое редактирование — норма расхода -->
    <q-dialog v-model="bulkNormDialog.open" persistent @hide="bulkNormApplyConfirmOpen = false">
      <q-card class="bulk-pt-card">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Групповое редактирование: норма расхода</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-stepper
          v-model="bulkNormDialog.step"
          animated
          flat
          alternative-labels
          color="primary"
          header-nav
          class="bulk-pt-stepper"
          @update:model-value="onBulkNormStepChange"
        >
          <q-step :name="1" title="Материал" icon="inventory_2" :done="bulkNormDialog.step > 1" :header-nav="true">
          <q-input
            v-model="bulkNormDialog.searchTerm"
            dense outlined clearable
            placeholder="Поиск по наименованию материала..."
            class="tw-mb-3"
            debounce="200"
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>
          <div
            v-if="!bulkNormFilteredMaterialOptions.length"
            class="tw-text-gray-400 tw-text-sm tw-py-6 tw-text-center"
          >
            {{ bulkNormDialog.searchTerm ? 'Ничего не найдено' : 'Нет материалов в загруженной смете' }}
          </div>
          <q-list v-else bordered separator class="bulk-pt-list">
            <q-item
              v-for="opt in bulkNormFilteredMaterialOptions"
              :key="opt.material_id"
              clickable
              :active="bulkNormDialog.selectedMaterialId === opt.material_id"
              active-class="bulk-pt-item--active"
              @click="onSelectBulkNormMaterial(opt.material_id)"
            >
              <q-item-section>
                <q-item-label>{{ opt.material_name || '—' }}</q-item-label>
                <q-item-label caption v-if="opt.unit_name">{{ opt.unit_name }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-badge color="blue-grey-4" text-color="white" :label="`${opt.rowsCount} стр.`">
                  <q-tooltip>Строк сметы по этому материалу</q-tooltip>
                </q-badge>
              </q-item-section>
            </q-item>
          </q-list>
          </q-step>

          <q-step
            :name="2"
            title="Тип закупа"
            icon="swap_horiz"
            :done="bulkNormDialog.step > 2"
            :header-nav="!!bulkNormDialog.selectedMaterialId"
          >
            <div
              v-if="!bulkNormPurchaseTypeOptions.length"
              class="tw-text-gray-400 tw-text-sm tw-py-6 tw-text-center"
            >
              Нет типов закупа для выбранного материала
            </div>
            <div
              v-else
              class="bulk-pt-pt-choices"
              :class="{ 'bulk-pt-pt-choices--single': bulkNormPurchaseTypeOptions.length === 1 }"
            >
              <button
                v-for="pt in bulkNormPurchaseTypeOptions"
                :key="pt.id"
                type="button"
                class="bulk-pt-pt-choice"
                :class="{ 'bulk-pt-pt-choice--active': bulkNormDialog.selectedPurchaseTypeId === pt.id }"
                @click="onSelectBulkNormPurchaseType(pt.id)"
              >
                <q-icon :name="pt.icon" size="22px" />
                <span class="bulk-pt-pt-choice-title">{{ pt.label }}</span>
              </button>
            </div>
          </q-step>

          <q-step
            :name="3"
            title="Где применить"
            icon="location_on"
            :done="bulkNormDialog.step > 3"
            :header-nav="bulkNormStep3HeaderNav"
          >
            <div
              v-if="!bulkNormScopeTree.length"
              class="tw-text-gray-400 tw-text-sm tw-py-6 tw-text-center"
            >
              Нет конструктивов для выбранного материала и типа закупа
              <template v-if="bulkNormDialog.selectedPurchaseTypeId === 1">
                (для «Подряд» не показываются позиции с АВР или КС2В)
              </template>
            </div>
            <div v-else class="bulk-pt-locations bulk-norm-scope">
              <div
                v-for="entrItem in bulkNormScopeTree"
                :key="entrItem.entrance ?? 'none'"
                class="bulk-pt-entrance"
              >
                <div class="bulk-pt-entrance-head">
                  <q-checkbox
                    :model-value="bulkNormScopeState(bulkNormScopeKeysForEntrance(entrItem)) === 'partial' ? null : (bulkNormScopeState(bulkNormScopeKeysForEntrance(entrItem)) === 'all')"
                    :indeterminate-value="null"
                    :toggle-indeterminate="false"
                    dense
                    @update:model-value="toggleBulkNormEntrance(entrItem)"
                  />
                  <span class="bulk-pt-entrance-title">Подъезд {{ entrItem.entrance ?? '—' }}</span>
                </div>
                <div
                  v-for="lvl in entrItem.levels"
                  :key="`${entrItem.entrance ?? ''}_${lvl.level_num ?? ''}`"
                  class="bulk-norm-scope-node"
                >
                  <div class="bulk-norm-scope-row">
                    <q-checkbox
                      :model-value="bulkNormScopeState(bulkNormScopeKeysForLevel(lvl)) === 'partial' ? null : (bulkNormScopeState(bulkNormScopeKeysForLevel(lvl)) === 'all')"
                      :indeterminate-value="null"
                      :toggle-indeterminate="false"
                      dense
                      @update:model-value="toggleBulkNormLevel(lvl)"
                    />
                    <span class="bulk-norm-scope-label">Этаж {{ lvl.level_num ?? '—' }}</span>
                  </div>
                  <div
                    v-for="grp in lvl.groups"
                    :key="`${grp.work_set_check_group_id ?? ''}`"
                    class="bulk-norm-scope-node bulk-norm-scope-node--group"
                  >
                    <div class="bulk-norm-scope-row">
                      <q-checkbox
                        :model-value="bulkNormScopeState(bulkNormScopeKeysForGroup(grp)) === 'partial' ? null : (bulkNormScopeState(bulkNormScopeKeysForGroup(grp)) === 'all')"
                        :indeterminate-value="null"
                        :toggle-indeterminate="false"
                        dense
                        @update:model-value="toggleBulkNormGroup(grp)"
                      />
                      <span class="bulk-norm-scope-label">{{ grp.work_set_check_group_name || 'Без группы' }}</span>
                    </div>
                    <div
                      v-for="work in grp.works"
                      :key="work.scopeKey"
                      class="bulk-norm-scope-node bulk-norm-scope-node--work"
                    >
                      <div class="bulk-norm-scope-row">
                        <q-checkbox
                          :model-value="bulkNormDialog.selectedScopeKeys.has(work.scopeKey)"
                          dense
                          @update:model-value="(val) => toggleBulkNormWork(work.scopeKey, val)"
                        />
                        <span class="bulk-norm-scope-label">{{ work.work_set_name || '—' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="bulkNormDialog.selectedScopeKeys.size"
              class="tw-text-xs tw-text-gray-600 tw-mt-3 tw-text-right"
            >
              Выбрано конструктивов: {{ bulkNormDialog.selectedScopeKeys.size }}
            </div>
          </q-step>

          <q-step
            :name="4"
            title="Норма расхода"
            icon="straighten"
            :header-nav="bulkNormStep4HeaderNav"
          >
            <q-linear-progress v-if="bulkNormMaterialSetsLoading" indeterminate class="tw-mb-3" />
            <div v-else-if="!bulkNormSelectedWorks.length" class="tw-text-gray-400 tw-text-sm tw-py-6 tw-text-center">
              Сначала выберите конструктивы на предыдущем шаге
            </div>
            <div v-else class="bulk-norm-consumption-step">
              <div class="tw-text-sm tw-mb-3">
                <span class="tw-text-gray-600">Материал:</span>
                <span class="tw-font-medium tw-ml-1">{{ bulkNormSelectedMaterialName }}</span>
              </div>
              <q-banner
                v-if="!bulkNormConsumptionOptions.length"
                dense
                rounded
                class="bg-blue-1 text-blue-9 tw-text-xs tw-mb-3"
              >
                В справочнике нет готовых норм — введите одно значение ниже; оно будет применено ко всем выбранным конструктивам.
              </q-banner>
              <q-table
                v-if="bulkNormConsumptionOptions.length"
                class="bulk-norm-preset-table tw-mb-3"
                flat
                bordered
                dense
                separator="cell"
                :rows="bulkNormConsumptionOptions"
                :columns="bulkNormPresetTableColumns"
                :row-key="bulkNormPresetRowKey"
                v-model:pagination="bulkNormPresetTablePagination"
                hide-pagination
              >
                <template #body="props">
                  <q-tr
                    :props="props"
                    class="bulk-norm-preset-row"
                    :class="{ 'bulk-norm-preset-row--active': bulkNormSelectedPresetKey === bulkNormPresetRowKey(props.row) }"
                    @click="onBulkNormPresetTableRowClick($event, props.row)"
                  >
                    <q-td
                      v-for="col in props.cols"
                      :key="col.name"
                      :props="props"
                    >
                      {{ col.value }}
                    </q-td>
                  </q-tr>
                </template>
              </q-table>
              <div class="bulk-norm-consumption-apply-all tw-mb-3">
                Своя норма 
                <q-input
                  :model-value="bulkNormApplyAllConsumption"
                  dense
                  outlined
                  clearable
                  inputmode="decimal"
                  placeholder="Напр. 0.025"
                  class="bulk-norm-custom-input bulk-norm-custom-input--narrow"
                  hide-bottom-space
                  @update:model-value="onBulkNormSharedConsumptionFieldUpdate"
                />
              </div>
              <!-- <div class="tw-text-xs tw-text-gray-600 tw-mb-2">
                Будет применено к конструктивам ({{ bulkNormSelectedWorks.length }}):
              </div>
              <div class="bulk-norm-consumption-list">
                <div
                  v-for="work in bulkNormSelectedWorks"
                  :key="work.scopeKey"
                  class="bulk-norm-consumption-item"
                >
                  <div class="bulk-norm-consumption-work-head">
                    <span class="bulk-norm-consumption-work-name">{{ work.work_set_name || '—' }}</span>
                    <span class="bulk-norm-consumption-work-meta">
                      П.{{ work.entrance ?? '—' }} · Эт.{{ work.level_num ?? '—' }}
                      <span v-if="work.work_set_check_group_name"> · {{ work.work_set_check_group_name }}</span>
                    </span>
                  </div>
                </div>
              </div> -->
            </div>
          </q-step>
        </q-stepper>

        <q-card-actions align="right" class="bulk-pt-actions">
          <q-btn flat label="Закрыть" no-caps v-close-popup />
          <q-btn
            flat
            label="Назад"
            v-if="bulkNormDialog.step > 1"
            @click="goBulkNormBack"
          />
          <q-btn
            color="primary"
            label="Далее"
            unelevated
            v-if="bulkNormDialog.step < 4"
            :disable="!bulkNormCanGoNext"
            :loading="bulkNormMaterialSetsLoading"
            @click="goBulkNormNext"
          />
          <q-btn
            v-if="bulkNormDialog.step === 4"
            icon="check"
            label="Применить"
            unelevated
            :color="bulkNormCanApply && !bulkNormApplying ? 'positive' : 'grey-5'"
            :text-color="bulkNormCanApply && !bulkNormApplying ? 'white' : 'grey-8'"
            :disable="!bulkNormCanApply || bulkNormApplying"
            :loading="bulkNormApplying"
            @click="openBulkNormApplyConfirm"
          >
            <q-tooltip v-if="!bulkNormCanApply">
              Выберите норму из списка или введите своё значение
            </q-tooltip>
            <q-tooltip v-else-if="bulkNormWouldChangeCount === 0">
              Все выбранные конструктивы уже с этой нормой — изменений не будет
            </q-tooltip>
          </q-btn>
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Подтверждение перед применением нормы расхода -->
    <q-dialog v-model="bulkNormApplyConfirmOpen" persistent>
      <q-card style="min-width: 320px; max-width: 420px;">
        <q-card-section class="text-body1">
          Обновить норму расхода? Кол-во материала изменится в таблице для подходящих строк; отправка на сервер — через «Сохранить изменения».
          Если по выбранной норме новое кол-во материала окажется меньше опер-факта по материалу — к таким строкам изменения не применятся.
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat no-caps label="Нет" color="primary" @click="bulkNormApplyConfirmOpen = false" />
          <q-btn
            unelevated
            no-caps
            color="positive"
            label="Да"
            :disable="!bulkNormCanApply || bulkNormApplying"
            :loading="bulkNormApplying"
            @click="confirmBulkNormApply"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка: выбор ТМЦ перед копированием на этажи -->
    <q-dialog v-model="copyMaterialPickDialog.open" persistent>
      <q-card style="min-width: 360px; max-width: 520px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выберите материал</div>
          <q-space />
          <q-btn icon="close" flat round dense @click="closeCopyMaterialPickDialog" />
        </q-card-section>
        <q-card-section class="tw-text-sm tw-text-gray-600 tw-pt-0">
          После выбора откроется окно с этажами
        </q-card-section>
        <q-card-section style="max-height: 360px; overflow-y: auto;" class="tw-pt-0">
          <q-list bordered separator>
            <q-item
              v-for="it in copyMaterialPickDialog.items"
              :key="copyMaterialPickDialog.mode === 'saved' ? (it.groupKey + '_' + it.material_id) : ('p_' + it._idx)"
              clickable
              @click="onPickMaterialRowForCopyToFloors(it)"
            >
              <q-item-section avatar>
                <q-icon name="category" color="teal" size="20px" />
              </q-item-section>
              <q-item-section>
                <q-item-label>
                  {{ copyMaterialPickDialog.mode === 'saved' ? getDisplayMaterialName(it) : it.material_name }}
                </q-item-label>
                <q-item-label caption>
                  {{ copyMaterialPickDialog.mode === 'saved' ? it.munt : it.material_unit_name }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Модалка: «Скопировать на этажи» (СМР или ТМЦ) -->
    <q-dialog v-model="copyToFloorsDialog.open" persistent>
      <q-card class="copy-floors-card" style="min-width: 560px; max-width: 720px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{ copyToFloorsDialog.mode === 'work' ? 'Скопировать СМР на этажи' : 'Скопировать ТМЦ на этажи' }}
          </div>
          <q-space />
          <q-btn icon="close" flat round dense @click="closeCopyToFloorsDialog" />
        </q-card-section>

        <q-card-section v-if="copyToFloorsDialog.snapshot" class="copy-floors-source">
          <div class="copy-floors-source-line">
            <q-icon name="construction" size="16px" color="primary" />
            <span class="copy-floors-source-name" :title="copyToFloorsDialog.snapshot.work_set_name">
              {{ copyToFloorsDialog.snapshot.work_set_name }}
            </span>
            <span v-if="copyToFloorsDialog.snapshot.unit_name" class="copy-floors-source-unit">
              ({{ copyToFloorsDialog.snapshot.unit_name }})
            </span>
            <q-badge
              v-if="copyToFloorsDialog.source === 'pending'"
              color="orange"
              label="из новой строки"
              class="tw-ml-2"
            >
              <q-tooltip>Источник — pending-добавление, цена будет пересчитана на сегодня</q-tooltip>
            </q-badge>
            <q-badge
              v-else
              color="primary"
              label="из сметы"
              class="tw-ml-2"
            >
              <q-tooltip>Источник — сохранённая работа, цена будет скопирована «как есть»</q-tooltip>
            </q-badge>
          </div>
          <div v-if="copyToFloorsDialog.mode === 'material'" class="copy-floors-source-line tw-mt-1">
            <q-icon name="category" size="16px" color="teal" />
            <span class="copy-floors-source-name" :title="copyToFloorsDialog.snapshot.material_name">
              {{ copyToFloorsDialog.snapshot.material_name }}
            </span>
            <span v-if="copyToFloorsDialog.snapshot.material_unit_name" class="copy-floors-source-unit">
              ({{ copyToFloorsDialog.snapshot.material_unit_name }})
            </span>
          </div>
          <div class="copy-floors-source-meta">
            Кол-во: <b>{{ copyToFloorsDialog.snapshot.qty }}</b>
            <template v-if="copyToFloorsDialog.mode === 'work' && copyToFloorsDialog.snapshot.materials?.length">
              · ТМЦ: <b>{{ copyToFloorsDialog.snapshot.materials.length }} поз.</b>
            </template>
          </div>
        </q-card-section>

        <q-card-section>
          <q-linear-progress v-if="copyToFloorsDialog.loading" indeterminate />
          <template v-else>
            <div
              v-if="!copySelectableFloors.length"
              class="tw-text-gray-400 tw-text-sm tw-py-6 tw-text-center"
            >
              Нет доступных этажей для копирования
              <div class="tw-text-xs tw-text-gray-300 tw-mt-1">
                Этажи с АВР/КС2В, начатым процессом приёмки, опер-фактом
                или дублирующейся работой/ТМЦ скрыты
              </div>
            </div>
            <template v-else>
              <div class="copy-floors-toolbar">
                <q-btn
                  flat dense no-caps
                  :icon="isAllCopySelected ? 'deselect' : 'select_all'"
                  :label="isAllCopySelected ? 'Снять выбор' : `Выбрать все (${copySelectableFloors.length})`"
                  size="sm"
                  color="primary"
                  @click="toggleSelectAllCopyFloors"
                />
                <span class="copy-floors-counter">
                  Выбрано: {{ copyToFloorsDialog.selectedFloorMapIds.size }} из {{ copySelectableFloors.length }}
                </span>
              </div>
              <div class="copy-floors-list">
                <div
                  v-for="entrance in copyEntrancesView"
                  :key="entrance.project_entrance_id ?? entrance.entrance_num"
                  class="copy-floors-entrance"
                >
                  <div class="copy-floors-entrance-head">
                    <q-checkbox
                      :model-value="copyEntranceState(entrance) === 'partial' ? null : (copyEntranceState(entrance) === 'all')"
                      :indeterminate-value="null"
                      :toggle-indeterminate="false"
                      dense
                      @update:model-value="toggleCopyEntrance(entrance)"
                    />
                    <span class="copy-floors-entrance-title">
                      Подъезд {{ entrance.entrance_num ?? '—' }}
                      <span v-if="entrance.block_name" class="copy-floors-block">{{ entrance.block_name }}</span>
                    </span>
                    <span class="copy-floors-entrance-meta">
                      {{ entrance.floors.length }} эт.
                    </span>
                  </div>
                  <div class="copy-floors-floors">
                    <label
                      v-for="floor in entrance.floors"
                      :key="floor.floor_map_id"
                      class="copy-floors-floor"
                    >
                      <q-checkbox
                        :model-value="copyToFloorsDialog.selectedFloorMapIds.has(floor.floor_map_id)"
                        dense
                        @update:model-value="(val) => toggleCopyFloor(floor, val)"
                      />
                      <span class="copy-floors-floor-label">Эт. {{ floor.level_num ?? '—' }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </template>
          </template>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" :disable="copyToFloorsDialog.applying" @click="closeCopyToFloorsDialog" />
          <q-btn
            color="positive"
            unelevated
            icon="content_copy"
            :label="copyToFloorsDialog.selectedFloorMapIds.size > 0
              ? `Скопировать (${copyToFloorsDialog.selectedFloorMapIds.size})`
              : 'Скопировать'"
            :disable="copyToFloorsDialog.selectedFloorMapIds.size === 0 || copyToFloorsDialog.applying"
            :loading="copyToFloorsDialog.applying"
            @click="confirmCopyToFloors"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';

const formatDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

import Amount from '@/components/common/Amount.vue';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';
import {
  getProjectSmetaNew,
  getSmetaNewWorkSetsByGroup,
  getSmetaNewAvailableGroups,
  getSmetaNewAvrList,
  getSmetaNewMaterialSearch,
  getSmetaNewRefreshPricesPreview,
  getSmetaNewRefreshSmrScope,
  getSmetaNewRefreshTmcPricesPreview,
  getSmetaNewNdsPercent,
  getSmetaNewFloorsForCopy,
  saveSmetaNewChanges,
  getSmetaChangeReasons,
  getProjectState,
} from './services';
import { getMaterialSets as getPackageMaterialSets } from '@/views/PackageList/services';

const props = defineProps({
  projectId: { type: Number, required: true },
  projectEntranceId: { type: [Number, String, null], default: null },
  floorMapId: { type: [Number, null], default: null },
  what: { type: String, default: 'general' },
  /** Фильтр по work_set_id с хаба; пустой массив — без ограничения */
  workSetIds: { type: Array, default: () => [] },
  /** ЖК; для шага 3 групповой нормы — у этих resident_id АВР не скрывается */
  residentId: { type: [Number, String, null], default: null },
});

/** На этих ЖК в шаге 3 «норма расхода» показываем все конструктивы, включая «Подряд» с АВР/КС2В */
const BULK_NORM_AVR_SHOW_RESIDENT_IDS = new Set([1515, 1524, 1516]);

const isBulkNormAvrShowResident = () => {
  const id = Number(props.residentId);
  return Number.isFinite(id) && BULK_NORM_AVR_SHOW_RESIDENT_IDS.has(id);
};

const { showWarningSnackbar, showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar();

// ─── данные ───────────────────────────────────────────────────────────────────
const isFetching = ref(false);
const isSaving = ref(false);
const isLoading = computed(() => isFetching.value || isSaving.value);
/**
 * Единое «состояние» проекта, приходит с бэка (см. getProjectState).
 * Держим здесь весь объект — новые поля будут доступны без правок компонента.
 */
const projectState = ref({ has_unsent_ds: false, unsent_ds_list: [], is_oper_fact_check_disabled: false });
const unsentDsList = computed(() => projectState.value.unsent_ds_list || []);
const hasUnsentDs = computed(() => !!projectState.value.has_unsent_ds);
const isOperFactCheckDisabled = computed(() => !!projectState.value.is_oper_fact_check_disabled);
const isEditable = computed(() => props.what === 'general' && !hasUnsentDs.value);
const data = ref([]);
const cachedNdsPercent = ref(null);

// ─── infinite scroll ──────────────────────────────────────────────────────────
const CHUNK_SIZE = 150;
const renderedCount = ref(CHUNK_SIZE);

// Виртуальные заголовки групп для новых групп (тех, что не в данных с бэка)
const dataWithPendingGroups = computed(() => {
  pendingVersion.value;
  const existingGroupKeys = new Set();
  const floorInfo = new Map(); // floor_map_id → { entrance, level_num, lastIndex }
  data.value.forEach((r, i) => {
    if (r.isGroupHeader) {
      existingGroupKeys.add(`${r.work_set_check_group_id}_${r.floor_map_id}`);
    }
    if (r.floor_map_id != null) {
      const cur = floorInfo.get(r.floor_map_id);
      if (!cur) {
        floorInfo.set(r.floor_map_id, {
          entrance: r.entrance,
          level_num: r.level_num,
          lastIndex: i,
        });
      } else {
        cur.lastIndex = i;
        if (cur.entrance == null && r.entrance != null) cur.entrance = r.entrance;
        if (cur.level_num == null && r.level_num != null) cur.level_num = r.level_num;
      }
    }
  });

  const seen = new Set();
  const virtualsByFloor = new Map(); // floor_map_id → [rows]
  const makeHeader = (entry) => {
    const key = `${entry.work_set_check_group_id}_${entry.floor_map_id}`;
    if (existingGroupKeys.has(key) || seen.has(key)) return null;
    seen.add(key);
    const info = floorInfo.get(entry.floor_map_id) || {};
    return {
      isGroupHeader: true,
      isVirtualGroup: true,
      work_set_check_group_id: entry.work_set_check_group_id,
      work_set_check_group_name: entry.work_set_check_group_name,
      floor_map_id: entry.floor_map_id,
      entrance: info.entrance ?? null,
      level_num: info.level_num ?? null,
      groupKey: `vg_${entry.work_set_check_group_id}_${entry.floor_map_id}`,
    };
  };
  for (const g of pendingChanges.newGroups) {
    const h = makeHeader(g);
    if (!h) continue;
    if (!virtualsByFloor.has(g.floor_map_id)) virtualsByFloor.set(g.floor_map_id, []);
    virtualsByFloor.get(g.floor_map_id).push(h);
  }
  for (const wa of pendingChanges.workAdditions) {
    const h = makeHeader(wa);
    if (!h) continue;
    if (!virtualsByFloor.has(wa.floor_map_id)) virtualsByFloor.set(wa.floor_map_id, []);
    virtualsByFloor.get(wa.floor_map_id).push(h);
  }

  if (!virtualsByFloor.size) return data.value;

  const result = [];
  const appended = new Set();
  data.value.forEach((r, i) => {
    result.push(r);
    if (r.floor_map_id != null) {
      const info = floorInfo.get(r.floor_map_id);
      if (info && info.lastIndex === i && virtualsByFloor.has(r.floor_map_id)) {
        result.push(...virtualsByFloor.get(r.floor_map_id));
        appended.add(r.floor_map_id);
      }
    }
  });
  virtualsByFloor.forEach((headers, fid) => {
    if (!appended.has(fid)) result.push(...headers);
  });
  return result;
});

const visibleData = computed(() => dataWithPendingGroups.value.slice(0, renderedCount.value));
const tableWrapperRef = ref(null);
let _scrollEl = null;

const onTableScroll = () => {
  const el = _scrollEl;
  if (!el) return;
  if (el.scrollHeight - el.scrollTop - el.clientHeight < 300 && renderedCount.value < dataWithPendingGroups.value.length) {
    renderedCount.value = Math.min(renderedCount.value + CHUNK_SIZE, dataWithPendingGroups.value.length);
  }
};

// ─── локальные изменения ──────────────────────────────────────────────────────
const pendingChanges = reactive({
  qtyWork: new Map(),       // key=groupKey → newQty
  qtyMaterial: new Map(),   // key=`${groupKey}_${material_id}` → newQty
  workSet: new Map(),       // key=groupKey → {newWorkSetId, newWorkSetName, newUnitName}
  material: new Map(),      // key=`${groupKey}_${material_id}` → {newMaterialId, newMaterialName, ...}
  purchaseType: new Map(),  // key=`${groupKey}_${material_id}` → purchaseTypeId (1 или 2)
  deleteWork: new Map(),    // key=groupKey → {work_set_id, work_set_check_group_id}
  deleteMaterial: new Map(), // key=`${groupKey}_${material_id}` → {work_set_id, work_set_check_group_id, material_id}
  workAdditions: [],         // [{work_set_id, work_set_check_group_id, work_set_name, floor_map_id}]
  materialAdditions: [],     // [{work_set_id, work_set_check_group_id, material_id, material_name, floor_map_id}]
  newGroups: [],             // [{work_set_check_group_id, work_set_check_group_name, floor_map_id}] — новые (пока пустые) группы
});

// вспомогательный счётчик для принудительного обновления computed
const pendingVersion = ref(0);
const triggerPendingUpdate = () => { pendingVersion.value++; };

/** Сравнение денег с бэком и с фронта (2 знака) */
const moneyDiffers = (backendVal, displayVal) => {
  const norm = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return Math.round(n * 100) / 100;
  };
  const b = norm(backendVal);
  const d = norm(displayVal);
  if (b === null && d === null) return false;
  if (b === null || d === null) return true;
  return b !== d;
};

const hasPendingChanges = computed(() => {
  pendingVersion.value;
  return pendingChanges.qtyWork.size > 0 ||
    pendingChanges.qtyMaterial.size > 0 ||
    pendingChanges.workSet.size > 0 ||
    pendingChanges.material.size > 0 ||
    pendingChanges.purchaseType.size > 0 ||
    pendingChanges.deleteWork.size > 0 ||
    pendingChanges.deleteMaterial.size > 0 ||
    pendingChanges.workAdditions.length > 0 ||
    pendingChanges.materialAdditions.length > 0 ||
    pendingChanges.newGroups.length > 0;
});

// true, если из всех pending-изменений сейчас есть только смена вида закупа
// (никаких qty/смен СМР/смен ТМЦ/удалений/добавлений/новых групп).
// В этом случае сохраняем без диалога «Причина пересчёта + комментарий».
const isOnlyPurchaseTypeChange = computed(() => {
  pendingVersion.value;
  return pendingChanges.purchaseType.size > 0 &&
    pendingChanges.qtyWork.size === 0 &&
    pendingChanges.qtyMaterial.size === 0 &&
    pendingChanges.workSet.size === 0 &&
    pendingChanges.material.size === 0 &&
    pendingChanges.deleteWork.size === 0 &&
    pendingChanges.deleteMaterial.size === 0 &&
    pendingChanges.workAdditions.length === 0 &&
    pendingChanges.materialAdditions.length === 0 &&
    pendingChanges.newGroups.length === 0;
});

const resetPendingChanges = () => {
  pendingChanges.qtyWork.clear();
  pendingChanges.qtyMaterial.clear();
  pendingChanges.workSet.clear();
  pendingChanges.material.clear();
  pendingChanges.purchaseType.clear();
  pendingChanges.deleteWork.clear();
  pendingChanges.deleteMaterial.clear();
  pendingChanges.workAdditions.splice(0);
  pendingChanges.materialAdditions.splice(0);
  pendingChanges.newGroups.splice(0);
  // выход из режима выбора СМР (если был активен)
  if (smrSelectionMode.value) {
    smrSelectionMode.value = false;
    smrScopeRows.value = [];
    smrSelectedKeys.value = new Set();
  }
  // выход из режима выбора ТМЦ (если был активен)
  if (tmcSelectionMode.value) {
    tmcSelectionMode.value = false;
    tmcScopeRows.value = [];
    tmcSelectedKeys.value = new Set();
  }
  triggerPendingUpdate();
};

// ─── helpers: отображение с учётом pending ────────────────────────────────────
const isWorkQtyChanged = (row) => {
  pendingVersion.value; // dependency
  return pendingChanges.qtyWork.has(row.groupKey);
};
const isMaterialQtyChanged = (row) => {
  pendingVersion.value;
  const key = `${row.groupKey}_${row.material_id}`;
  return pendingChanges.qtyMaterial.has(key);
};
const isWorkChanged = (row) => {
  pendingVersion.value;
  const ch = pendingChanges.workSet.get(row.groupKey);
  if (!ch) return false;
  return ch.newWorkSetId !== ch.oldWorkSetId;
};
const isMaterialChanged = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  const key = `${row.groupKey}_${row.material_id}`;
  const ch = pendingChanges.material.get(key);
  if (!ch) return false;
  return ch.newMaterialId !== ch.oldMaterialId;
};
const hasWorkPending = (row) => {
  pendingVersion.value;
  return pendingChanges.workSet.has(row.groupKey);
};
const hasMaterialPending = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  return pendingChanges.material.has(`${row.groupKey}_${row.material_id}`);
};

const getDisplayWorkQty = (row) => {
  pendingVersion.value;
  return pendingChanges.qtyWork.has(row.groupKey)
    ? pendingChanges.qtyWork.get(row.groupKey)
    : row.wqty;
};
const getDisplayMaterialQty = (row) => {
  pendingVersion.value;
  const key = `${row.groupKey}_${row.material_id}`;
  return pendingChanges.qtyMaterial.has(key)
    ? pendingChanges.qtyMaterial.get(key)
    : row.mqty;
};

/** Кол-во СМР для расчёта нормы (pending qty работы; без опер-факта — как в справочнике и orig_wqty). */
const getWorkQtyForNorm = (row) => {
  pendingVersion.value;
  if (pendingChanges.qtyWork.has(row.groupKey)) {
    return Number(pendingChanges.qtyWork.get(row.groupKey)) || 0;
  }
  return Number(getDisplayWorkQty(row) || row.orig_wqty || row.wqty || 0);
};

const getOriginalNormRatio = (row) => {
  if (!row.material_id) return null;
  const wq = Number(row.orig_wqty ?? row.wqty ?? 0);
  if (!wq) return row.norm_ratio > 0 ? row.norm_ratio : null;
  return Number(row.mqty) / wq;
};

const getDisplayNormRatio = (row) => {
  if (!row.material_id) return null;
  const wq = getWorkQtyForNorm(row);
  if (!wq) return null;
  return Number(getDisplayMaterialQty(row)) / wq;
};

const formatNormRatio = (n) => {
  if (n == null || Number.isNaN(n)) return '';
  return Number(n).toFixed(3);
};

const normDiffers = (oldVal, newVal) => {
  const norm = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return Math.round(n * 1000) / 1000;
  };
  const b = norm(oldVal);
  const d = norm(newVal);
  if (b === null && d === null) return false;
  if (b === null || d === null) return true;
  return b !== d;
};

const isNormRatioChanged = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  if (!isMaterialQtyChanged(row) && !isWorkQtyChanged(row)) return false;
  return normDiffers(getOriginalNormRatio(row), getDisplayNormRatio(row));
};

const getDisplayWorkName = (row) => {
  pendingVersion.value;
  return pendingChanges.workSet.has(row.groupKey)
    ? pendingChanges.workSet.get(row.groupKey).newWorkSetName
    : row.wname;
};
const getDisplayUnitName = (row) => {
  pendingVersion.value;
  return pendingChanges.workSet.has(row.groupKey)
    ? pendingChanges.workSet.get(row.groupKey).newUnitName
    : row.wunt;
};
const getDisplayWorkPrice = (row) => {
  pendingVersion.value;
  const ch = pendingChanges.workSet.get(row.groupKey);
  if (ch && ch.newWorkSetPrice != null) return ch.newWorkSetPrice;
  return row.wprc;
};
const getDisplayWorkPriceWithoutNds = (row) => {
  pendingVersion.value;
  const ch = pendingChanges.workSet.get(row.groupKey);
  if (ch && ch.newWorkSetPriceNoNds != null) return ch.newWorkSetPriceNoNds;
  return row.wprc_no_nds;
};
const getDisplayMaterialName = (row) => {
  pendingVersion.value;
  const key = `${row.groupKey}_${row.material_id}`;
  return pendingChanges.material.has(key)
    ? pendingChanges.material.get(key).newMaterialName
    : row.mname;
};
const getDisplayMaterialPrice = (row) => {
  pendingVersion.value;
  const key = `${row.groupKey}_${row.material_id}`;
  const change = pendingChanges.material.get(key);
  if (change && change.newMaterialPrice != null) return change.newMaterialPrice;
  return row.mprc;
};
const getDisplayMaterialSum = (row) => {
  pendingVersion.value;
  if (row.material_id && isMaterialPendingDelete(row)) return 0;
  if (!isMaterialSumChanged(row)) return row.msum;
  const price = getDisplayMaterialPrice(row);
  const qty = getDisplayMaterialQty(row);
  if (price == null || qty == null) return row.msum;
  return Math.round(price * qty * 100) / 100;
};

// ─── helpers: тип закупки ─────────────────────────────────────────────────────
const PURCHASE_TYPES = { CONTRACTOR: 1, TEAM_PROJECT: 2 };
const getDisplayPurchaseTypeId = (row) => {
  pendingVersion.value;
  if (!row.material_id) return null;
  const key = `${row.groupKey}_${row.material_id}`;
  const matChange = pendingChanges.material.get(key);
  if (matChange) return matChange.purchaseTypeId;
  if (pendingChanges.purchaseType.has(key)) return pendingChanges.purchaseType.get(key);
  return row.purchase_type_id || null;
};
const isPurchaseTypeChanged = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  const key = `${row.groupKey}_${row.material_id}`;
  const displayed = getDisplayPurchaseTypeId(row);
  const original = row.purchase_type_id || null;
  if (displayed !== original) return true;
  return pendingChanges.purchaseType.has(key);
};
const setPurchaseType = (row, typeId) => {
  if (!isEditable.value) return;
  if (hasOperFactMaterial(row)) {
    showWarningSnackbar('Нельзя изменить тип закупки: по материалу есть опер-факт');
    return;
  }
  if (isLockedAny(row)) {
    showWarningSnackbar(`Нельзя изменить тип закупки: на эту работу ${getLockedReason(row)}`);
    return;
  }
  if (isLockedByForeman(row)) return;
  const key = `${row.groupKey}_${row.material_id}`;
  const matChange = pendingChanges.material.get(key);
  if (matChange) {
    matChange.purchaseTypeId = typeId;
  } else if (typeId === row.purchase_type_id) {
    pendingChanges.purchaseType.delete(key);
  } else {
    pendingChanges.purchaseType.set(key, typeId);
  }
  triggerPendingUpdate();
};

// ─── helpers: блокировки (НУ / Опер-факт) ────────────────────────────────────
const getCheckGroupKey = (row) =>
  `${row.entrance || ""}_${row.level_num ?? ""}_${row.work_set_check_group_id ?? ""}`;

const lockedCheckGroups = computed(() => {
  const locked = new Set();
  data.value.forEach(r => {
    if (r.isGroupHeader) return;
    if (r.has_foreman_call) {
      locked.add(getCheckGroupKey(r));
    }
  });
  return locked;
});

// TEMPORARILY DISABLED: foreman call locks
// (опер-факт-проверки активны, см. hasOperFactWork/hasOperFactMaterial ниже)
const isGroupLockedByForeman = (/* row */) => false; // lockedCheckGroups.value.has(getCheckGroupKey(row));
const isLockedByForeman = (/* row */) => false; // !!row.is_nu_locked || isGroupLockedByForeman(row);
const getLockReason = (/* row */) => {
  // if (row.is_nu_locked) return 'Конструктив заблокирован (Принят НУ)';
  // if (isGroupLockedByForeman(row)) return 'Группа работ заблокирована: есть вызов ИТР';
  return '';
};
const hasOperFactWork = (row) =>
  !isOperFactCheckDisabled.value && !!row?.has_oper_fact_work;
const hasOperFactMaterial = (row) =>
  !isOperFactCheckDisabled.value && !!row?.has_oper_fact_material;

// ─── helpers: блокировка при наличии АВР (по конкретной работе, не по группе) ─
const getAvrWorkKey = (row) =>
  `${row.floor_map_id ?? ""}_${row.work_set_check_group_id ?? ""}_${row.work_set_id ?? ""}`;

const avrLockedWorks = computed(() => {
  const locked = new Set();
  data.value.forEach(r => {
    if (r.isGroupHeader) return;
    if ((r.avr_count || 0) > 0) {
      locked.add(getAvrWorkKey(r));
    }
  });
  return locked;
});

const hasAvr = (row) => avrLockedWorks.value.has(getAvrWorkKey(row));

// ─── helpers: «начатый процесс приёмки» (мягкая блокировка, без АВР/КС2В) ───
const getProcessGroupKey = (row) =>
  `${row.work_set_check_group_id ?? ""}_${row.floor_map_id ?? ""}`;

const processLockedGroups = computed(() => {
  const locked = new Set();
  data.value.forEach(r => {
    if (r.isGroupHeader) return;
    if ((r.process_count || 0) > 0 && (r.avr_count || 0) === 0) {
      locked.add(getProcessGroupKey(r));
    }
  });
  return locked;
});

const hasProcess = (row) => processLockedGroups.value.has(getProcessGroupKey(row));

const isLockedAny = (row) => hasAvr(row);
const getLockedReason = (row) => {
  if (hasAvr(row)) return 'есть АВР или КС2В';
  if (hasProcess(row)) return 'идёт процесс приёмки';
  return '';
};

// ТМЦ от «Команды проекта» (purchase_type_id=2) на работах с АВР/КС2В правится
// и удаляется без ограничений — цена по нему = 0, общая сумма приёмки не меняется.
// Запрет смены типа закупа на АВР сохраняется через setPurchaseType (см. ниже).
const isTeamProjectMaterial = (row) =>
  Number(getDisplayPurchaseTypeId(row)) === PURCHASE_TYPES.TEAM_PROJECT;

const canEditWork = (row) => !isLockedAny(row) && !hasOperFactWork(row);
const canEditMaterial = (row) =>
  (!isLockedAny(row) || isTeamProjectMaterial(row)) && !hasOperFactMaterial(row);
const canDeleteWork = (row) => !isLockedAny(row) && !hasOperFactWork(row);
const canDeleteMaterial = (row) =>
  (!isLockedAny(row) || isTeamProjectMaterial(row)) && !hasOperFactMaterial(row);

// ─── helpers: пометка на удаление ────────────────────────────────────────────
const isWorkPendingDelete = (row) => {
  pendingVersion.value;
  return pendingChanges.deleteWork.has(row.groupKey);
};
const isMaterialPendingDelete = (row) => {
  pendingVersion.value;
  const key = `${row.groupKey}_${row.material_id}`;
  return pendingChanges.deleteMaterial.has(key);
};

// ─── Групповое редактирование: тип закупа материала ──────────────────────────
// Bulk-операции (тип закупа, цены СМР/ТМЦ) сознательно НЕ учитывают
// projectState.is_oper_fact_check_disabled и всегда блокируют строки с опер-фактом.
// Это консервативный подход для пакетных изменений.
const isBulkPtRowBlocked = (r) =>
  isLockedAny(r) || !!r.has_oper_fact_work || !!r.has_oper_fact_material;

const getBulkPtBlockReason = (r) => {
  if (hasAvr(r))                return 'есть АВР или КС2В';
  if (r.has_oper_fact_work)     return 'есть опер-факт по работе';
  if (r.has_oper_fact_material) return 'есть опер-факт по материалу';
  return '';
};

const bulkPtDialog = reactive({
  open: false,
  step: 1,
  searchTerm: '',
  selectedMaterialId: null,
  selectedFloorMapIds: new Set(),
  newPurchaseTypeId: null,
  // drafts: накапливаемые «черновые» правки.
  // Применяются разом по нажатию «Применить» вместе с текущим выбором (если он валиден).
  drafts: [],
});

const resetBulkPtCurrentSelection = () => {
  bulkPtDialog.searchTerm = '';
  bulkPtDialog.selectedMaterialId = null;
  bulkPtDialog.selectedFloorMapIds = new Set();
  bulkPtDialog.newPurchaseTypeId = null;
};

const openBulkPtDialog = () => {
  bulkPtDialog.step = 1;
  resetBulkPtCurrentSelection();
  bulkPtDialog.drafts = [];
  bulkPtDialog.open = true;
};

// distinct материалы текущей сметы (data.value уже отфильтрована бэком по подъезду/этажу).
// Материал, у которого ВСЕ строки заблокированы (АВР/КС2В/опер-факт), не показываем.
const bulkPtMaterialOptions = computed(() => {
  pendingVersion.value;
  const m = new Map();
  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (!r.material_id) continue;
    if (isMaterialPendingDelete(r) || isWorkPendingDelete(r)) continue;
    if (!m.has(r.material_id)) {
      m.set(r.material_id, {
        material_id: r.material_id,
        material_name: r.mname || '',
        unit_name: r.munt || '',
        rows: [],
      });
    }
    m.get(r.material_id).rows.push(r);
  }
  return [...m.values()]
    .map(opt => ({
      ...opt,
      rowsAvailable: opt.rows.filter(rr => !isBulkPtRowBlocked(rr)).length,
    }))
    .filter(opt => opt.rowsAvailable > 0)
    .sort((a, b) => (a.material_name || '').localeCompare(b.material_name || '', 'ru'));
});

const bulkPtFilteredMaterialOptions = computed(() => {
  const term = (bulkPtDialog.searchTerm || '').trim().toLowerCase();
  if (!term) return bulkPtMaterialOptions.value;
  return bulkPtMaterialOptions.value.filter(opt =>
    (opt.material_name || '').toLowerCase().includes(term),
  );
});

// дерево «Подъезд → Этажи» для выбранного материала
const bulkPtLocations = computed(() => {
  pendingVersion.value;
  const opt = bulkPtMaterialOptions.value
    .find(x => x.material_id === bulkPtDialog.selectedMaterialId);
  if (!opt) return [];
  const byEntrance = new Map();
  for (const r of opt.rows) {
    const e = r.entrance ?? null;
    if (!byEntrance.has(e)) byEntrance.set(e, new Map());
    const fm = byEntrance.get(e);
    if (!fm.has(r.floor_map_id)) {
      fm.set(r.floor_map_id, {
        floor_map_id: r.floor_map_id,
        entrance: e,
        level_num: r.level_num,
        rows: [],
        ptSet: new Set(),
      });
    }
    const node = fm.get(r.floor_map_id);
    node.rows.push(r);
    node.ptSet.add(getDisplayPurchaseTypeId(r));
  }
  return [...byEntrance.entries()]
    .sort(([a], [b]) => (a ?? 0) - (b ?? 0))
    .map(([entrance, fm]) => ({
      entrance,
      floors: [...fm.values()]
        .map(n => {
          const allLocked = n.rows.every(rr => isBulkPtRowBlocked(rr));
          const lockReason = allLocked
            ? (getBulkPtBlockReason(n.rows[0]) || 'строки заблокированы')
            : '';
          const currentPt = n.ptSet.size > 1 ? 'mixed' : ([...n.ptSet][0] ?? null);
          return {
            floor_map_id: n.floor_map_id,
            entrance: n.entrance,
            level_num: n.level_num,
            allLocked,
            lockReason,
            currentPt,
          };
        })
        .sort((a, b) => (a.level_num ?? 0) - (b.level_num ?? 0)),
    }));
});

// Сводный статус выбора по подъезду: 'all' | 'partial' | 'none'.
const bulkPtEntranceState = (entranceItem) => {
  const usable = entranceItem.floors.filter(f => !f.allLocked);
  if (!usable.length) return 'none';
  const selected = usable.filter(f => bulkPtDialog.selectedFloorMapIds.has(f.floor_map_id)).length;
  if (selected === 0) return 'none';
  if (selected === usable.length) return 'all';
  return 'partial';
};

const toggleBulkPtFloor = (floor, val) => {
  if (floor.allLocked) return;
  const next = new Set(bulkPtDialog.selectedFloorMapIds);
  if (val) next.add(floor.floor_map_id);
  else next.delete(floor.floor_map_id);
  bulkPtDialog.selectedFloorMapIds = next;
};

const toggleBulkPtEntrance = (entranceItem) => {
  const usable = entranceItem.floors.filter(f => !f.allLocked);
  const next = new Set(bulkPtDialog.selectedFloorMapIds);
  const allSelected = usable.length
    && usable.every(f => next.has(f.floor_map_id));
  if (allSelected) {
    usable.forEach(f => next.delete(f.floor_map_id));
  } else {
    usable.forEach(f => next.add(f.floor_map_id));
  }
  bulkPtDialog.selectedFloorMapIds = next;
};

const onSelectBulkPtMaterial = (materialId) => {
  if (bulkPtDialog.selectedMaterialId !== materialId) {
    bulkPtDialog.selectedFloorMapIds = new Set();
    bulkPtDialog.newPurchaseTypeId = null;
  }
  bulkPtDialog.selectedMaterialId = materialId;
};

const bulkPtCanGoNext = computed(() => {
  if (bulkPtDialog.step === 1) return !!bulkPtDialog.selectedMaterialId;
  if (bulkPtDialog.step === 2) return bulkPtDialog.selectedFloorMapIds.size > 0;
  return false;
});

const goBulkPtNext = () => {
  if (!bulkPtCanGoNext.value) return;
  bulkPtDialog.step += 1;
};

const goBulkPtBack = () => {
  if (bulkPtDialog.step <= 1) return;
  bulkPtDialog.step -= 1;
};

// «Текущий выбор» валиден и его можно добавить в очередь / применить.
const bulkPtCanAddDraft = computed(() =>
  !!bulkPtDialog.selectedMaterialId
  && bulkPtDialog.selectedFloorMapIds.size > 0
  && (bulkPtDialog.newPurchaseTypeId === 1 || bulkPtDialog.newPurchaseTypeId === 2));

// «Применить» доступно, если есть хотя бы один draft ИЛИ валиден текущий выбор.
const bulkPtCanApply = computed(() =>
  bulkPtDialog.drafts.length > 0 || bulkPtCanAddDraft.value);

// Сколько «пачек» (drafts + текущий, если валиден) уйдёт при «Применить».
const bulkPtPendingCount = computed(() =>
  bulkPtDialog.drafts.length + (bulkPtCanAddDraft.value ? 1 : 0));

const addBulkPtDraft = () => {
  if (!bulkPtCanAddDraft.value) return;
  const opt = bulkPtMaterialOptions.value
    .find(x => x.material_id === bulkPtDialog.selectedMaterialId);
  bulkPtDialog.drafts.push({
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    material_id: bulkPtDialog.selectedMaterialId,
    material_name: opt?.material_name || '',
    unit_name: opt?.unit_name || '',
    floor_map_ids: [...bulkPtDialog.selectedFloorMapIds],
    purchase_type_id: bulkPtDialog.newPurchaseTypeId,
  });
  // возвращаемся к шагу 1 с чистым выбором — продолжаем добавлять
  resetBulkPtCurrentSelection();
  bulkPtDialog.step = 1;
};

const removeBulkPtDraft = (id) => {
  bulkPtDialog.drafts = bulkPtDialog.drafts.filter(d => d.id !== id);
};

const applyBulkPurchaseType = () => {
  // Собираем все задачи: накопленные drafts + текущий выбор, если валиден.
  const tasks = bulkPtDialog.drafts.map(d => ({
    material_id: d.material_id,
    floors: new Set(d.floor_map_ids),
    targetType: d.purchase_type_id,
  }));
  if (bulkPtCanAddDraft.value) {
    tasks.push({
      material_id: bulkPtDialog.selectedMaterialId,
      floors: new Set(bulkPtDialog.selectedFloorMapIds),
      targetType: bulkPtDialog.newPurchaseTypeId,
    });
  }
  if (!tasks.length) return;

  let changed = 0;
  let sameType = 0;
  let skippedLocked = 0;
  let skippedOperFact = 0;
  for (const t of tasks) {
    for (const r of data.value) {
      if (r.isGroupHeader) continue;
      if (r.material_id !== t.material_id) continue;
      if (!t.floors.has(r.floor_map_id)) continue;
      if (r.has_oper_fact_work || r.has_oper_fact_material) { skippedOperFact++; continue; }
      if (isLockedAny(r)) { skippedLocked++; continue; }
      if (getDisplayPurchaseTypeId(r) === t.targetType) { sameType++; continue; }
      setPurchaseType(r, t.targetType);
      changed++;
    }
  }
  bulkPtDialog.open = false;
  bulkPtDialog.drafts = [];

  if (changed === 0) {
    showSucceedSnackbar('Все выбранные строки уже имеют выбранный тип закупа или заблокированы');
    return;
  }
  const tail = [];
  if (sameType > 0)        tail.push(`совпадало: ${sameType}`);
  if (skippedLocked > 0)   tail.push(`АВР/КС2В: ${skippedLocked}`);
  if (skippedOperFact > 0) tail.push(`опер-факт: ${skippedOperFact}`);
  let msg = `Изменён тип закупа у ${changed} стр.`;
  if (tail.length) msg += ` (${tail.join(', ')})`;
  msg += '. Нажмите «Сохранить»';
  showSucceedSnackbar(msg);
};

// ─── Групповое редактирование: норма расхода ─────────────────────────────────
const bulkNormDialog = reactive({
  open: false,
  step: 1,
  searchTerm: '',
  selectedMaterialId: null,
  selectedPurchaseTypeId: null,
  selectedScopeKeys: new Set(),
  workConsumptions: {},
});

const bulkNormMaterialSetsLoading = ref(false);
const bulkNormApplying = ref(false);
const bulkNormApplyConfirmOpen = ref(false);
const bulkNormConsumptionOptions = ref([]);
/** Справочник наборов материалов — один запрос при открытии страницы */
const packageMaterialSetsCache = ref(null);
let packageMaterialSetsLoadPromise = null;
const bulkNormApplyAllConsumption = ref('');
/** Ключ выбранной строки справочника (отдельно от поля «своя норма») */
const bulkNormSelectedPresetKey = ref(null);

const bulkNormPresetTableColumns = [
  {
    name: 'set_name',
    label: 'Набор',
    field: 'set_name',
    align: 'left',
    sortable: true,
    format: (v) => (v != null && String(v).trim() !== '' ? v : '—'),
  },
  {
    name: 'formula',
    label: 'Формула',
    field: 'formula',
    align: 'left',
    format: (v) => (v != null && String(v).trim() !== '' ? v : '—'),
  },
  {
    name: 'consumption',
    label: 'Норма расхода',
    field: 'consumption',
    align: 'right',
    sortable: true,
  },
];

const bulkNormPresetRowKey = (row) => `${row.set_name}\0${row.consumption}\0${row.formula}`;

const bulkNormPresetTablePagination = ref({ rowsPerPage: 0 });

const parseBulkNormConsumption = (raw) => {
  if (raw === '' || raw == null) return null;
  const n = Number(String(raw).trim().replace(',', '.'));
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
};

const clearBulkNormConsumptionsForSelected = () => {
  for (const work of bulkNormSelectedWorks.value) {
    delete bulkNormDialog.workConsumptions[work.scopeKey];
  }
};

/** Значение из справочника — для всех выбранных на шаге «Где применить» конструктивов */
const setBulkNormConsumptionForAll = (value) => {
  for (const work of bulkNormSelectedWorks.value) {
    bulkNormDialog.workConsumptions[work.scopeKey] = value;
  }
};

const onBulkNormPickPreset = (value) => {
  const num = value != null && typeof value === 'object'
    ? Number(value.consumption)
    : Number(value);
  if (Number.isNaN(num)) return;
  setBulkNormConsumptionForAll(num);
  bulkNormSelectedPresetKey.value = bulkNormPresetRowKey(value);
  bulkNormApplyAllConsumption.value = '';
};

const onBulkNormPresetTableRowClick = (_evt, row) => {
  onBulkNormPickPreset(row);
};

const onBulkNormSharedConsumptionFieldUpdate = (val) => {
  bulkNormApplyAllConsumption.value = val;
  bulkNormSelectedPresetKey.value = null;
  const trimmed = val == null ? '' : String(val).trim();
  if (!trimmed) {
    clearBulkNormConsumptionsForSelected();
    return;
  }
  const parsed = parseBulkNormConsumption(val);
  if (parsed != null) {
    setBulkNormConsumptionForAll(parsed);
  } else {
    clearBulkNormConsumptionsForSelected();
  }
};

const bulkNormApplyAllConsumptionValid = computed(() =>
  parseBulkNormConsumption(bulkNormApplyAllConsumption.value) != null,
);

const openBulkNormDialog = () => {
  bulkNormDialog.step = 1;
  bulkNormDialog.searchTerm = '';
  bulkNormDialog.selectedMaterialId = null;
  bulkNormDialog.selectedPurchaseTypeId = null;
  bulkNormDialog.selectedScopeKeys = new Set();
  bulkNormDialog.workConsumptions = {};
  bulkNormConsumptionOptions.value = [];
  bulkNormApplyAllConsumption.value = '';
  bulkNormSelectedPresetKey.value = null;
  bulkNormApplyConfirmOpen.value = false;
  bulkNormLastValidStep = 1;
  bulkNormDialog.open = true;
};

const openBulkNormApplyConfirm = () => {
  if (!bulkNormCanApply.value || bulkNormApplying.value) return;
  bulkNormApplyConfirmOpen.value = true;
};

const confirmBulkNormApply = () => {
  bulkNormApplyConfirmOpen.value = false;
  void applyBulkNorm();
};

const onSelectBulkNormMaterial = (materialId) => {
  if (bulkNormDialog.selectedMaterialId !== materialId) {
    bulkNormDialog.selectedPurchaseTypeId = null;
    bulkNormDialog.selectedScopeKeys = new Set();
    bulkNormDialog.workConsumptions = {};
    bulkNormConsumptionOptions.value = [];
  }
  bulkNormDialog.selectedMaterialId = materialId;
  pruneBulkNormSelectedScopeKeys();
};

const onSelectBulkNormPurchaseType = (ptId) => {
  if (bulkNormDialog.selectedPurchaseTypeId !== ptId) {
    bulkNormDialog.selectedScopeKeys = new Set();
    bulkNormDialog.workConsumptions = {};
  }
  bulkNormDialog.selectedPurchaseTypeId = ptId;
  pruneBulkNormSelectedScopeKeys();
};

const bulkNormCanGoNext = computed(() => {
  if (bulkNormDialog.step === 1) return !!bulkNormDialog.selectedMaterialId;
  if (bulkNormDialog.step === 2) {
    return bulkNormDialog.selectedPurchaseTypeId === 1
      || bulkNormDialog.selectedPurchaseTypeId === 2;
  }
  if (bulkNormDialog.step === 3) return bulkNormDialog.selectedScopeKeys.size > 0;
  return false;
});

const bulkNormStep3HeaderNav = computed(() =>
  !!bulkNormDialog.selectedMaterialId
  && (bulkNormDialog.selectedPurchaseTypeId === 1
    || bulkNormDialog.selectedPurchaseTypeId === 2),
);

const bulkNormStep4HeaderNav = computed(() =>
  bulkNormStep3HeaderNav.value && bulkNormDialog.selectedScopeKeys.size > 0,
);

let bulkNormLastValidStep = 1;

const prepareBulkNormConsumptionStep = async () => {
  pruneBulkNormWorkConsumptions();
  await loadBulkNormConsumptionOptions();
  bulkNormApplyAllConsumption.value = '';
  bulkNormSelectedPresetKey.value = null;
};

const onBulkNormStepChange = async (newStep) => {
  const target = Number(newStep);
  if (target === bulkNormLastValidStep) return;
  const prev = bulkNormLastValidStep;
  if (target === 3 && prev !== 3) {
    pruneBulkNormSelectedScopeKeys();
  }
  if (target === 4 && prev !== 4 && bulkNormStep4HeaderNav.value) {
    await prepareBulkNormConsumptionStep();
  }
  bulkNormLastValidStep = target;
};

const goBulkNormBack = () => {
  if (bulkNormDialog.step <= 1) return;
  bulkNormDialog.step -= 1;
  bulkNormLastValidStep = bulkNormDialog.step;
};

const extractBulkNormPresetOptions = (list, materialId) => {
  const mid = Number(materialId);
  const seen = new Set();
  const out = [];
  for (const set of list || []) {
    const setName = set.set_name != null ? String(set.set_name) : '';
    for (const m of set.materials || []) {
      if (Number(m.material_id) !== mid) continue;
      const c = Number(m.consumption);
      if (Number.isNaN(c)) continue;
      const formula = m.formula != null && String(m.formula).trim() !== ''
        ? String(m.formula)
        : '';
      const dedupeKey = `${setName}\0${c}\0${formula}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push({ set_name: setName, consumption: c, formula });
    }
  }
  out.sort((a, b) => {
    const byName = a.set_name.localeCompare(b.set_name, 'ru');
    if (byName !== 0) return byName;
    if (a.consumption !== b.consumption) return a.consumption - b.consumption;
    return a.formula.localeCompare(b.formula, 'ru');
  });
  return out;
};

const loadPackageMaterialSetsOnce = async () => {
  if (packageMaterialSetsCache.value != null) {
    return packageMaterialSetsCache.value;
  }
  if (!packageMaterialSetsLoadPromise) {
    packageMaterialSetsLoadPromise = (async () => {
      try {
        const sets = await getPackageMaterialSets();
        packageMaterialSetsCache.value = Array.isArray(sets) ? sets : [];
      } catch {
        packageMaterialSetsCache.value = [];
      } finally {
        packageMaterialSetsLoadPromise = null;
      }
      return packageMaterialSetsCache.value;
    })();
  }
  return packageMaterialSetsLoadPromise;
};

const loadBulkNormConsumptionOptions = async () => {
  const mid = bulkNormDialog.selectedMaterialId;
  if (!mid) {
    bulkNormConsumptionOptions.value = [];
    return;
  }
  bulkNormMaterialSetsLoading.value = true;
  try {
    const sets = await loadPackageMaterialSetsOnce();
    bulkNormConsumptionOptions.value = extractBulkNormPresetOptions(sets, mid);
  } catch {
    bulkNormConsumptionOptions.value = [];
    showErrorSnackbar('Не удалось загрузить нормы расхода из справочника');
  } finally {
    bulkNormMaterialSetsLoading.value = false;
  }
};

const pruneBulkNormWorkConsumptions = () => {
  const allowed = bulkNormDialog.selectedScopeKeys;
  const next = {};
  for (const key of allowed) {
    if (bulkNormDialog.workConsumptions[key] != null) {
      next[key] = bulkNormDialog.workConsumptions[key];
    }
  }
  bulkNormDialog.workConsumptions = next;
};

const goBulkNormNext = async () => {
  if (!bulkNormCanGoNext.value) return;
  if (bulkNormDialog.step === 2) {
    pruneBulkNormSelectedScopeKeys();
  }
  if (bulkNormDialog.step === 3) {
    await prepareBulkNormConsumptionStep();
  }
  bulkNormDialog.step += 1;
  bulkNormLastValidStep = bulkNormDialog.step;
};

/** Расчёт кол-ва ТМЦ по норме. forBulkNorm: без опер-факта (как в тексте мастера нормы). */
const calcBulkNormMaterialAmount = (row, consumption, { forBulkNorm = false } = {}) => {
  const workQty = forBulkNorm
    ? Number(getDisplayWorkQty(row) || row.orig_wqty || 0)
    : (hasOperFactWork(row) && row.oper_fact_work_qty != null
      ? Number(row.oper_fact_work_qty)
      : Number(getDisplayWorkQty(row) || row.orig_wqty || 0));
  if (!workQty || workQty <= 0 || Number.isNaN(consumption)) return null;

  let amount = Math.round(consumption * workQty * 10000) / 10000;
  if (isPieceMaterial(row)) amount = roundPieceQty(amount);

  if (!forBulkNorm && hasOperFactMaterial(row) && row.oper_fact_material_qty != null) {
    const minMqty = parseFloat(row.oper_fact_material_qty);
    if (amount < minMqty) amount = minMqty;
  }
  return amount;
};

/** Массовая норма: не снижать кол-во материала ниже опер-факта — такие строки пропускаем. */
const bulkNormBlockedByOperFactMaterial = (row, materialAmount) => {
  if (materialAmount == null) return false;
  if (!hasOperFactMaterial(row) || row.oper_fact_material_qty == null) return false;
  const minMqty = parseFloat(row.oper_fact_material_qty);
  if (Number.isNaN(minMqty)) return false;
  return materialAmount < minMqty;
};

const applyBulkNorm = () => {
  if (bulkNormApplying.value) return;
  if (!bulkNormCanApply.value) return;

  const mid = bulkNormDialog.selectedMaterialId;
  const pt = bulkNormDialog.selectedPurchaseTypeId;
  let applied = 0;
  let skippedLocked = 0;
  let skippedNoWorkQty = 0;
  let skippedNoWorkSet = 0;
  let skippedUnchanged = 0;
  let skippedOperFactMaterial = 0;

  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (r.material_id !== mid) continue;
    if (getDisplayPurchaseTypeId(r) !== pt) continue;
    if (isMaterialPendingDelete(r) || isWorkPendingDelete(r)) continue;

    const scopeKey = makeBulkNormScopeKey(
      r.entrance,
      r.level_num,
      r.work_set_check_group_id,
      r.work_set_id,
    );

    if (!bulkNormDialog.selectedScopeKeys.has(scopeKey)) continue;

    const consumption = parseBulkNormConsumption(bulkNormDialog.workConsumptions[scopeKey]);
    if (consumption == null) continue;

    if (isBulkNormApplyLocked(r)) { skippedLocked++; continue; }

    if (!r.work_set_id || r.work_set_check_group_id == null) {
      skippedNoWorkSet++;
      continue;
    }

    const materialAmount = calcBulkNormMaterialAmount(r, consumption, { forBulkNorm: true });
    if (materialAmount == null) { skippedNoWorkQty++; continue; }
    if (bulkNormBlockedByOperFactMaterial(r, materialAmount)) {
      skippedOperFactMaterial++;
      continue;
    }

    const matKey = `${r.groupKey}_${r.material_id}`;
    if (bulkNormRowAlreadyAtConsumption(r, consumption)) {
      skippedUnchanged++;
      continue;
    }
    pendingChanges.qtyMaterial.set(matKey, materialAmount);
    applied++;
  }

  if (!applied) {
    if (skippedUnchanged > 0 && !skippedLocked && !skippedNoWorkQty && !skippedNoWorkSet && !skippedOperFactMaterial) {
      bulkNormDialog.open = false;
      showWarningSnackbar(
        `Все выбранные конструктивы (${skippedUnchanged}) уже соответствуют выбранной норме расхода.`,
      );
      return;
    }
    if (skippedOperFactMaterial > 0 && !skippedLocked && !skippedNoWorkQty && !skippedNoWorkSet && !skippedUnchanged) {
      bulkNormDialog.open = false;
      showSucceedSnackbar('Изменений не было.');
      return;
    }
    const hints = [];
    if (skippedUnchanged > 0) hints.push('часть строк уже соответствует выбранной норме');
    const hint = hints.length ? ` ${hints.join('; ')}.` : '';
    showWarningSnackbar(`Не удалось применить норму: строки заблокированы или нет данных для сохранения.${hint}`);
    return;
  }

  triggerPendingUpdate();
  bulkNormDialog.open = false;

  const tail = [];
  if (skippedLocked > 0) tail.push(`заблокировано: ${skippedLocked}`);
  if (skippedNoWorkQty > 0) tail.push(`без кол-ва работы: ${skippedNoWorkQty}`);
  if (skippedNoWorkSet > 0) tail.push(`без work_set_id/work_set_check_group_id: ${skippedNoWorkSet}`);
  if (skippedUnchanged > 0) tail.push(`без изменений (уже по норме): ${skippedUnchanged}`);
  let msg = `Обновлена норма расхода у ${applied} стр. Нажмите «Сохранить изменения» для отправки на сервер.`;
  if (tail.length) msg += ` (${tail.join(', ')})`;
  showSucceedSnackbar(msg);
};

const makeBulkNormScopeKey = (entrance, levelNum, groupId, workSetId) =>
  `${entrance ?? ''}|${levelNum ?? ''}|${groupId ?? ''}|${workSetId ?? ''}`;

/** Шаг 3: скрываем «Подряд»+АВР/КС2В для всех ЖК, кроме BULK_NORM_AVR_SHOW_RESIDENT_IDS. */
const isBulkNormScopeBlocked = (row) => {
  if (isBulkNormAvrShowResident()) return false;
  return hasAvr(row) && getDisplayPurchaseTypeId(row) === PURCHASE_TYPES.CONTRACTOR;
};

/** При применении нормы: пропускаем строки с АВР, кроме ЖК из BULK_NORM_AVR_SHOW_RESIDENT_IDS. */
const isBulkNormApplyLocked = (row) => !isBulkNormAvrShowResident() && isLockedAny(row);

const bulkNormBlockedScopeKeys = computed(() => {
  pendingVersion.value;
  const blocked = new Set();
  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (!isBulkNormScopeBlocked(r)) continue;
    blocked.add(makeBulkNormScopeKey(
      r.entrance,
      r.level_num,
      r.work_set_check_group_id,
      r.work_set_id,
    ));
  }
  return blocked;
});

const pruneBulkNormSelectedScopeKeys = () => {
  const blocked = bulkNormBlockedScopeKeys.value;
  if (!blocked.size && !bulkNormDialog.selectedScopeKeys.size) return;
  const next = new Set();
  for (const k of bulkNormDialog.selectedScopeKeys) {
    if (!blocked.has(k)) next.add(k);
  }
  if (next.size !== bulkNormDialog.selectedScopeKeys.size) {
    bulkNormDialog.selectedScopeKeys = next;
    pruneBulkNormWorkConsumptions();
  }
};

// wname заполняется только в первой строке конструктива (rowspan в таблице).
// work_set_name приходит с API на каждой строке — используем его для подписи в дереве.
const getBulkNormWorkSetName = (row) =>
  getDisplayWorkName(row) || row.work_set_name || row.wname || '';

const bulkNormPurchaseTypeOptions = computed(() => {
  pendingVersion.value;
  const mid = bulkNormDialog.selectedMaterialId;
  if (!mid) return [];
  const types = new Set();
  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (r.material_id !== mid) continue;
    if (isMaterialPendingDelete(r) || isWorkPendingDelete(r)) continue;
    const pt = getDisplayPurchaseTypeId(r);
    if (pt === 1 || pt === 2) types.add(pt);
  }
  const opts = [];
  if (types.has(1)) opts.push({ id: 1, label: 'Подряд', icon: 'engineering' });
  if (types.has(2)) opts.push({ id: 2, label: 'Команда проекта', icon: 'group' });
  return opts;
});

const bulkNormScopeTree = computed(() => {
  pendingVersion.value;
  const mid = bulkNormDialog.selectedMaterialId;
  const pt = bulkNormDialog.selectedPurchaseTypeId;
  if (!mid || !pt) return [];

  const byEntrance = new Map();
  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (r.material_id !== mid) continue;
    if (getDisplayPurchaseTypeId(r) !== pt) continue;
    if (isMaterialPendingDelete(r) || isWorkPendingDelete(r)) continue;
    if (isBulkNormScopeBlocked(r)) continue;

    const entrance = r.entrance ?? null;
    const levelNum = r.level_num ?? null;
    const groupId = r.work_set_check_group_id ?? null;
    const workSetId = r.work_set_id ?? null;
    const scopeKey = makeBulkNormScopeKey(entrance, levelNum, groupId, workSetId);

    if (!byEntrance.has(entrance)) byEntrance.set(entrance, new Map());
    const byLevel = byEntrance.get(entrance);
    if (!byLevel.has(levelNum)) byLevel.set(levelNum, new Map());
    const byGroup = byLevel.get(levelNum);
    if (!byGroup.has(groupId)) {
      byGroup.set(groupId, {
        work_set_check_group_id: groupId,
        work_set_check_group_name: r.work_set_check_group_name || '',
        works: new Map(),
      });
    }
    const groupNode = byGroup.get(groupId);
    if (!groupNode.works.has(workSetId)) {
      groupNode.works.set(workSetId, {
        work_set_id: workSetId,
        work_set_name: getBulkNormWorkSetName(r),
        scopeKey,
      });
    } else {
      const workNode = groupNode.works.get(workSetId);
      if (!workNode.work_set_name) {
        workNode.work_set_name = getBulkNormWorkSetName(r);
      }
    }
  }

  return [...byEntrance.entries()]
    .sort(([a], [b]) => (a ?? 0) - (b ?? 0))
    .map(([entrance, byLevel]) => ({
      entrance,
      levels: [...byLevel.entries()]
        .sort(([a], [b]) => (a ?? 0) - (b ?? 0))
        .map(([level_num, byGroup]) => ({
          level_num,
          groups: [...byGroup.values()]
            .map(g => ({
              work_set_check_group_id: g.work_set_check_group_id,
              work_set_check_group_name: g.work_set_check_group_name,
              works: [...g.works.values()].sort((a, b) =>
                (a.work_set_name || '').localeCompare(b.work_set_name || '', 'ru'),
              ),
            }))
            .filter(g => g.works.length > 0)
            .sort((a, b) =>
              (a.work_set_check_group_name || '').localeCompare(b.work_set_check_group_name || '', 'ru'),
            ),
        }))
        .filter(lvl => lvl.groups.length > 0),
    }))
    .filter(entr => entr.levels.length > 0);
});

const bulkNormSelectedWorks = computed(() => {
  const selected = bulkNormDialog.selectedScopeKeys;
  const works = [];
  for (const entr of bulkNormScopeTree.value) {
    for (const lvl of entr.levels) {
      for (const grp of lvl.groups) {
        for (const work of grp.works) {
          if (!selected.has(work.scopeKey)) continue;
          works.push({
            ...work,
            entrance: entr.entrance,
            level_num: lvl.level_num,
            work_set_check_group_name: grp.work_set_check_group_name,
          });
        }
      }
    }
  }
  return works.sort((a, b) => {
    const ea = a.entrance ?? 0;
    const eb = b.entrance ?? 0;
    if (ea !== eb) return ea - eb;
    const la = a.level_num ?? 0;
    const lb = b.level_num ?? 0;
    if (la !== lb) return la - lb;
    return (a.work_set_name || '').localeCompare(b.work_set_name || '', 'ru');
  });
});

const bulkNormAllWorksHaveConsumption = () =>
  bulkNormSelectedWorks.value.length > 0
  && bulkNormSelectedWorks.value.every((w) =>
    parseBulkNormConsumption(bulkNormDialog.workConsumptions[w.scopeKey]) != null,
  );

/** Строка уже соответствует выбранной норме (кол-во и норма на ед. работы). */
const bulkNormRowAlreadyAtConsumption = (row, consumption) => {
  if (consumption == null) return false;
  if (normDiffers(getOriginalNormRatio(row), consumption)) return false;
  const amount = calcBulkNormMaterialAmount(row, consumption, { forBulkNorm: true });
  if (amount == null) return false;
  return Math.abs(amount - Number(row.mqty)) < 1e-6;
};

/** Сколько строк сметы реально изменятся при текущем выборе нормы и конструктивов. */
const bulkNormWouldChangeCount = computed(() => {
  pendingVersion.value;
  const mid = bulkNormDialog.selectedMaterialId;
  const pt = bulkNormDialog.selectedPurchaseTypeId;
  if (!mid || !pt) return 0;
  let count = 0;
  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (r.material_id !== mid) continue;
    if (getDisplayPurchaseTypeId(r) !== pt) continue;
    if (isMaterialPendingDelete(r) || isWorkPendingDelete(r)) continue;
    const scopeKey = makeBulkNormScopeKey(
      r.entrance,
      r.level_num,
      r.work_set_check_group_id,
      r.work_set_id,
    );
    if (!bulkNormDialog.selectedScopeKeys.has(scopeKey)) continue;
    const consumption = parseBulkNormConsumption(bulkNormDialog.workConsumptions[scopeKey]);
    if (consumption == null) continue;
    if (isBulkNormApplyLocked(r)) continue;
    if (!r.work_set_id || r.work_set_check_group_id == null) continue;
    const materialAmount = calcBulkNormMaterialAmount(r, consumption, { forBulkNorm: true });
    if (materialAmount == null) continue;
    if (bulkNormBlockedByOperFactMaterial(r, materialAmount)) continue;
    if (!bulkNormRowAlreadyAtConsumption(r, consumption)) count++;
  }
  return count;
});

/** Шаг 4: выбрана строка справочника или введена валидная своя норма */
const bulkNormCanApply = computed(() => {
  if (bulkNormDialog.step !== 4) return false;
  if (!bulkNormAllWorksHaveConsumption()) return false;
  if (bulkNormApplyAllConsumptionValid.value) return true;
  if (bulkNormSelectedPresetKey.value != null) return true;
  return false;
});

const bulkNormScopeKeysForEntrance = (entrItem) => {
  const keys = [];
  for (const lvl of entrItem.levels) keys.push(...bulkNormScopeKeysForLevel(lvl));
  return keys;
};

const bulkNormScopeKeysForLevel = (lvl) => {
  const keys = [];
  for (const grp of lvl.groups) keys.push(...bulkNormScopeKeysForGroup(grp));
  return keys;
};

const bulkNormScopeKeysForGroup = (grp) => grp.works.map(w => w.scopeKey);

const bulkNormScopeState = (keys) => {
  if (!keys.length) return 'none';
  const selected = keys.filter(k => bulkNormDialog.selectedScopeKeys.has(k)).length;
  if (selected === 0) return 'none';
  if (selected === keys.length) return 'all';
  return 'partial';
};

const setBulkNormScopeKeys = (keys, selectAll) => {
  const next = new Set(bulkNormDialog.selectedScopeKeys);
  if (selectAll) keys.forEach(k => next.add(k));
  else keys.forEach(k => next.delete(k));
  bulkNormDialog.selectedScopeKeys = next;
};

const toggleBulkNormEntrance = (entrItem) => {
  const keys = bulkNormScopeKeysForEntrance(entrItem);
  setBulkNormScopeKeys(keys, bulkNormScopeState(keys) !== 'all');
};

const toggleBulkNormLevel = (lvl) => {
  const keys = bulkNormScopeKeysForLevel(lvl);
  setBulkNormScopeKeys(keys, bulkNormScopeState(keys) !== 'all');
};

const toggleBulkNormGroup = (grp) => {
  const keys = bulkNormScopeKeysForGroup(grp);
  setBulkNormScopeKeys(keys, bulkNormScopeState(keys) !== 'all');
};

const toggleBulkNormWork = (scopeKey, val) => {
  const next = new Set(bulkNormDialog.selectedScopeKeys);
  if (val) next.add(scopeKey);
  else next.delete(scopeKey);
  bulkNormDialog.selectedScopeKeys = next;
};

// Уникальные материалы из уже загруженной сметы (data.value).
const bulkNormMaterialOptions = computed(() => {
  pendingVersion.value;
  const m = new Map();
  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (!r.material_id) continue;
    if (isMaterialPendingDelete(r) || isWorkPendingDelete(r)) continue;
    if (!m.has(r.material_id)) {
      m.set(r.material_id, {
        material_id: r.material_id,
        material_name: getDisplayMaterialName(r) || r.mname || '',
        unit_name: r.munt || '',
        rowsCount: 0,
      });
    }
    m.get(r.material_id).rowsCount += 1;
  }
  return [...m.values()].sort((a, b) =>
    (a.material_name || '').localeCompare(b.material_name || '', 'ru'),
  );
});

const bulkNormSelectedMaterialName = computed(() => {
  const id = bulkNormDialog.selectedMaterialId;
  if (id == null) return '—';
  const row = bulkNormMaterialOptions.value.find(
    o => Number(o.material_id) === Number(id),
  );
  return row?.material_name?.trim() || '—';
});

const bulkNormFilteredMaterialOptions = computed(() => {
  const term = (bulkNormDialog.searchTerm || '').trim().toLowerCase();
  if (!term) return bulkNormMaterialOptions.value;
  return bulkNormMaterialOptions.value.filter(opt =>
    (opt.material_name || '').toLowerCase().includes(term),
  );
});

const toggleDeleteWork = (row) => {
  if (!isEditable.value) return;
  if (pendingChanges.deleteWork.has(row.groupKey)) {
    pendingChanges.deleteWork.delete(row.groupKey);
    data.value.forEach(r => {
      if (!r.isGroupHeader && r.groupKey === row.groupKey && r.material_id) {
        pendingChanges.deleteMaterial.delete(`${r.groupKey}_${r.material_id}`);
      }
    });
  } else {
    if (isLockedAny(row)) {
      showWarningSnackbar(`Нельзя удалить работу: на эту группу работ ${getLockedReason(row)}`);
      return;
    }
    if (isLockedByForeman(row)) {
      showWarningSnackbar(getLockReason(row));
      return;
    }
    if (hasOperFactWork(row)) {
      showWarningSnackbar('Запрещено удалять работу при наличии опер-факта');
      return;
    }
    if (pendingChanges.workSet.has(row.groupKey)) {
      showWarningSnackbar('Сначала отмените смену работы, потом удаляйте');
      return;
    }
    pendingChanges.deleteWork.set(row.groupKey, {
      work_set_id: row.work_set_id,
      work_set_check_group_id: row.work_set_check_group_id || null,
    });
    pendingChanges.qtyWork.delete(row.groupKey);
    data.value.forEach(r => {
      if (!r.isGroupHeader && r.groupKey === row.groupKey && r.material_id) {
        const key = `${r.groupKey}_${r.material_id}`;
        pendingChanges.deleteMaterial.set(key, {
          work_set_id: r.work_set_id,
          work_set_check_group_id: r.work_set_check_group_id || null,
          material_id: r.material_id,
        });
        pendingChanges.qtyMaterial.delete(key);
      }
    });
  }
  triggerPendingUpdate();
};

const revertWorkChange = (row) => {
  pendingChanges.workSet.delete(row.groupKey);
  triggerPendingUpdate();
};

const revertWorkQtyChange = (row) => {
  pendingChanges.qtyWork.delete(row.groupKey);
  data.value.forEach(r => {
    if (!r.isGroupHeader && r.groupKey === row.groupKey && r.material_id) {
      pendingChanges.qtyMaterial.delete(`${r.groupKey}_${r.material_id}`);
    }
  });
  triggerPendingUpdate();
};

const revertMaterialChange = (row) => {
  const key = `${row.groupKey}_${row.material_id}`;
  pendingChanges.material.delete(key);
  triggerPendingUpdate();
};

const revertMaterialQtyChange = (row) => {
  const key = `${row.groupKey}_${row.material_id}`;
  pendingChanges.qtyMaterial.delete(key);
  triggerPendingUpdate();
};

const toggleDeleteMaterial = (row) => {
  if (!isEditable.value) return;
  const key = `${row.groupKey}_${row.material_id}`;
  if (pendingChanges.deleteMaterial.has(key)) {
    pendingChanges.deleteMaterial.delete(key);
  } else {
    if (isLockedAny(row) && !isTeamProjectMaterial(row)) {
      showWarningSnackbar(`Нельзя удалить материал: на эту работу ${getLockedReason(row)}`);
      return;
    }
    if (isLockedByForeman(row)) {
      showWarningSnackbar(getLockReason(row));
      return;
    }
    if (hasOperFactMaterial(row)) {
      showWarningSnackbar('Запрещено удалять материал при наличии опер-факта');
      return;
    }
    if (pendingChanges.material.has(key)) {
      showWarningSnackbar('Сначала отмените смену материала, потом удаляйте');
      return;
    }
    pendingChanges.deleteMaterial.set(key, {
      work_set_id: row.work_set_id,
      work_set_check_group_id: row.work_set_check_group_id || null,
      material_id: row.material_id,
    });
    pendingChanges.qtyMaterial.delete(key);
  }
  triggerPendingUpdate();
};

// ─── multi-select обновление цен СМР + обновление цен ТМЦ ────────────────────
const smrSelectionMode = ref(false);
const smrScopeRows = ref([]);                  // список троек (floor, work_set, group) с флагами блокировок
const smrSelectedKeys = ref(new Set());        // выбранные пользователем (ключ: floor_map_id_work_set_id_group_id)
const smrApplying = ref(false);
const smrScopeLoading = ref(false);
const tmcLoading = ref(false);

const smrSelectionKey = (row) =>
  `${row.floor_map_id}_${row.work_set_id}_${row.work_set_check_group_id}`;

const smrScopeMap = computed(() => {
  const m = new Map();
  for (const r of smrScopeRows.value) {
    m.set(`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}`, r);
  }
  return m;
});

// Доступные для выбора строки скоупа: без опер-факта и без АВР.
// «Начатый процесс приёмки» (has_process) намеренно НЕ блокирует обновление цен —
// при необходимости снимок приёмки пересчитается. Скоуп сам по себе уже учитывает
// фильтры (подъезд/этаж/работы), т.к. приходит с бэка с теми же параметрами,
// что и таблица сметы.
const smrSelectableScopeRows = computed(() =>
  smrScopeRows.value.filter((r) => !r.has_oper_fact && !r.has_avr),
);

const isAllSmrSelected = computed(() => {
  const total = smrSelectableScopeRows.value.length;
  if (!total) return false;
  if (smrSelectedKeys.value.size < total) return false;
  return smrSelectableScopeRows.value.every((r) =>
    smrSelectedKeys.value.has(`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}`),
  );
});

const toggleSelectAllSmr = () => {
  if (isAllSmrSelected.value) {
    smrSelectedKeys.value = new Set();
    return;
  }
  const next = new Set();
  for (const r of smrSelectableScopeRows.value) {
    next.add(`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}`);
  }
  smrSelectedKeys.value = next;
};

const isSmrRowBlocked = (row) => {
  const r = smrScopeMap.value.get(smrSelectionKey(row));
  return !!(r && (r.has_oper_fact || r.has_avr));
};

const getSmrBlockReason = (row) => {
  const r = smrScopeMap.value.get(smrSelectionKey(row));
  if (!r) return '';
  if (r.has_oper_fact) return 'По работе уже есть опер-факт';
  if (r.has_avr)       return 'По работе есть АВР';
  return '';
};

const enterSmrSelectionMode = async () => {
  if (!props.projectId) return;
  if (hasPendingChanges.value) {
    showWarningSnackbar('Сначала сохраните или отмените текущие изменения');
    return;
  }
  smrScopeLoading.value = true;
  try {
    const rows = await getSmetaNewRefreshSmrScope(
      props.projectId,
      props.projectEntranceId || null,
      props.floorMapId || null,
      Array.isArray(props.workSetIds) && props.workSetIds.length ? props.workSetIds : null,
    );
    smrScopeRows.value = Array.isArray(rows) ? rows : [];
    smrSelectedKeys.value = new Set();
    smrSelectionMode.value = true;
  } catch (e) {
    console.error('refresh smr scope failed:', e);
    showErrorSnackbar('Не удалось получить список работ для обновления цен');
  } finally {
    smrScopeLoading.value = false;
  }
};

const exitSmrSelectionMode = () => {
  smrSelectionMode.value = false;
  smrScopeRows.value = [];
  smrSelectedKeys.value = new Set();
};

const onSmrCheckboxToggle = (row, val) => {
  const key = smrSelectionKey(row);
  const r = smrScopeMap.value.get(key);
  if (val) {
    if (r) {
      // приоритет ошибок: опер-факт → АВР (has_process намеренно не блокирует)
      if (r.has_oper_fact) { showWarningSnackbar('По работе уже есть опер-факт — обновление цены запрещено'); return; }
      if (r.has_avr)       { showWarningSnackbar('По работе есть АВР — обновление цены запрещено'); return; }
    }
  }
  const next = new Set(smrSelectedKeys.value);
  if (val) next.add(key); else next.delete(key);
  smrSelectedKeys.value = next;
};

/**
 * Применить накопленный multi-select: для каждой выбранной (floor, work_set, group)
 * параллельно дёрнуть `refresh_prices_preview` и смержить расхождения **только по СМР**
 * в pendingChanges. Цены материалов обновляются отдельно — через ТМЦ multi-select.
 */
const applySmrSelection = async () => {
  if (smrApplying.value) return;
  if (!smrSelectedKeys.value.size) return;
  smrApplying.value = true;

  const tasks = [...smrSelectedKeys.value].map((key) => {
    const r = smrScopeMap.value.get(key);
    if (!r) return Promise.resolve({ r: null, items: [] });
    return getSmetaNewRefreshPricesPreview({
      project_id: props.projectId,
      floor_map_id: r.floor_map_id,
      work_set_id: r.work_set_id,
      work_set_check_group_id: r.work_set_check_group_id,
    })
      .then((items) => ({ r, items: Array.isArray(items) ? items : [] }))
      .catch((err) => {
        console.error('refresh_prices_preview failed for', r, err);
        return { r, items: [], error: err };
      });
  });

  let results;
  try {
    results = await Promise.all(tasks);
  } catch (e) {
    console.error('applySmrSelection failed:', e);
    showErrorSnackbar('Не удалось обновить часть цен');
    smrApplying.value = false;
    return;
  }

  let cntWorks = 0;
  let cntSkippedOf = 0;       // пропущенных групп из-за опер-факта (страховка от рассинхронизации)
  let cntErrors = 0;

  for (const { r, items, error } of results) {
    if (!r) continue;
    if (error) { cntErrors += 1; continue; }
    if (!items.length) continue;

    const workItem = items.find((i) => i.kind === 'work') || null;
    if (!workItem) continue;

    if (workItem.has_oper_fact) { cntSkippedOf += 1; continue; }

    const groupRows = data.value.filter(
      (rr) => !rr.isGroupHeader
        && rr.floor_map_id === r.floor_map_id
        && rr.work_set_id === r.work_set_id
        && rr.work_set_check_group_id === r.work_set_check_group_id,
    );
    if (!groupRows.length) continue;
    const workRow = groupRows.find((rr) => rr.work_rowspan) || groupRows[0];
    const groupKey = workRow.groupKey;

    const workDiffers =
      !!(workItem.is_different && (
        moneyDiffers(workRow.wprc, workItem.new_price_with_nds) ||
        moneyDiffers(workRow.wprc_no_nds, workItem.new_price_no_nds)
      ));
    if (workDiffers) {
      pendingChanges.workSet.set(groupKey, {
        oldWorkSetId: workRow.work_set_id,
        newWorkSetId: workRow.work_set_id,
        newWorkSetName: workRow.wname,
        newUnitName: workRow.wunt,
        newWorkSetPrice: Number(workItem.new_price_with_nds),
        newWorkSetPriceNoNds: Number(workItem.new_price_no_nds),
      });
      cntWorks += 1;
    }
  }

  triggerPendingUpdate();
  smrApplying.value = false;
  exitSmrSelectionMode();

  if (cntWorks === 0) {
    if (cntErrors > 0) showErrorSnackbar(`Не удалось обновить ${cntErrors} групп`);
    else showSucceedSnackbar('Цены СМР актуальны');
    return;
  }
  let msg = `Обновлено цен СМР: ${cntWorks}. Нажмите «Сохранить»`;
  const tail = [];
  if (cntSkippedOf > 0) tail.push(`пропущено с опер-фактом: ${cntSkippedOf}`);
  if (cntErrors > 0) tail.push(`ошибок: ${cntErrors}`);
  if (tail.length) msg += ` (${tail.join(', ')})`;
  showSucceedSnackbar(msg);
};

// ─── multi-select обновление цен ТМЦ ─────────────────────────────────────────
// По UX устроено как и для СМР (см. выше): пользователь жмёт «Обновить цену ТМЦ»,
// фронт грузит скоуп материалов через `refresh_tmc_prices_preview`, отображает
// чек-боксы в столбце «Материал», есть «Выбрать все» по доступным (не заблокированным).
// «Применить» мерджит расхождения в pendingChanges.material — сохранение обычное.
const tmcSelectionMode = ref(false);
const tmcScopeRows = ref([]);
const tmcSelectedKeys = ref(new Set());
const tmcApplying = ref(false);

const tmcSelectionKey = (row) =>
  `${row.floor_map_id}_${row.work_set_id}_${row.work_set_check_group_id}_${row.material_id}`;

const tmcScopeMap = computed(() => {
  const m = new Map();
  for (const r of tmcScopeRows.value) {
    m.set(`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}_${r.material_id}`, r);
  }
  return m;
});

// Доступные для выбора строки скоупа: без АВР/опер-факта и с расхождением цен.
// «Начатый процесс приёмки» (has_process) намеренно НЕ блокирует обновление цен —
// is_blocked на бэке не включает has_process. Без расхождения нет смысла выбирать —
// пометка ничего не изменит.
const tmcSelectableScopeRows = computed(() =>
  tmcScopeRows.value.filter((r) => !r.is_blocked && r.is_different),
);

const isAllTmcSelected = computed(() => {
  const total = tmcSelectableScopeRows.value.length;
  if (!total) return false;
  if (tmcSelectedKeys.value.size < total) return false;
  return tmcSelectableScopeRows.value.every((r) =>
    tmcSelectedKeys.value.has(`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}_${r.material_id}`),
  );
});

const toggleSelectAllTmc = () => {
  if (isAllTmcSelected.value) {
    tmcSelectedKeys.value = new Set();
    return;
  }
  const next = new Set();
  for (const r of tmcSelectableScopeRows.value) {
    next.add(`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}_${r.material_id}`);
  }
  tmcSelectedKeys.value = next;
};

const isTmcRowBlocked = (row) => {
  const r = tmcScopeMap.value.get(tmcSelectionKey(row));
  return !!(r && r.is_blocked);
};
const isTmcRowSame = (row) => {
  const r = tmcScopeMap.value.get(tmcSelectionKey(row));
  return !!(r && !r.is_different && !r.is_blocked);
};
const getTmcBlockReason = (row) => {
  const r = tmcScopeMap.value.get(tmcSelectionKey(row));
  if (!r) return '';
  if (r.has_oper_fact) return 'Опер-факт по материалу ≥ кол-ва в смете — обновление цены запрещено';
  if (r.has_avr)       return 'По работе есть АВР';
  return '';
};

const enterTmcSelectionMode = async () => {
  if (!props.projectId) return;
  if (hasPendingChanges.value) {
    showWarningSnackbar('Сначала сохраните или отмените текущие изменения');
    return;
  }
  tmcLoading.value = true;
  try {
    const rows = await getSmetaNewRefreshTmcPricesPreview(
      props.projectId,
      props.projectEntranceId || null,
      props.floorMapId || null,
      Array.isArray(props.workSetIds) && props.workSetIds.length ? props.workSetIds : null,
    );
    tmcScopeRows.value = Array.isArray(rows) ? rows : [];
    tmcSelectedKeys.value = new Set();
    tmcSelectionMode.value = true;
  } catch (e) {
    console.error('refresh tmc scope failed:', e);
    showErrorSnackbar('Не удалось получить список ТМЦ для обновления цен');
  } finally {
    tmcLoading.value = false;
  }
};

const exitTmcSelectionMode = () => {
  tmcSelectionMode.value = false;
  tmcScopeRows.value = [];
  tmcSelectedKeys.value = new Set();
};

const onTmcCheckboxToggle = (row, val) => {
  const key = tmcSelectionKey(row);
  const r = tmcScopeMap.value.get(key);
  if (val) {
    if (!r) return;
    if (r.is_blocked) {
      if (r.has_oper_fact) { showWarningSnackbar('Опер-факт по материалу ≥ кол-ва в смете — обновление цены запрещено'); return; }
      if (r.has_avr)       { showWarningSnackbar('По работе есть АВР — обновление цены запрещено'); return; }
    }
    if (!r.is_different) {
      showWarningSnackbar('Цена ТМЦ актуальна — выбор не нужен');
      return;
    }
  }
  const next = new Set(tmcSelectedKeys.value);
  if (val) next.add(key); else next.delete(key);
  tmcSelectedKeys.value = next;
};

/**
 * Применить накопленный TMC multi-select: для каждой выбранной строки
 * `(floor, ws, group, material)` смержить новую цену в pendingChanges.material.
 * Тип закупки сохраняется как у строки (отображаемый, с учётом локальных правок).
 */
const applyTmcSelection = () => {
  if (tmcApplying.value) return;
  if (!tmcSelectedKeys.value.size) return;
  tmcApplying.value = true;

  let applied = 0;
  let skippedNoMatch = 0;
  let skippedTypeChanged = 0;

  for (const key of tmcSelectedKeys.value) {
    const m = tmcScopeMap.value.get(key);
    if (!m || m.is_blocked || !m.is_different) continue;

    const matchRows = data.value.filter((rr) =>
      !rr.isGroupHeader
      && rr.material_id === m.material_id
      && rr.work_set_id === m.work_set_id
      && rr.work_set_check_group_id === m.work_set_check_group_id
      && rr.floor_map_id === m.floor_map_id,
    );
    if (!matchRows.length) { skippedNoMatch += 1; continue; }

    for (const matRow of matchRows) {
      const currentPt =
        getDisplayPurchaseTypeId(matRow) ?? matRow.purchase_type_id ?? m.purchase_type_id;
      if (currentPt !== m.purchase_type_id) {
        skippedTypeChanged += 1;
        continue;
      }
      const matKey = `${matRow.groupKey}_${m.material_id}`;
      pendingChanges.material.set(matKey, {
        oldMaterialId: m.material_id,
        newMaterialId: m.material_id,
        newMaterialName: matRow.mname,
        newMaterialPrice: Number(m.new_price_with_nds),
        purchaseTypeId: currentPt,
      });
      applied += 1;
    }
  }

  triggerPendingUpdate();
  tmcApplying.value = false;
  exitTmcSelectionMode();

  if (applied === 0) {
    showSucceedSnackbar('Цены ТМЦ актуальны');
    return;
  }
  let msg = `Обновлено цен ТМЦ: ${applied}. Нажмите «Сохранить»`;
  const tail = [];
  if (skippedTypeChanged > 0) tail.push(`с изменённым типом закупки: ${skippedTypeChanged}`);
  if (skippedNoMatch > 0)     tail.push(`не найдено в таблице: ${skippedNoMatch}`);
  if (tail.length) msg += ` (${tail.join(', ')})`;
  showSucceedSnackbar(msg);
};

const getDisplayMaterialPriceWithoutNds = (row) => {
  pendingVersion.value;
  const key = `${row.groupKey}_${row.material_id}`;
  const change = pendingChanges.material.get(key);
  if (change && change.newMaterialPrice != null) {
    const nds = row.nds_percent_material || 0;
    return nds > 0
      ? Math.round(change.newMaterialPrice / (1 + nds / 100) * 100) / 100
      : change.newMaterialPrice;
  }
  return row.mprc_no_nds;
};
const getDisplayMaterialSumWithoutNds = (row) => {
  pendingVersion.value;
  if (row.material_id && isMaterialPendingDelete(row)) return 0;
  if (!isMaterialSumChanged(row)) return row.msum_no_nds;
  const price = getDisplayMaterialPriceWithoutNds(row);
  const qty = getDisplayMaterialQty(row);
  if (price == null || qty == null) return row.msum_no_nds;
  return Math.round(price * qty * 100) / 100;
};

// ─── helpers: суммы СМР с учётом pending ──────────────────────────────────────
const getDisplayWorkSum = (row) => {
  pendingVersion.value;
  if (isWorkPendingDelete(row)) return 0;
  if (!isWorkQtyChanged(row) && !hasWorkPending(row)) return row.wsum;
  const qty = getDisplayWorkQty(row);
  const price = getDisplayWorkPrice(row);
  if (qty == null || price == null) return row.wsum;
  return Math.round(price * qty * 100) / 100;
};
const getDisplayWorkSumWithoutNds = (row) => {
  pendingVersion.value;
  if (isWorkPendingDelete(row)) return 0;
  if (!isWorkQtyChanged(row) && !hasWorkPending(row)) return row.wsum_no_nds;
  const qty = getDisplayWorkQty(row);
  const price = getDisplayWorkPriceWithoutNds(row);
  if (qty == null || price == null) return row.wsum_no_nds;
  return Math.round(price * qty * 100) / 100;
};
const isWorkSumNdsDiffers = (row) => {
  pendingVersion.value;
  return moneyDiffers(row.wsum, getDisplayWorkSum(row));
};
const isWorkSumNoNdsDiffers = (row) => {
  pendingVersion.value;
  return moneyDiffers(row.wsum_no_nds, getDisplayWorkSumWithoutNds(row));
};
const isWorkPriceNdsDiffers = (row) => {
  pendingVersion.value;
  return moneyDiffers(row.wprc, getDisplayWorkPrice(row));
};
const isWorkPriceNoNdsDiffers = (row) => {
  pendingVersion.value;
  return moneyDiffers(row.wprc_no_nds, getDisplayWorkPriceWithoutNds(row));
};
const isMaterialPriceNdsDiffers = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  return moneyDiffers(row.mprc, getDisplayMaterialPrice(row));
};
const isMaterialPriceNoNdsDiffers = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  return moneyDiffers(row.mprc_no_nds, getDisplayMaterialPriceWithoutNds(row));
};
const isMaterialSumChanged = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  return isMaterialQtyChanged(row) || hasMaterialPending(row);
};
const isMaterialSumNdsDiffers = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  return moneyDiffers(row.msum, getDisplayMaterialSum(row));
};
const isMaterialSumNoNdsDiffers = (row) => {
  pendingVersion.value;
  if (!row.material_id) return false;
  return moneyDiffers(row.msum_no_nds, getDisplayMaterialSumWithoutNds(row));
};

// ─── helpers: итого с учётом pending ──────────────────────────────────────────
const groupMaterialRows = computed(() => {
  const map = new Map();
  data.value.forEach(r => {
    if (r.isGroupHeader || !r.material_id) return;
    if (!map.has(r.groupKey)) map.set(r.groupKey, []);
    map.get(r.groupKey).push(r);
  });
  return map;
});

const getPendingMaterialAdditionSumsForWorkRow = (row) => {
  pendingVersion.value;
  let withNds = 0;
  let noNds = 0;
  const wid = row.work_set_id;
  const gid = row.work_set_check_group_id ?? null;
  const fid = row.floor_map_id;
  for (const a of pendingChanges.materialAdditions) {
    if (a.work_set_id !== wid) continue;
    if ((a.work_set_check_group_id ?? null) !== gid) continue;
    if (a.floor_map_id !== fid) continue;
    const q = a.qty === '' || a.qty == null ? null : Number(String(a.qty).replace(',', '.'));
    if (q == null || Number.isNaN(q) || a.material_price == null) continue;
    withNds += Math.round(q * Number(a.material_price) * 100) / 100;
    const pnd = a.material_price_no_nds != null ? Number(a.material_price_no_nds) : 0;
    noNds += Math.round(q * pnd * 100) / 100;
  }
  return { withNds, noNds };
};

const getDisplayTotal = (row) => {
  pendingVersion.value;
  const wSum = getDisplayWorkSum(row);
  const rows = groupMaterialRows.value.get(row.groupKey) || [];
  let mSumAll = rows.reduce((s, r) => s + (getDisplayMaterialSum(r) || 0), 0);
  const add = getPendingMaterialAdditionSumsForWorkRow(row);
  return Math.round(((wSum || 0) + mSumAll + add.withNds) * 100) / 100;
};
const getDisplayTotalWithoutNds = (row) => {
  pendingVersion.value;
  const wSum = getDisplayWorkSumWithoutNds(row);
  const rows = groupMaterialRows.value.get(row.groupKey) || [];
  let mSumAll = rows.reduce((s, r) => s + (getDisplayMaterialSumWithoutNds(r) || 0), 0);
  const add = getPendingMaterialAdditionSumsForWorkRow(row);
  return Math.round(((wSum || 0) + mSumAll + add.noNds) * 100) / 100;
};
const isTotalNdsDiffers = (row) => {
  pendingVersion.value;
  if (!row.work_rowspan) return false;
  return moneyDiffers(row.ttl, getDisplayTotal(row));
};
const isTotalNoNdsDiffers = (row) => {
  pendingVersion.value;
  if (!row.work_rowspan) return false;
  return moneyDiffers(row.ttl_no_nds, getDisplayTotalWithoutNds(row));
};

const tableTitle = computed(() => 'Конструктив');

/** Итого с НДС / без НДС по строкам (с учётом несохранённых правок) */
const amount = computed(() => {
  pendingVersion.value;
  return Math.round(
    data.value.reduce((acc, r) => {
      if (r.isGroupHeader || !r.work_rowspan) return acc;
      if (isWorkQtyChanged(r) || hasWorkPending(r)) return acc + (getDisplayTotal(r) || 0);
      return acc + (r.ttl || 0);
    }, 0) * 100
  ) / 100;
});
const amountWithoutNds = computed(() => {
  pendingVersion.value;
  return Math.round(
    data.value.reduce((acc, r) => {
      if (r.isGroupHeader || !r.work_rowspan) return acc;
      if (isWorkQtyChanged(r) || hasWorkPending(r)) return acc + (getDisplayTotalWithoutNds(r) || 0);
      return acc + (r.ttl_no_nds || 0);
    }, 0) * 100
  ) / 100;
});

/** СМР без НДС: работы без НДС + ТМЦ без НДС от подряда (тип ≠ команда проекта) */
const totalWorkSumNoNds = computed(() => {
  pendingVersion.value;
  let acc = 0;
  data.value.forEach((r) => {
    if (r.isGroupHeader) return;
    if (r.work_rowspan) acc += getDisplayWorkSumWithoutNds(r) || 0;
    if (!r.material_id) return;
    const pt = getDisplayPurchaseTypeId(r);
    if (pt === PURCHASE_TYPES.TEAM_PROJECT) return;
    acc += getDisplayMaterialSumWithoutNds(r) || 0;
  });
  pendingChanges.workAdditions.forEach((wa) => {
    const q = toNum(wa.qty);
    if (q != null && wa.work_set_price_no_nds != null) {
      acc += Math.round(q * wa.work_set_price_no_nds * 100) / 100;
    }
  });
  pendingChanges.materialAdditions.forEach((ma) => {
    if (ma.purchase_type_id !== PURCHASE_TYPES.CONTRACTOR) return;
    const q = toNum(ma.qty);
    if (q == null || ma.material_price_no_nds == null) return;
    acc += Math.round(q * Number(ma.material_price_no_nds) * 100) / 100;
  });
  return Math.round(acc * 100) / 100;
});

/** ТМЦ без НДС: только материалы с типом закупки «команда проекта» */
const totalMaterialSumNoNds = computed(() => {
  pendingVersion.value;
  let acc = 0;
  data.value.forEach((r) => {
    if (r.isGroupHeader || !r.material_id) return;
    if (getDisplayPurchaseTypeId(r) !== PURCHASE_TYPES.TEAM_PROJECT) return;
    acc += getDisplayMaterialSumWithoutNds(r) || 0;
  });
  pendingChanges.materialAdditions.forEach((ma) => {
    if (ma.purchase_type_id !== PURCHASE_TYPES.TEAM_PROJECT) return;
    const q = toNum(ma.qty);
    if (q == null || ma.material_price_no_nds == null) return;
    acc += Math.round(q * Number(ma.material_price_no_nds) * 100) / 100;
  });
  return Math.round(acc * 100) / 100;
});

// ─── загрузка данных ──────────────────────────────────────────────────────────
const loadData = async () => {
  if (!props.projectId) { data.value = []; return; }
  isFetching.value = true;
  const workSetFilter =
    Array.isArray(props.workSetIds) && props.workSetIds.length > 0 ? props.workSetIds : null;
  const res = await getProjectSmetaNew(
    props.projectId,
    props.projectEntranceId,
    props.what,
    props.floorMapId,
    workSetFilter,
  );
  isFetching.value = false;
  const rows = res || [];
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].isGroupHeader) continue;
    const next = rows[i + 1];
    rows[i].isLastInWork = !next || next.isGroupHeader || next.groupKey !== rows[i].groupKey;
  }
  data.value = rows;
  renderedCount.value = CHUNK_SIZE;
};

const loadProjectState = async () => {
  if (!props.projectId) {
    projectState.value = { has_unsent_ds: false, unsent_ds_list: [], is_oper_fact_check_disabled: false };
    return;
  }
  projectState.value = await getProjectState(props.projectId);
};

// ─── Модалка: список АВР ─────────────────────────────────────────────────────
const avrListDialog = reactive({
  open: false,
  loading: false,
  workSetName: '',
  list: [],
});

const openAvrListDialog = async (row) => {
  avrListDialog.workSetName = row.wname || row.work_set_name || '';
  avrListDialog.list = [];
  avrListDialog.open = true;
  avrListDialog.loading = true;
  avrListDialog.list = await getSmetaNewAvrList({
    project_id: props.projectId,
    project_entrance_id: row.project_entrance_id,
    floor_map_id: row.floor_map_id,
    work_set_id: row.work_set_id,
    work_set_check_group_id: row.work_set_check_group_id || undefined,
  });
  avrListDialog.loading = false;
};

// ─── Модалка: Кол-во ──────────────────────────────────────────────────────────
const editQtyDialog = reactive({
  open: false,
  type: 'work',     // 'work' | 'material'
  row: null,
  value: null,
  label: '',
  currentQty: 0,
  hasAvr: false,
});

const openEditQtyDialog = (type, row) => {
  if (!isEditable.value) return;
  if (isLockedByForeman(row)) {
    showWarningSnackbar(getLockReason(row));
    return;
  }
  if (type === 'work' && pendingChanges.deleteWork.has(row.groupKey)) {
    showWarningSnackbar('Работа помечена на удаление');
    return;
  }
  if (type === 'material') {
    const matKey = `${row.groupKey}_${row.material_id}`;
    if (pendingChanges.deleteMaterial.has(matKey) || pendingChanges.deleteWork.has(row.groupKey)) {
      showWarningSnackbar('Материал или работа помечены на удаление');
      return;
    }
  }
  editQtyDialog.type = type;
  editQtyDialog.row = row;
  // Для ТМЦ от команды проекта (purchase_type_id=2) на АВР/КС2В работах
  // ограничения по уменьшению/доп-работам не применяются — цена=0, общая сумма
  // приёмки не меняется. Поэтому баннер и проверка снижения не активируются.
  editQtyDialog.hasAvr = hasAvr(row)
    && !(type === 'material' && isTeamProjectMaterial(row));
  if (type === 'work') {
    editQtyDialog.value = getDisplayWorkQty(row);
    editQtyDialog.currentQty = Number(getDisplayWorkQty(row) || 0);
    editQtyDialog.label = getDisplayWorkName(row);
  } else {
    editQtyDialog.value = getDisplayMaterialQty(row);
    editQtyDialog.currentQty = Number(getDisplayMaterialQty(row) || 0);
    editQtyDialog.label = getDisplayMaterialName(row);
  }
  editQtyDialog.open = true;
};

// Штучные материалы (unit_code='PIECE') — только целые значения qty.
// Округляем по обычной мат. логике (HALF_UP): 1.49→1, 1.5→2.
const isPieceMaterial = (row) => (row?.munc || '') === 'PIECE';
const roundPieceQty = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return v;
  return Math.sign(v) * Math.floor(Math.abs(v) + 0.5);
};

const confirmEditQty = () => {
  const val = parseFloat(editQtyDialog.value);
  if (isNaN(val) || val <= 0) return;
  const row = editQtyDialog.row;
  if (editQtyDialog.hasAvr && val < editQtyDialog.currentQty) {
    showWarningSnackbar('Нельзя уменьшить кол-во: на эту группу работ есть АВР или КС2В. Допустимо только увеличение.');
    return;
  }
  if (editQtyDialog.type === 'work') {
    if (hasOperFactWork(row) && row.oper_fact_work_qty != null) {
      const minQty = parseFloat(row.oper_fact_work_qty);
      if (val < minQty) {
        showWarningSnackbar(`Кол-во работы не может быть меньше опер-факта (${minQty})`);
        return;
      }
    }
    pendingChanges.qtyWork.set(row.groupKey, val);
    data.value.forEach(r => {
      if (!r.isGroupHeader && r.groupKey === row.groupKey && r.material_id && r.orig_wqty > 0) {
        const norm = r.mqty / r.orig_wqty;
        let newMqty = Math.round(norm * val * 10000) / 10000;
        // Для штучных материалов автоматически округляем до целого.
        if (isPieceMaterial(r)) {
          newMqty = roundPieceQty(newMqty);
        }
        if (hasOperFactMaterial(r) && r.oper_fact_material_qty != null) {
          const minMqty = parseFloat(r.oper_fact_material_qty);
          if (newMqty < minMqty) newMqty = minMqty;
        }
        pendingChanges.qtyMaterial.set(`${r.groupKey}_${r.material_id}`, newMqty);
      }
    });
  } else {
    if (isPieceMaterial(row) && val !== Math.trunc(val)) {
      showWarningSnackbar(`Кол-во штучного материала должно быть целым числом${row?.munt ? ` (${row.munt})` : ''}`);
      return;
    }
    if (hasOperFactMaterial(row) && row.oper_fact_material_qty != null) {
      const minQty = parseFloat(row.oper_fact_material_qty);
      if (val < minQty) {
        showWarningSnackbar(`Кол-во материала не может быть меньше опер-факта (${minQty})`);
        return;
      }
    }
    const key = `${row.groupKey}_${row.material_id}`;
    pendingChanges.qtyMaterial.set(key, val);
  }
  triggerPendingUpdate();
  editQtyDialog.open = false;
};

// ─── Модалка: Смена работы ────────────────────────────────────────────────────
const editWorkDialog = reactive({
  open: false,
  row: null,
  currentName: '',
  list: [],
  loading: false,
});

const openEditWorkDialog = async (row) => {
  if (!isEditable.value) return;
  if (!row.work_set_check_group_id) return;
  if (isLockedAny(row)) {
    showWarningSnackbar(`Нельзя изменить работу: на эту группу работ ${getLockedReason(row)}`);
    return;
  }
  if (isLockedByForeman(row)) {
    showWarningSnackbar(getLockReason(row));
    return;
  }
  if (hasOperFactWork(row)) {
    showWarningSnackbar('Запрещено менять работу при наличии опер-факта');
    return;
  }
  editWorkDialog.row = row;
  editWorkDialog.currentName = getDisplayWorkName(row);
  editWorkDialog.list = [];
  editWorkDialog.open = true;
  editWorkDialog.loading = true;
  editWorkDialog.list = await getSmetaNewWorkSetsByGroup(row.work_set_check_group_id, props.projectId);
  editWorkDialog.loading = false;
};

const confirmEditWork = (ws) => {
  const row = editWorkDialog.row;

  if (pendingChanges.deleteWork.has(row.groupKey)) {
    showWarningSnackbar('Работа помечена на удаление. Сначала отмените удаление.');
    editWorkDialog.open = false;
    return;
  }
  if (ws.work_set_id === row.work_set_id) {
    if (pendingChanges.workSet.has(row.groupKey)) {
      pendingChanges.workSet.delete(row.groupKey);
      triggerPendingUpdate();
    }
    editWorkDialog.open = false;
    return;
  }
  const checked = new Set();
  const duplicateInExisting = data.value.some(r => {
    if (r.isGroupHeader || r.groupKey === row.groupKey || checked.has(r.groupKey) ||
        r.floor_map_id !== row.floor_map_id ||
        r.work_set_check_group_id !== row.work_set_check_group_id) return false;
    checked.add(r.groupKey);
    const wsChange = pendingChanges.workSet.get(r.groupKey);
    return (wsChange ? wsChange.newWorkSetId : r.work_set_id) === ws.work_set_id;
  });
  const duplicateInAdditions = pendingChanges.workAdditions.some(a =>
    a.work_set_id === ws.work_set_id &&
    a.floor_map_id === row.floor_map_id &&
    a.work_set_check_group_id === row.work_set_check_group_id
  );
  if (duplicateInExisting || duplicateInAdditions) {
    showWarningSnackbar(`Эта работа уже существует: ${ws.work_set_name}`);
    return;
  }
  const price = Number(ws.work_set_price || 0);
  const ndsPercent = Number(ws.nds_percent || 0);
  const isIncludeNds = ws.is_include_nds ?? false;
  const priceNoNds = (isIncludeNds && ndsPercent > 0)
    ? price / (1 + ndsPercent / 100)
    : price;
  pendingChanges.workSet.set(row.groupKey, {
    newWorkSetId: ws.work_set_id,
    newWorkSetName: ws.work_set_name,
    newUnitName: ws.unit_name,
    oldWorkSetId: row.work_set_id,
    newWorkSetPrice: price,
    newWorkSetPriceNoNds: Math.round(priceNoNds * 100) / 100,
  });
  triggerPendingUpdate();
  editWorkDialog.open = false;
};

// ─── Модалка: Смена материала ─────────────────────────────────────────────────
const editMaterialDialog = reactive({
  open: false,
  row: null,
  currentName: '',
  search: '',
  results: [],
  loading: false,
});

const openEditMaterialDialog = (row) => {
  if (!isEditable.value) return;
  if (isLockedAny(row) && !isTeamProjectMaterial(row)) {
    showWarningSnackbar(`Нельзя изменить материал: на эту работу ${getLockedReason(row)}`);
    return;
  }
  if (isLockedByForeman(row)) {
    showWarningSnackbar(getLockReason(row));
    return;
  }
  if (hasOperFactMaterial(row)) {
    showWarningSnackbar('Запрещено менять материал при наличии опер-факта');
    return;
  }
  editMaterialDialog.row = row;
  editMaterialDialog.currentName = getDisplayMaterialName(row);
  editMaterialDialog.search = '';
  editMaterialDialog.results = [];
  editMaterialDialog.open = true;
};

const searchMaterials = async (val) => {
  if (!val || val.length < 2) { editMaterialDialog.results = []; return; }
  editMaterialDialog.loading = true;
  const res = await getSmetaNewMaterialSearch({
    material_name: val,
    project_id: props.projectId,
    floor_map_id: editMaterialDialog.row?.floor_map_id,
  });
  editMaterialDialog.results = res || [];
  editMaterialDialog.loading = false;
};

const confirmEditMaterial = (mat) => {
  const row = editMaterialDialog.row;
  const oldKey = `${row.groupKey}_${row.material_id}`;
  if (pendingChanges.deleteMaterial.has(oldKey)) {
    showWarningSnackbar('Материал помечен на удаление. Сначала отмените удаление.');
    editMaterialDialog.open = false;
    return;
  }
  if (mat.material_id === row.material_id) {
    if (pendingChanges.material.has(oldKey)) {
      pendingChanges.material.delete(oldKey);
      triggerPendingUpdate();
    }
    editMaterialDialog.open = false;
    return;
  }
  const currentMaterialId = pendingChanges.material.has(oldKey)
    ? pendingChanges.material.get(oldKey).newMaterialId
    : row.material_id;
  if (mat.material_id === currentMaterialId) {
    editMaterialDialog.open = false;
    return;
  }
  const duplicateInExisting = data.value.some(r => {
    if (r.isGroupHeader || !r.material_id ||
        r.floor_map_id !== row.floor_map_id ||
        r.work_set_id !== row.work_set_id ||
        r.work_set_check_group_id !== row.work_set_check_group_id ||
        r.material_id === row.material_id) return false;
    const matKey = `${r.groupKey}_${r.material_id}`;
    const matChange = pendingChanges.material.get(matKey);
    return (matChange ? matChange.newMaterialId : r.material_id) === mat.material_id;
  });
  const duplicateInAdditions = pendingChanges.materialAdditions.some(a =>
    a.material_id === mat.material_id &&
    a.floor_map_id === row.floor_map_id &&
    a.work_set_id === row.work_set_id &&
    a.work_set_check_group_id === (row.work_set_check_group_id || null)
  );
  if (duplicateInExisting || duplicateInAdditions) {
    showWarningSnackbar(`Этот материал уже привязан к данной работе: ${mat.material_name}`);
    return;
  }
  pendingChanges.material.set(oldKey, {
    newMaterialId: mat.material_id,
    newMaterialName: mat.material_name,
    newMaterialPrice: mat.price ?? null,
    oldMaterialName: row.mname,
    oldMaterialId: row.material_id,
    purchaseTypeId: getDisplayPurchaseTypeId(row) || row.purchase_type_id || mat.purchase_type_id || 1,
  });
  triggerPendingUpdate();
  editMaterialDialog.open = false;
};

// ─── Модалка: Добавление СМР ──────────────────────────────────────────────────
const addWorkDialog = reactive({
  open: false,
  row: null,
  groupName: '',
  list: [],
  loading: false,
  willCreateAddwork: false,
});

const openAddWorkDialog = async (row) => {
  if (!row.work_set_check_group_id) return;
  if (isLockedByForeman(row)) {
    showWarningSnackbar(getLockReason(row));
    return;
  }
  addWorkDialog.row = row;
  addWorkDialog.groupName = row.work_set_check_group_name || 'Без группы';
  addWorkDialog.list = [];
  addWorkDialog.willCreateAddwork = hasAvr(row) || hasProcess(row);
  addWorkDialog.open = true;
  addWorkDialog.loading = true;
  addWorkDialog.list = await getSmetaNewWorkSetsByGroup(row.work_set_check_group_id, props.projectId);
  addWorkDialog.loading = false;
};

const confirmAddWork = (ws) => {
  const row = addWorkDialog.row;

  const checked = new Set();
  const existsInData = data.value.some(r => {
    if (r.isGroupHeader || checked.has(r.groupKey) ||
        r.floor_map_id !== row.floor_map_id ||
        r.work_set_check_group_id !== row.work_set_check_group_id) return false;
    checked.add(r.groupKey);
    const wsChange = pendingChanges.workSet.get(r.groupKey);
    return (wsChange ? wsChange.newWorkSetId : r.work_set_id) === ws.work_set_id;
  });
  const existsInPending = pendingChanges.workAdditions.some(a =>
    a.floor_map_id === row.floor_map_id &&
    a.work_set_check_group_id === row.work_set_check_group_id &&
    a.work_set_id === ws.work_set_id
  );
  if (existsInData || existsInPending) {
    showWarningSnackbar(`Эта работа уже добавлена: ${ws.work_set_name}`);
    return;
  }

  const price = Number(ws.work_set_price || 0);
  const ndsPercent = Number(ws.nds_percent || 0);
  const isIncludeNds = ws.is_include_nds ?? false;
  const priceNoNds = (isIncludeNds && ndsPercent > 0)
    ? price / (1 + ndsPercent / 100)
    : price;
  pendingChanges.workAdditions.push({
    work_set_id: ws.work_set_id,
    work_set_check_group_id: row.work_set_check_group_id,
    work_set_name: ws.work_set_name,
    unit_name: ws.unit_name || '',
    work_set_price: price,
    work_set_price_no_nds: priceNoNds,
    is_include_nds: isIncludeNds,
    nds_percent: ndsPercent,
    floor_map_id: row.floor_map_id,
    qty: null,
  });
  triggerPendingUpdate();
  addWorkDialog.open = false;
};

// ─── Модалка: Добавление группы работ ────────────────────────────────────────
const addGroupDialog = reactive({
  open: false,
  groupsLoading: false,
  availableGroups: [],
  floorMapId: null,
  search: '',
});

const filteredAvailableGroups = computed(() => {
  const q = (addGroupDialog.search || '').trim().toLowerCase();
  if (!q) return addGroupDialog.availableGroups;
  return addGroupDialog.availableGroups.filter(g =>
    String(g.work_set_check_group_name || '').toLowerCase().includes(q)
  );
});

const openAddGroupDialog = async (floorMapId) => {
  addGroupDialog.floorMapId = floorMapId;
  addGroupDialog.availableGroups = [];
  addGroupDialog.search = '';
  addGroupDialog.open = true;
  addGroupDialog.groupsLoading = true;
  addGroupDialog.availableGroups = await getSmetaNewAvailableGroups(props.projectId, addGroupDialog.floorMapId);
  addGroupDialog.groupsLoading = false;
};

const selectGroupForAddGroup = (group) => {
  const exists = pendingChanges.newGroups.some(
    g => g.work_set_check_group_id === group.work_set_check_group_id && g.floor_map_id === addGroupDialog.floorMapId
  );
  if (!exists) {
    pendingChanges.newGroups.push({
      work_set_check_group_id: group.work_set_check_group_id,
      work_set_check_group_name: group.work_set_check_group_name,
      floor_map_id: addGroupDialog.floorMapId,
    });
    triggerPendingUpdate();
  }
  addGroupDialog.open = false;
};

const closeAddGroupDialog = () => {
  addGroupDialog.open = false;
};

const removeNewGroup = (row) => {
  const gid = row.work_set_check_group_id;
  const fid = row.floor_map_id;
  const idxG = pendingChanges.newGroups.findIndex(
    g => g.work_set_check_group_id === gid && g.floor_map_id === fid
  );
  if (idxG !== -1) pendingChanges.newGroups.splice(idxG, 1);
  for (let i = pendingChanges.workAdditions.length - 1; i >= 0; i--) {
    const wa = pendingChanges.workAdditions[i];
    if (wa.work_set_check_group_id === gid && wa.floor_map_id === fid) {
      pendingChanges.workAdditions.splice(i, 1);
    }
  }
  for (let i = pendingChanges.materialAdditions.length - 1; i >= 0; i--) {
    const ma = pendingChanges.materialAdditions[i];
    if (ma.work_set_check_group_id === gid && ma.floor_map_id === fid) {
      pendingChanges.materialAdditions.splice(i, 1);
    }
  }
  triggerPendingUpdate();
};

// ─── Модалка: Добавление ТМЦ ──────────────────────────────────────────────────
// Доступно на любых работах, в т.ч. с подписанным АВР/КС2В. На АВР/КС2В-работах
// бэкенд (public.smeta_new__save_changes) разрешает только purchase_type_id=2
// (Команда проекта); type=1 будет отклонён. Поэтому на таких работах фронт
// форсит покупательский тип на «Команда проекта» (isAvrLocked=true → тэг
// «Подряд» в pending-строке заблокирован существующей иконкой-замком).
// material_price на pending-строке оставляем как обычно (реальная цена прайса);
// на бэке для type=2 цена обнулится автоматически — общая сумма вызова приёмки
// при этом не изменится (см. wcheck.smeta__cgc_sync_after_smeta_change).
const addMaterialDialog = reactive({
  open: false,
  row: null,
  workName: '',
  search: '',
  results: [],
  loading: false,
  forceTeamProject: false,
});

const openAddMaterialDialog = (row) => {
  if (isLockedByForeman(row)) {
    showWarningSnackbar(getLockReason(row));
    return;
  }
  addMaterialDialog.row = row;
  addMaterialDialog.workName = row.wname || row.work_set_name || '';
  addMaterialDialog.search = '';
  addMaterialDialog.results = [];
  addMaterialDialog.forceTeamProject = hasAvr(row);
  addMaterialDialog.open = true;
};

const openAddMaterialForPendingWork = (wa) => {
  openAddMaterialDialog({
    work_set_id: wa.work_set_id,
    work_set_check_group_id: wa.work_set_check_group_id,
    work_set_name: wa.work_set_name,
    wname: wa.work_set_name,
    floor_map_id: wa.floor_map_id,
    orig_wqty: wa.qty || 0,
    wqty: wa.qty || 0,
  });
};

const searchAddMaterials = async (val) => {
  if (!val || val.length < 2) { addMaterialDialog.results = []; return; }
  addMaterialDialog.loading = true;
  const res = await getSmetaNewMaterialSearch({
    material_name: val,
    project_id: props.projectId,
    floor_map_id: addMaterialDialog.row?.floor_map_id,
  });
  addMaterialDialog.results = res || [];
  addMaterialDialog.loading = false;
};

const confirmAddMaterial = async (mat) => {
  const row = addMaterialDialog.row;
  const duplicateInData = data.value.some(r => {
    if (r.isGroupHeader || !r.material_id ||
        r.floor_map_id !== row.floor_map_id ||
        r.work_set_id !== row.work_set_id ||
        r.work_set_check_group_id !== row.work_set_check_group_id) return false;
    const matKey = `${r.groupKey}_${r.material_id}`;
    const matChange = pendingChanges.material.get(matKey);
    return (matChange ? matChange.newMaterialId : r.material_id) === mat.material_id;
  });
  const duplicateInAdditions = pendingChanges.materialAdditions.some(a =>
    a.material_id === mat.material_id &&
    a.work_set_id === row.work_set_id &&
    a.work_set_check_group_id === (row.work_set_check_group_id || null) &&
    a.floor_map_id === row.floor_map_id
  );
  if (duplicateInData || duplicateInAdditions) {
    showWarningSnackbar(`Нельзя добавить один и тот же материал дважды: ${mat.material_name}`);
    return;
  }

  if (cachedNdsPercent.value == null) {
    cachedNdsPercent.value = await getSmetaNewNdsPercent();
  }
  const nds = cachedNdsPercent.value || 0;
  const price = Number(mat.price || 0);
  const priceNoNds = (nds > 0) ? price / (1 + nds / 100) : price;
  let workQty = row.orig_wqty || row.wqty || 0;
  if (row.groupKey && pendingChanges.qtyWork.has(row.groupKey)) {
    workQty = pendingChanges.qtyWork.get(row.groupKey);
  }
  const forceTeam = addMaterialDialog.forceTeamProject;
  const purchaseTypeId = forceTeam
    ? PURCHASE_TYPES.TEAM_PROJECT
    : (Number(mat.purchase_type_id) || PURCHASE_TYPES.CONTRACTOR);
  pendingChanges.materialAdditions.push({
    work_set_id: row.work_set_id,
    work_set_check_group_id: row.work_set_check_group_id || null,
    material_id: mat.material_id,
    material_name: mat.material_name,
    material_unit_name: mat.material_unit_name || '',
    material_unit_code: mat.material_unit_code || '',
    material_price: price,
    material_price_no_nds: priceNoNds,
    floor_map_id: row.floor_map_id,
    qty: null,
    work_qty: workQty,
    purchase_type_id: purchaseTypeId,
    isAvrLocked: forceTeam,
    willCreateAddwork: false,
  });
  triggerPendingUpdate();
  addMaterialDialog.open = false;
};

// ─── Helpers: визуальное отображение pending additions ─────────────────────────
const workAdditionsByGroup = computed(() => {
  pendingVersion.value;
  const map = new Map();
  pendingChanges.workAdditions.forEach((a, idx) => {
    const key = `${a.work_set_check_group_id}_${a.floor_map_id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ ...a, _idx: idx });
  });
  return map;
});

const materialAdditionsByWork = computed(() => {
  pendingVersion.value;
  const map = new Map();
  pendingChanges.materialAdditions.forEach((a, idx) => {
    const key = `${a.work_set_id}_${a.work_set_check_group_id}_${a.floor_map_id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ ...a, _idx: idx });
  });
  return map;
});

const getWorkAdditionsForGroup = (groupId, floorMapId) => {
  return workAdditionsByGroup.value.get(`${groupId}_${floorMapId}`) || [];
};

const getMaterialAdditionsForWork = (workSetId, groupId, floorMapId) => {
  return materialAdditionsByWork.value.get(`${workSetId}_${groupId}_${floorMapId}`) || [];
};

const getEffectiveRowspan = (row) => {
  if (!row.work_rowspan) return 0;
  const additions = materialAdditionsByWork.value.get(
    `${row.work_set_id}_${row.work_set_check_group_id}_${row.floor_map_id}`
  );
  return row.work_rowspan + (additions ? additions.length : 0);
};

const getPendingWorkRows = (wa) => {
  const materials = getMaterialAdditionsForWork(wa.work_set_id, wa.work_set_check_group_id, wa.floor_map_id);
  const totalRows = Math.max(1, materials.length);
  if (materials.length === 0) {
    return [{ wa, ma: null, isFirst: true, totalRows }];
  }
  return materials.map((ma, idx) => ({
    wa,
    ma,
    isFirst: idx === 0,
    totalRows,
  }));
};

const removeWorkAddition = (idx) => {
  const wa = pendingChanges.workAdditions[idx];
  for (let i = pendingChanges.materialAdditions.length - 1; i >= 0; i--) {
    const ma = pendingChanges.materialAdditions[i];
    if (ma.work_set_id === wa.work_set_id &&
        ma.work_set_check_group_id === wa.work_set_check_group_id &&
        ma.floor_map_id === wa.floor_map_id) {
      pendingChanges.materialAdditions.splice(i, 1);
    }
  }
  pendingChanges.workAdditions.splice(idx, 1);
  triggerPendingUpdate();
};

const sanitizeQty = (val) => {
  if (val === '' || val == null) return null;
  let str = String(val).replace(/[^0-9.,]/g, '').replace(',', '.');
  if (!str) return null;
  const dotIdx = str.indexOf('.');
  if (dotIdx !== -1) {
    str = str.slice(0, dotIdx + 1) + str.slice(dotIdx + 1).replace(/\./g, '').slice(0, 6);
  }
  return str;
};

// Для штучных материалов не пускаем дробные: дробная часть и точка отбрасываются.
const sanitizeQtyInteger = (val) => {
  if (val === '' || val == null) return null;
  const str = String(val).replace(/[^0-9]/g, '');
  return str || null;
};

const toNum = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

/** Суммы ТМЦ по pending-добавлению СМР (для колонки «Итого») */
const getPendingWaMaterialMoneySums = (wa) => {
  pendingVersion.value;
  const materials = getMaterialAdditionsForWork(wa.work_set_id, wa.work_set_check_group_id, wa.floor_map_id);
  let withNds = 0;
  let noNds = 0;
  for (const ma of materials) {
    const q = toNum(ma.qty);
    if (q == null || ma.material_price == null) continue;
    withNds += Math.round(q * Number(ma.material_price) * 100) / 100;
    const pnd = ma.material_price_no_nds != null ? Number(ma.material_price_no_nds) : 0;
    noNds += Math.round(q * pnd * 100) / 100;
  }
  return { withNds, noNds };
};
const getPendingWorkAdditionTotalWithNds = (wa) => {
  pendingVersion.value;
  let w = 0;
  const q = toNum(wa.qty);
  if (q != null && wa.work_set_price != null) {
    w = Math.round(q * wa.work_set_price * 100) / 100;
  }
  const { withNds } = getPendingWaMaterialMoneySums(wa);
  return Math.round((w + withNds) * 100) / 100;
};
const getPendingWorkAdditionTotalNoNds = (wa) => {
  pendingVersion.value;
  let w = 0;
  const q = toNum(wa.qty);
  if (q != null && wa.work_set_price_no_nds != null) {
    w = Math.round(q * wa.work_set_price_no_nds * 100) / 100;
  }
  const { noNds } = getPendingWaMaterialMoneySums(wa);
  return Math.round((w + noNds) * 100) / 100;
};
const hasPendingWorkAdditionTotalDisplay = (wa) => {
  pendingVersion.value;
  const q = toNum(wa.qty);
  const wOk = q != null && wa.work_set_price != null;
  const { withNds, noNds } = getPendingWaMaterialMoneySums(wa);
  if (withNds !== 0 || noNds !== 0) return true;
  return !!wOk;
};

const updateWorkAdditionQty = (idx, val) => {
  pendingChanges.workAdditions[idx].qty = sanitizeQty(val);
  triggerPendingUpdate();
};

const updateMaterialAdditionQty = (idx, val) => {
  const ma = pendingChanges.materialAdditions[idx];
  ma.qty = (ma?.material_unit_code === 'PIECE')
    ? sanitizeQtyInteger(val)
    : sanitizeQty(val);
  triggerPendingUpdate();
};

const removeMaterialAddition = (idx) => {
  pendingChanges.materialAdditions.splice(idx, 1);
  triggerPendingUpdate();
};

const setPendingMaPurchaseType = (idx, typeId) => {
  if (!isEditable.value) return;
  const ma = pendingChanges.materialAdditions[idx];
  if (!ma) return;
  ma.purchase_type_id = typeId;
  triggerPendingUpdate();
};

// ─── Модалка: Скопировать на этажи (СМР / ТМЦ) ────────────────────────────────
//
// Идея: сборка snapshot источника + загрузка списка этажей с сервера (бэк
// возвращает блокировки has_avr (по work_set_id на этаже), has_process/has_oper_fact
// (по группе) + already_exists,
// для material-mode дополнительно work_missing/material_already_exists).
// Заблокированные этажи в UI скрываем целиком. По «Применить» пушим N×записей
// в pendingChanges.workAdditions / materialAdditions, дальше юзер сохраняет
// обычным «Сохранить изменения» (один общий save_changes на все этажи).
//
// Цены:
//  - source='saved'   → передаём «как есть» (forced); SP их не пересчитывает.
//  - source='pending' → не передаём; SP подтянет сегодняшний прайс (стандартное
//                       поведение «Добавить СМР/ТМЦ»).
const copyToFloorsDialog = reactive({
  open: false,
  mode: null,         // 'work' | 'material'
  source: null,       // 'saved' | 'pending'
  loading: false,
  applying: false,
  snapshot: null,     // объект из getWorkCopySnapshot / getMaterialCopySnapshot
  floors: [],         // ответ public.smeta_new__floors_for_copy (включая заблокированные)
  selectedFloorMapIds: new Set(),
});

const isCopyToFloorsHidden = computed(() =>
  smrSelectionMode.value || tmcSelectionMode.value
);

/** Цена СМР без НДС: из row.wprc_no_nds, иначе считаем сами. */
const computeWorkPriceNoNds = (price, isIncludeNds, ndsPercent) => {
  const p = Number(price) || 0;
  if (!isIncludeNds || !ndsPercent) return p;
  return Math.round((p / (1 + Number(ndsPercent) / 100)) * 100) / 100;
};

/** Цена ТМЦ без НДС из row.mprc_no_nds, иначе считаем сами. */
const computeMaterialPriceNoNds = (price, isIncludeNds, ndsPercent) => {
  const p = Number(price) || 0;
  if (!isIncludeNds || !ndsPercent) return p;
  return Math.round((p / (1 + Number(ndsPercent) / 100)) * 100) / 100;
};

/**
 * Snapshot источника копирования работы.
 * Принимает либо row из data.value (источник = saved), либо pending workAddition
 * (по флагу __isPendingAddition). Возвращает работу + связанные материалы
 * (для saved — собирает все строки группы; для pending — соответствующие
 * materialAdditions из pendingChanges).
 */
const getWorkCopySnapshot = (rowOrWa) => {
  pendingVersion.value;

  if (rowOrWa && rowOrWa.__isPendingAddition) {
    const wa = rowOrWa;
    const materials = getMaterialAdditionsForWork(
      wa.work_set_id, wa.work_set_check_group_id, wa.floor_map_id,
    ).map(m => ({
      material_id: m.material_id,
      material_name: m.material_name,
      material_unit_name: m.material_unit_name || '',
      qty: Number(m.qty) || 0,
      material_price: m.material_price != null ? Number(m.material_price) : null,
      material_price_no_nds: m.material_price_no_nds != null ? Number(m.material_price_no_nds) : null,
      purchase_type_id: m.purchase_type_id || PURCHASE_TYPES.CONTRACTOR,
    }));
    return {
      source: 'pending',
      work_set_id: wa.work_set_id,
      work_set_check_group_id: wa.work_set_check_group_id,
      work_set_check_group_name: wa.work_set_check_group_name || '',
      work_set_name: wa.work_set_name,
      unit_name: wa.unit_name || '',
      qty: Number(wa.qty) || 0,
      work_set_price: wa.work_set_price,
      work_set_price_no_nds: wa.work_set_price_no_nds,
      is_include_nds: null,
      nds_percent: null,
      source_floor_map_id: wa.floor_map_id || null,
      materials,
    };
  }

  const row = rowOrWa;
  const groupKey = row.groupKey;
  const wsChange = pendingChanges.workSet.get(groupKey);
  const workSetId = wsChange ? wsChange.newWorkSetId : row.work_set_id;
  const workSetName = getDisplayWorkName(row);
  const unitName = getDisplayUnitName(row);
  const qty = Number(getDisplayWorkQty(row)) || 0;
  const wPrc = Number(getDisplayWorkPrice(row));
  const wPrcNoNds = Number(getDisplayWorkPriceWithoutNds(row));
  const isIncludeNdsW = !!row.is_incloude_nds_work_set;
  const ndsPercentW = row.nds_percent_work_set != null ? Number(row.nds_percent_work_set) : null;

  const materials = [];
  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (r.groupKey !== groupKey) continue;
    if (!r.material_id) continue;
    if (isMaterialPendingDelete(r)) continue;
    const matKey = `${r.groupKey}_${r.material_id}`;
    const matChange = pendingChanges.material.get(matKey);
    const materialId = matChange ? matChange.newMaterialId : r.material_id;
    const materialName = matChange ? matChange.newMaterialName : r.mname;
    const purchaseTypeId = getDisplayPurchaseTypeId(r) || PURCHASE_TYPES.CONTRACTOR;
    const matPrc = Number(getDisplayMaterialPrice(r));
    const matPrcNoNds = Number(getDisplayMaterialPriceWithoutNds(r));
    const matQty = Number(getDisplayMaterialQty(r)) || 0;
    materials.push({
      material_id: materialId,
      material_name: materialName,
      material_unit_name: r.munt || '',
      qty: matQty,
      // Для what='general' (режим редактирования сметы) row.mprc = material_price_origin
      material_price: matPrc,
      material_price_origin: matPrc,
      material_price_no_nds: matPrcNoNds,
      is_include_nds: !!r.is_incloude_nds_material,
      nds_percent: r.nds_percent_material != null ? Number(r.nds_percent_material) : null,
      purchase_type_id: purchaseTypeId,
    });
  }

  return {
    source: 'saved',
    work_set_id: workSetId,
    work_set_check_group_id: row.work_set_check_group_id,
    work_set_check_group_name: row.work_set_check_group_name || '',
    work_set_name: workSetName,
    unit_name: unitName,
    qty,
    work_set_price: wPrc,
    work_set_price_no_nds: wPrcNoNds,
    is_include_nds: isIncludeNdsW,
    nds_percent: ndsPercentW,
    source_floor_map_id: row.floor_map_id || null,
    materials,
  };
};

/**
 * Snapshot одного материала в работе. Источник — либо строка таблицы (saved),
 * либо pending materialAddition (по флагу __isPendingAddition).
 */
const getMaterialCopySnapshot = (rowOrMa) => {
  pendingVersion.value;

  if (rowOrMa && rowOrMa.__isPendingAddition) {
    const ma = rowOrMa;
    return {
      source: 'pending',
      work_set_id: ma.work_set_id,
      work_set_check_group_id: ma.work_set_check_group_id,
      material_id: ma.material_id,
      material_name: ma.material_name,
      material_unit_name: ma.material_unit_name || '',
      qty: Number(ma.qty) || 0,
      material_price: ma.material_price != null ? Number(ma.material_price) : null,
      material_price_origin: null,
      material_price_no_nds: ma.material_price_no_nds != null ? Number(ma.material_price_no_nds) : null,
      is_include_nds: null,
      nds_percent: null,
      purchase_type_id: ma.purchase_type_id || PURCHASE_TYPES.CONTRACTOR,
      source_floor_map_id: ma.floor_map_id || null,
    };
  }

  const row = rowOrMa;
  const groupKey = row.groupKey;
  const matKey = `${groupKey}_${row.material_id}`;
  const wsChange = pendingChanges.workSet.get(groupKey);
  const matChange = pendingChanges.material.get(matKey);
  const workSetId = wsChange ? wsChange.newWorkSetId : row.work_set_id;
  const materialId = matChange ? matChange.newMaterialId : row.material_id;
  const materialName = matChange ? matChange.newMaterialName : row.mname;
  const purchaseTypeId = getDisplayPurchaseTypeId(row) || PURCHASE_TYPES.CONTRACTOR;
  const mPrc = Number(getDisplayMaterialPrice(row));
  const mPrcNoNds = Number(getDisplayMaterialPriceWithoutNds(row));
  const mQty = Number(getDisplayMaterialQty(row)) || 0;

  return {
    source: 'saved',
    work_set_id: workSetId,
    work_set_check_group_id: row.work_set_check_group_id,
    material_id: materialId,
    material_name: materialName,
    material_unit_name: row.munt || '',
    qty: mQty,
    material_price: mPrc,
    material_price_origin: mPrc,
    material_price_no_nds: mPrcNoNds,
    is_include_nds: !!row.is_incloude_nds_material,
    nds_percent: row.nds_percent_material != null ? Number(row.nds_percent_material) : null,
    purchase_type_id: purchaseTypeId,
    source_floor_map_id: row.floor_map_id || null,
    work_set_name: row.wname || '',
  };
};

const _resetCopyDialog = () => {
  copyToFloorsDialog.mode = null;
  copyToFloorsDialog.source = null;
  copyToFloorsDialog.snapshot = null;
  copyToFloorsDialog.floors = [];
  copyToFloorsDialog.selectedFloorMapIds = new Set();
  copyToFloorsDialog.loading = false;
  copyToFloorsDialog.applying = false;
};

const closeCopyToFloorsDialog = () => {
  copyToFloorsDialog.open = false;
  _resetCopyDialog();
};

/** Открыть «Скопировать работу на этажи». rowOrWa — строка СМР или pending wa. */
const openCopyWorkToFloorsDialog = async (rowOrWa) => {
  if (!isEditable.value || isCopyToFloorsHidden.value) return;
  const snapshot = getWorkCopySnapshot(rowOrWa);
  if (!snapshot.work_set_id || !snapshot.work_set_check_group_id) {
    showWarningSnackbar('Нет данных по работе для копирования');
    return;
  }
  if (!snapshot.qty || snapshot.qty <= 0) {
    showWarningSnackbar('Укажите кол-во работы у источника, прежде чем копировать');
    return;
  }
  _resetCopyDialog();
  copyToFloorsDialog.mode = 'work';
  copyToFloorsDialog.source = snapshot.source;
  copyToFloorsDialog.snapshot = snapshot;
  copyToFloorsDialog.open = true;
  copyToFloorsDialog.loading = true;
  copyToFloorsDialog.floors = await getSmetaNewFloorsForCopy(
    props.projectId,
    snapshot.source_floor_map_id,
    snapshot.work_set_id,
    snapshot.work_set_check_group_id,
    null,
  );
  copyToFloorsDialog.loading = false;
};

/** Открыть «Скопировать материал в такие же работы на этажи». */
const openCopyMaterialToFloorsDialog = async (rowOrMa) => {
  if (!isEditable.value || isCopyToFloorsHidden.value) return;
  const snapshot = getMaterialCopySnapshot(rowOrMa);
  if (!snapshot.material_id || !snapshot.work_set_id || !snapshot.work_set_check_group_id) {
    showWarningSnackbar('Нет данных по материалу для копирования');
    return;
  }
  if (!snapshot.qty || snapshot.qty <= 0) {
    showWarningSnackbar('Укажите кол-во материала у источника, прежде чем копировать');
    return;
  }
  _resetCopyDialog();
  copyToFloorsDialog.mode = 'material';
  copyToFloorsDialog.source = snapshot.source;
  copyToFloorsDialog.snapshot = snapshot;
  copyToFloorsDialog.open = true;
  copyToFloorsDialog.loading = true;
  copyToFloorsDialog.floors = await getSmetaNewFloorsForCopy(
    props.projectId,
    snapshot.source_floor_map_id,
    snapshot.work_set_id,
    snapshot.work_set_check_group_id,
    snapshot.material_id,
    snapshot.purchase_type_id,
  );
  copyToFloorsDialog.loading = false;
};

const copyMaterialPickDialog = reactive({
  open: false,
  mode: null,
  items: [],
});

const collectSavedMaterialRowsForCopy = (workRow) => {
  pendingVersion.value;
  const groupKey = workRow.groupKey;
  const out = [];
  for (const r of data.value) {
    if (r.isGroupHeader) continue;
    if (r.groupKey !== groupKey) continue;
    if (!r.material_id) continue;
    if (isMaterialPendingDelete(r)) continue;
    out.push(r);
  }
  return out;
};

const closeCopyMaterialPickDialog = () => {
  copyMaterialPickDialog.open = false;
  copyMaterialPickDialog.mode = null;
  copyMaterialPickDialog.items = [];
};

const openCopyTmcToFloorsFromSavedWork = (workRow) => {
  if (!isEditable.value || isCopyToFloorsHidden.value) return;
  if (isLockedByForeman(workRow)) return;
  const mats = collectSavedMaterialRowsForCopy(workRow);
  if (!mats.length) {
    showWarningSnackbar('Нет ТМЦ для копирования');
    return;
  }
  if (mats.length === 1) {
    openCopyMaterialToFloorsDialog(mats[0]);
    return;
  }
  copyMaterialPickDialog.mode = 'saved';
  copyMaterialPickDialog.items = mats;
  copyMaterialPickDialog.open = true;
};

const openCopyTmcToFloorsFromPendingWork = (wa) => {
  if (!isEditable.value || isCopyToFloorsHidden.value) return;
  const mats = getMaterialAdditionsForWork(
    wa.work_set_id,
    wa.work_set_check_group_id,
    wa.floor_map_id,
  );
  if (!mats.length) {
    showWarningSnackbar('Сначала добавьте ТМЦ');
    return;
  }
  if (mats.length === 1) {
    openCopyMaterialToFloorsDialog({ ...mats[0], __isPendingAddition: true });
    return;
  }
  copyMaterialPickDialog.mode = 'pending';
  copyMaterialPickDialog.items = mats;
  copyMaterialPickDialog.open = true;
};

const onPickMaterialRowForCopyToFloors = (item) => {
  const mode = copyMaterialPickDialog.mode;
  closeCopyMaterialPickDialog();
  if (mode === 'saved') {
    openCopyMaterialToFloorsDialog(item);
  } else if (mode === 'pending') {
    openCopyMaterialToFloorsDialog({ ...item, __isPendingAddition: true });
  }
};

/** Дерево «Подъезд → доступные этажи». Заблокированные скрываем по требованию. */
const copyEntrancesView = computed(() => {
  const list = (copyToFloorsDialog.floors || []).filter(f => !f.is_blocked);
  if (!list.length) return [];
  const byEntrance = new Map();
  for (const f of list) {
    const key = f.project_entrance_id ?? `e_${f.entrance_num ?? 'none'}`;
    if (!byEntrance.has(key)) {
      byEntrance.set(key, {
        project_entrance_id: f.project_entrance_id,
        entrance_num: f.entrance_num,
        block_name: f.block_name,
        floors: [],
      });
    }
    byEntrance.get(key).floors.push(f);
  }
  return [...byEntrance.values()]
    .sort((a, b) => (a.entrance_num ?? 0) - (b.entrance_num ?? 0))
    .map(e => ({
      ...e,
      floors: e.floors
        .slice()
        .sort((a, b) => (a.level_num ?? 0) - (b.level_num ?? 0)),
    }));
});

const copySelectableFloors = computed(() =>
  (copyToFloorsDialog.floors || []).filter(f => !f.is_blocked)
);

const copyEntranceState = (entrance) => {
  const ids = entrance.floors.map(f => f.floor_map_id);
  if (!ids.length) return 'none';
  const sel = ids.filter(id => copyToFloorsDialog.selectedFloorMapIds.has(id)).length;
  if (sel === 0) return 'none';
  if (sel === ids.length) return 'all';
  return 'partial';
};

const toggleCopyFloor = (floor, val) => {
  const next = new Set(copyToFloorsDialog.selectedFloorMapIds);
  if (val) next.add(floor.floor_map_id);
  else next.delete(floor.floor_map_id);
  copyToFloorsDialog.selectedFloorMapIds = next;
};

const toggleCopyEntrance = (entrance) => {
  const ids = entrance.floors.map(f => f.floor_map_id);
  const next = new Set(copyToFloorsDialog.selectedFloorMapIds);
  const allSelected = ids.length && ids.every(id => next.has(id));
  if (allSelected) {
    ids.forEach(id => next.delete(id));
  } else {
    ids.forEach(id => next.add(id));
  }
  copyToFloorsDialog.selectedFloorMapIds = next;
};

const isAllCopySelected = computed(() => {
  const ids = copySelectableFloors.value.map(f => f.floor_map_id);
  if (!ids.length) return false;
  return ids.every(id => copyToFloorsDialog.selectedFloorMapIds.has(id));
});

const toggleSelectAllCopyFloors = () => {
  if (isAllCopySelected.value) {
    copyToFloorsDialog.selectedFloorMapIds = new Set();
    return;
  }
  const next = new Set();
  copySelectableFloors.value.forEach(f => next.add(f.floor_map_id));
  copyToFloorsDialog.selectedFloorMapIds = next;
};

/**
 * Применить snapshot работы на N target floors:
 *  - на каждый target пушим 1× workAddition + K× materialAdditions;
 *  - если на target нет ни одной строки этой группы (виртуальная),
 *    добавляем pendingChanges.newGroups, чтобы pending wa отрисовался под ней;
 *  - для source='saved' прокидываем forced-цены (work_set_price/material_price/
 *    material_price_origin/is_include_nds/nds_percent).
 */
const applyWorkCopy = (snapshot, floorMapIds) => {
  const ids = [...new Set(floorMapIds)].filter(id => id != null);
  if (!ids.length) return 0;

  const isSaved = snapshot.source === 'saved';

  for (const fid of ids) {
    const groupExists = data.value.some(r =>
      !r.isGroupHeader &&
      r.floor_map_id === fid &&
      r.work_set_check_group_id === snapshot.work_set_check_group_id
    );
    const groupAlreadyVirtual = pendingChanges.newGroups.some(g =>
      g.floor_map_id === fid &&
      g.work_set_check_group_id === snapshot.work_set_check_group_id
    );
    if (!groupExists && !groupAlreadyVirtual) {
      pendingChanges.newGroups.push({
        work_set_check_group_id: snapshot.work_set_check_group_id,
        work_set_check_group_name: snapshot.work_set_check_group_name || '',
        floor_map_id: fid,
      });
    }

    const wa = {
      work_set_id: snapshot.work_set_id,
      work_set_check_group_id: snapshot.work_set_check_group_id,
      work_set_name: snapshot.work_set_name,
      unit_name: snapshot.unit_name || '',
      work_set_price: snapshot.work_set_price,
      work_set_price_no_nds: snapshot.work_set_price_no_nds,
      floor_map_id: fid,
      qty: snapshot.qty,
      isCopyFromSaved: isSaved,
    };
    if (isSaved) {
      // forced для SP — копируем цену сметы 1-в-1, не пересчитываем на сегодня
      wa.forced_work_set_price = Number(snapshot.work_set_price) || 0;
      wa.forced_is_include_nds = !!snapshot.is_include_nds;
      wa.forced_nds_percent = snapshot.nds_percent != null ? Number(snapshot.nds_percent) : null;
    }
    pendingChanges.workAdditions.push(wa);

    for (const m of snapshot.materials || []) {
      const ma = {
        work_set_id: snapshot.work_set_id,
        work_set_check_group_id: snapshot.work_set_check_group_id,
        material_id: m.material_id,
        material_name: m.material_name,
        material_unit_name: m.material_unit_name || '',
        material_price: m.material_price != null ? Number(m.material_price) : null,
        material_price_no_nds: m.material_price_no_nds != null ? Number(m.material_price_no_nds) : null,
        floor_map_id: fid,
        qty: m.qty,
        work_qty: snapshot.qty,
        purchase_type_id: m.purchase_type_id || PURCHASE_TYPES.CONTRACTOR,
        isAvrLocked: false,
        willCreateAddwork: false,
        isCopyFromSaved: isSaved,
      };
      if (isSaved) {
        // forced ТМЦ: для type=2 material_price=0, material_price_origin = реальная цена
        const pt = m.purchase_type_id || PURCHASE_TYPES.CONTRACTOR;
        const realPrice = m.material_price_origin != null
          ? Number(m.material_price_origin)
          : Number(m.material_price) || 0;
        ma.forced_material_price = pt === 2 ? 0 : realPrice;
        ma.forced_material_price_origin = realPrice;
        ma.forced_is_include_nds = !!m.is_include_nds;
        ma.forced_nds_percent = m.nds_percent != null ? Number(m.nds_percent) : null;
      }
      pendingChanges.materialAdditions.push(ma);
    }
  }

  triggerPendingUpdate();
  return ids.length;
};

/**
 * Применить snapshot материала на N target floors. Работа на target floor
 * уже существует (бэк это гарантирует — иначе work_missing → этаж скрыт).
 */
const applyMaterialCopy = (snapshot, floorMapIds) => {
  const ids = [...new Set(floorMapIds)].filter(id => id != null);
  if (!ids.length) return 0;

  const isSaved = snapshot.source === 'saved';

  for (const fid of ids) {
    const ma = {
      work_set_id: snapshot.work_set_id,
      work_set_check_group_id: snapshot.work_set_check_group_id,
      material_id: snapshot.material_id,
      material_name: snapshot.material_name,
      material_unit_name: snapshot.material_unit_name || '',
      material_price: snapshot.material_price != null ? Number(snapshot.material_price) : null,
      material_price_no_nds: snapshot.material_price_no_nds != null
        ? Number(snapshot.material_price_no_nds)
        : null,
      floor_map_id: fid,
      qty: snapshot.qty,
      // work_qty на target floor нам неизвестен на фронте; оставим 0 — норма
      // в pending UI просто не отрисуется. Сервер пересчитает по факту.
      work_qty: 0,
      purchase_type_id: snapshot.purchase_type_id || PURCHASE_TYPES.CONTRACTOR,
      isAvrLocked: false,
      willCreateAddwork: false,
      isCopyFromSaved: isSaved,
    };
    if (isSaved) {
      const pt = snapshot.purchase_type_id || PURCHASE_TYPES.CONTRACTOR;
      const realPrice = snapshot.material_price_origin != null
        ? Number(snapshot.material_price_origin)
        : Number(snapshot.material_price) || 0;
      ma.forced_material_price = pt === 2 ? 0 : realPrice;
      ma.forced_material_price_origin = realPrice;
      ma.forced_is_include_nds = !!snapshot.is_include_nds;
      ma.forced_nds_percent = snapshot.nds_percent != null ? Number(snapshot.nds_percent) : null;
    }
    pendingChanges.materialAdditions.push(ma);
  }

  triggerPendingUpdate();
  return ids.length;
};

const confirmCopyToFloors = () => {
  if (copyToFloorsDialog.applying) return;
  const snap = copyToFloorsDialog.snapshot;
  if (!snap) return;
  const ids = [...copyToFloorsDialog.selectedFloorMapIds];
  if (!ids.length) {
    showWarningSnackbar('Выберите хотя бы один этаж');
    return;
  }
  copyToFloorsDialog.applying = true;
  let copied = 0;
  if (copyToFloorsDialog.mode === 'work') {
    copied = applyWorkCopy(snap, ids);
  } else if (copyToFloorsDialog.mode === 'material') {
    copied = applyMaterialCopy(snap, ids);
  }
  copyToFloorsDialog.applying = false;
  closeCopyToFloorsDialog();
  if (copied > 0) {
    showSucceedSnackbar(`Скопировано на ${copied} этаж(ей). Не забудьте «Сохранить»`);
  }
};

// ─── Модалка: Сохранить ───────────────────────────────────────────────────────
const saveDialog = reactive({
  open: false,
  reason: null,
  comments: '',
  reasonOptions: [],
  reasonsLoading: false,
});

const openSaveDialog = async () => {
  const invalidWork = pendingChanges.workAdditions.find(a => !toNum(a.qty) || toNum(a.qty) <= 0);
  if (invalidWork) {
    showErrorSnackbar(`Укажите кол-во для новой СМР: ${invalidWork.work_set_name}`);
    return;
  }
  const invalidMat = pendingChanges.materialAdditions.find(a => !toNum(a.qty) || toNum(a.qty) <= 0);
  if (invalidMat) {
    showErrorSnackbar(`Укажите кол-во для нового ТМЦ: ${invalidMat.material_name}`);
    return;
  }
  const noPurchaseTypeMat = pendingChanges.materialAdditions.find(a => !a.purchase_type_id);
  if (noPurchaseTypeMat) {
    showErrorSnackbar(`Укажите тип закупки (Подряд или Команда проекта) для ТМЦ: ${noPurchaseTypeMat.material_name}`);
    return;
  }

  for (const [groupKey, newQty] of pendingChanges.qtyWork) {
    if (!toNum(newQty) || toNum(newQty) <= 0) {
      const row = data.value.find(r => r.groupKey === groupKey);
      showErrorSnackbar(`Кол-во работы должно быть больше нуля: ${row?.wname || groupKey}`);
      return;
    }
    const wRow = data.value.find(r => r.groupKey === groupKey && r.work_rowspan);
    if (wRow && hasOperFactWork(wRow) && wRow.oper_fact_work_qty != null) {
      const minQty = parseFloat(wRow.oper_fact_work_qty);
      if (toNum(newQty) < minQty) {
        showErrorSnackbar(`Кол-во работы не может быть меньше опер-факта (${minQty}): ${wRow?.wname || groupKey}`);
        return;
      }
    }
  }

  for (const [key, newQty] of pendingChanges.qtyMaterial) {
    if (!toNum(newQty) || toNum(newQty) <= 0) {
      const [gk, midStr] = key.split(/_(?=\d+$)/);
      const row = data.value.find(r => r.groupKey === gk && r.material_id === parseInt(midStr));
      showErrorSnackbar(`Кол-во материала должно быть больше нуля: ${row?.mname || key}`);
      return;
    }
    const [gk2, midStr2] = key.split(/_(?=\d+$)/);
    const mRow = data.value.find(r => r.groupKey === gk2 && r.material_id === parseInt(midStr2));
    if (mRow && hasOperFactMaterial(mRow) && mRow.oper_fact_material_qty != null) {
      const minQty = parseFloat(mRow.oper_fact_material_qty);
      if (toNum(newQty) < minQty) {
        showErrorSnackbar(`Кол-во материала не может быть меньше опер-факта (${minQty}): ${mRow?.mname || key}`);
        return;
      }
    }
  }

  const floorsPayloadPre = buildFloorsPayload();
  if (floorsPayloadPre == null) return;

  // Если поменяли только вид закупа — сохраняем сразу, без диалога
  // «Причина пересчёта + комментарий». Бэкенд сам разрешит null reason_code.
  if (isOnlyPurchaseTypeChange.value) {
    await doSave(null, null, floorsPayloadPre);
    return;
  }

  saveDialog.reason = null;
  saveDialog.comments = '';
  saveDialog.open = true;
  saveDialog.reasonsLoading = true;
  const res = await getSmetaChangeReasons();
  saveDialog.reasonOptions = res || [];
  saveDialog.reasonsLoading = false;
};

// Группирует все pending-изменения по floor_map_id и возвращает массив payload'ов по этажам.
// Возвращает null и показывает ошибку, если ни для одного изменения не удалось определить этаж.
const buildFloorsPayload = () => {
  const floors = new Map();
  const ensure = (floorMapId) => {
    if (!floors.has(floorMapId)) {
      floors.set(floorMapId, {
        floor_map_id: floorMapId,
        qty_work_changes: [],
        qty_material_changes: [],
        work_set_changes: [],
        material_changes: [],
        work_deletions: [],
        material_deletions: [],
        work_additions: [],
        material_additions: [],
      });
    }
    return floors.get(floorMapId);
  };
  const findRow = (groupKey, materialId = null) => (
    materialId != null
      ? data.value.find(r => r.groupKey === groupKey && r.material_id === materialId)
      : data.value.find(r => r.groupKey === groupKey)
  );

  pendingChanges.qtyWork.forEach((newQty, groupKey) => {
    if (pendingChanges.deleteWork.has(groupKey)) return;
    const row = findRow(groupKey);
    if (!row?.floor_map_id) return;
    const wsChange = pendingChanges.workSet.get(groupKey);
    ensure(row.floor_map_id).qty_work_changes.push({
      work_set_id: wsChange ? wsChange.newWorkSetId : row.work_set_id,
      work_set_check_group_id: row.work_set_check_group_id || null,
      new_qty: newQty,
    });
  });

  pendingChanges.qtyMaterial.forEach((newQty, key) => {
    const [groupKey, materialIdStr] = key.split(/_(?=\d+$)/);
    if (pendingChanges.deleteWork.has(groupKey)) return;
    if (pendingChanges.deleteMaterial.has(key)) return;
    const materialId = parseInt(materialIdStr);
    const row = findRow(groupKey, materialId);
    if (!row?.floor_map_id) return;
    const wsChange = pendingChanges.workSet.get(groupKey);
    const matChange = pendingChanges.material.get(key);
    ensure(row.floor_map_id).qty_material_changes.push({
      work_set_id: wsChange ? wsChange.newWorkSetId : row.work_set_id,
      work_set_check_group_id: row.work_set_check_group_id || null,
      material_id: matChange ? matChange.newMaterialId : materialId,
      new_qty: newQty,
    });
  });

  pendingChanges.workSet.forEach((change, groupKey) => {
    if (pendingChanges.deleteWork.has(groupKey)) return;
    const row = findRow(groupKey);
    if (!row?.floor_map_id) return;
    ensure(row.floor_map_id).work_set_changes.push({
      old_work_set_id: change.oldWorkSetId,
      new_work_set_id: change.newWorkSetId,
      work_set_check_group_id: row.work_set_check_group_id || null,
    });
  });

  pendingChanges.material.forEach((change, key) => {
    const [groupKey, materialIdStr] = key.split(/_(?=\d+$)/);
    if (pendingChanges.deleteWork.has(groupKey)) return;
    const materialId = parseInt(materialIdStr);
    const row = findRow(groupKey, materialId);
    if (!row?.floor_map_id) return;
    const wsChange = pendingChanges.workSet.get(groupKey);
    ensure(row.floor_map_id).material_changes.push({
      work_set_id: wsChange ? wsChange.newWorkSetId : row.work_set_id,
      work_set_check_group_id: row.work_set_check_group_id || null,
      old_material_id: change.oldMaterialId,
      new_material_id: change.newMaterialId,
      new_material_price: change.newMaterialPrice ?? null,
      purchase_type_id: change.purchaseTypeId || row.purchase_type_id || 1,
    });
  });

  pendingChanges.purchaseType.forEach((typeId, key) => {
    if (pendingChanges.material.has(key)) return;
    const [groupKey, materialIdStr] = key.split(/_(?=\d+$)/);
    if (pendingChanges.deleteWork.has(groupKey)) return;
    if (pendingChanges.deleteMaterial.has(key)) return;
    const materialId = parseInt(materialIdStr);
    const row = findRow(groupKey, materialId);
    if (!row?.floor_map_id) return;
    const wsChange = pendingChanges.workSet.get(groupKey);
    ensure(row.floor_map_id).material_changes.push({
      work_set_id: wsChange ? wsChange.newWorkSetId : row.work_set_id,
      work_set_check_group_id: row.work_set_check_group_id || null,
      old_material_id: materialId,
      new_material_id: materialId,
      new_material_price: null,
      purchase_type_id: typeId,
    });
  });

  pendingChanges.deleteWork.forEach((val, groupKey) => {
    const row = findRow(groupKey);
    if (!row?.floor_map_id) return;
    ensure(row.floor_map_id).work_deletions.push(val);
  });

  pendingChanges.deleteMaterial.forEach((val, key) => {
    const [groupKey, materialIdStr] = key.split(/_(?=\d+$)/);
    // материалы конструктива, удаляемого целиком, не добавляем — бэкенд удалит их сам
    if (pendingChanges.deleteWork.has(groupKey)) return;
    const materialId = parseInt(materialIdStr);
    const row = findRow(groupKey, materialId);
    if (!row?.floor_map_id) return;
    ensure(row.floor_map_id).material_deletions.push(val);
  });

  pendingChanges.workAdditions.forEach(a => {
    if (!a.floor_map_id) return;
    const item = {
      work_set_id: a.work_set_id,
      work_set_check_group_id: a.work_set_check_group_id,
      qty: Number(a.qty) || 0,
    };
    // Цена и НДС из диалога «Добавить СМР» (или forced при copy-to-floors).
    // Без них SP берёт НДС из первой строки этажа — может не совпасть с project_tab.
    if (a.forced_work_set_price != null) {
      item.work_set_price = Number(a.forced_work_set_price);
    } else if (a.work_set_price != null) {
      item.work_set_price = Number(a.work_set_price);
    }
    if (a.forced_is_include_nds != null) {
      item.is_include_nds = !!a.forced_is_include_nds;
    } else if (a.is_include_nds != null) {
      item.is_include_nds = !!a.is_include_nds;
    }
    if (a.forced_nds_percent != null) {
      item.nds_percent = Number(a.forced_nds_percent);
    } else if (a.nds_percent != null) {
      item.nds_percent = Number(a.nds_percent);
    }
    ensure(a.floor_map_id).work_additions.push(item);
  });

  pendingChanges.materialAdditions.forEach(a => {
    if (!a.floor_map_id) return;
    const matchRow = data.value.find(r =>
      !r.isGroupHeader &&
      r.work_set_id === a.work_set_id &&
      r.work_set_check_group_id === a.work_set_check_group_id &&
      r.floor_map_id === a.floor_map_id
    );
    if (matchRow && pendingChanges.deleteWork.has(matchRow.groupKey)) return;
    let wsId = a.work_set_id;
    if (matchRow) {
      const wsChange = pendingChanges.workSet.get(matchRow.groupKey);
      if (wsChange) wsId = wsChange.newWorkSetId;
    }
    const item = {
      work_set_id: wsId,
      work_set_check_group_id: a.work_set_check_group_id,
      material_id: a.material_id,
      qty: Number(a.qty) || 0,
      purchase_type_id: a.purchase_type_id || 1,
    };
    // Forced цены ТМЦ при copy-to-floors из сохранённого источника:
    // material_price_origin — реальная цена; material_price = 0 для type=2,
    // = origin для type=1 (см. SP smeta_new__save_changes).
    if (a.forced_material_price != null) {
      item.material_price = Number(a.forced_material_price);
    }
    if (a.forced_material_price_origin != null) {
      item.material_price_origin = Number(a.forced_material_price_origin);
    }
    if (a.forced_is_include_nds != null) {
      item.is_include_nds = !!a.forced_is_include_nds;
    }
    if (a.forced_nds_percent != null) {
      item.nds_percent = Number(a.forced_nds_percent);
    }
    ensure(a.floor_map_id).material_additions.push(item);
  });

  if (floors.size === 0) {
    showErrorSnackbar('Не удалось определить этажи для сохранения');
    return null;
  }
  return [...floors.values()];
};

// Общая часть сохранения. Используется и из confirmSave (после диалога с
// выбором причины/комментария), и из openSaveDialog при кейсе «только вид
// закупа поменяли» (когда диалог не показываем).
const doSave = async (reasonCode, comments, prebuiltFloorsPayload = null) => {
  const floorsPayload = prebuiltFloorsPayload ?? buildFloorsPayload();
  if (floorsPayload == null) return;

  isSaving.value = true;
  const res = await saveSmetaNewChanges({
    project_id: props.projectId,
    project_entrance_id: props.projectEntranceId || null,
    reason_code: reasonCode || null,
    comments: comments || null,
    floors: floorsPayload,
  });
  isSaving.value = false;
  if (!res) return;

  resetPendingChanges();
  await loadData();
  await loadProjectState();
};

const confirmSave = async () => {
  if (!saveDialog.reason) return;

  const floorsPayload = buildFloorsPayload();
  if (floorsPayload == null) {
    saveDialog.open = false;
    return;
  }

  const reasonCode = saveDialog.reason;
  const comments = saveDialog.comments || null;
  saveDialog.open = false;

  await doSave(reasonCode, comments, floorsPayload);
};

// ─── lifecycle: infinite scroll ───────────────────────────────────────────────
onMounted(() => {
  nextTick(() => {
    const wrapper = tableWrapperRef.value;
    if (wrapper) {
      _scrollEl = wrapper.querySelector('.q-table__middle');
      if (_scrollEl) _scrollEl.addEventListener('scroll', onTableScroll, { passive: true });
    }
  });
});
onBeforeUnmount(() => {
  if (_scrollEl) {
    _scrollEl.removeEventListener('scroll', onTableScroll);
    _scrollEl = null;
  }
});

// ─── watch ─────────────────────────────────────────────────────────────────────
watch(
  () => [
    props.projectId,
    props.projectEntranceId,
    props.what,
    props.floorMapId,
    JSON.stringify(props.workSetIds || []),
  ],
  () => {
    resetPendingChanges();
    loadData();
    loadProjectState();
    void loadPackageMaterialSetsOnce();
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.package-table {
  margin-top: 0;
}

.unsent-ds-banner {
  background: linear-gradient(90deg, #d32f2f 0%, #e57373 100%);
  color: #fff;

  :deep(.q-banner__avatar) {
    align-items: flex-start;
  }

  a,
  .q-btn {
    color: #fff;
  }
}

.smeta-action-bar {
  position: sticky;
  top: 170px;
  z-index: 11;
  background: #fff;
  padding: 8px 0;
}

.smeta-totals {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  &__main {
    display: flex;
    align-items: center;
    gap: 16px;
    font-weight: 700;
    font-size: 16px;
  }

  &__label {
    font-size: 11px;
    color: #888;
    font-weight: 500;
  }

  &__divider {
    width: 1px;
    height: 32px;
    background: #ddd;
  }

  &__detail {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #666;
  }

  &__dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #ccc;
  }
}


.q-table tbody tr:hover {
  background-color: #f0f6ff;
}

.group-header-row {
  .group-header-cell {
    background-color: #e8eaf6;
    font-weight: 700;
    font-size: 13px;
    padding: 4px 8px 4px 12px !important;
    color: #3949ab;
    border-top: 2px solid #c5cae9;
  }

  .group-header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .group-floor-hint {
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 8px;
    background: #fff;
    color: #2e7d32;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.2px;
  }

  &--new .group-header-cell {
    background-color: #e8f5e9;
    color: #2e7d32;
    border-top: 2px solid #a5d6a7;
  }
}

.group-locked-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 12px;
  padding: 2px 10px;
  border-radius: 4px;
  background-color: #ffebee;
  color: #c62828;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  vertical-align: middle;
}

:deep(.cell-changed) {
  background-color: #fffde7 !important;
}

.multi-material-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 6px;
  border-radius: 8px;
  background-color: #eef2ff;
  color: #4f46e5;
  font-size: 10px;
  font-weight: 600;
}

.clickable-cell {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .edit-icon {
    opacity: 0;
    color: #888;
    transition: opacity 0.15s;
  }

  &:hover {
    text-decoration: underline;
    text-decoration-style: dashed;

    .edit-icon {
      opacity: 1;
    }
  }
}

.qty-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .qty-edit-btn {
    opacity: 0;
    transition: opacity 0.15s;
  }
}

tr:hover .qty-edit-btn {
  opacity: 1;
}

.cell-text-block {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.old-value {
  font-size: 11px;
  color: #9e9e9e;
  text-decoration: line-through;
  white-space: normal;
  line-height: 1.3;
}

.new-value {
  font-weight: 700;
  color: #e65100;
  white-space: normal;
  line-height: 1.3;
}

.changed-val {
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  &__old {
    font-size: 10px;
    color: #9e9e9e;
    text-decoration: line-through;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__new {
    font-weight: 700;
    color: #e65100;
    line-height: 1.3;
    white-space: nowrap;
  }
}

.qty-values {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.unit-inline {
  font-size: 10px;
  color: #9e9e9e;
  font-style: italic;
  line-height: 1.2;
  margin-top: 1px;
}

.entrance-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.3;

  .entrance-label {
    font-weight: 600;
    font-size: 12px;
  }
  .floor-label {
    font-size: 11px;
    color: #666;
  }
}

.norm-cell {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.price-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.nds-badge {
  font-size: 9px !important;
  padding: 1px 4px !important;
  opacity: 0.85;
}

.status-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  min-width: 110px;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.status-label {
  font-size: 10px;
  color: #888;
  font-weight: 600;
  min-width: 28px;
}

.status-amount {
  font-size: 11px;
}

.row-pending-delete {
  opacity: 0.55;
}

:deep(.cell-pending-delete) {
  background-color: #fff1f1 !important;

  .cell-text-block span:not(.old-value),
  .clickable-cell > span {
    text-decoration: line-through;
    text-decoration-color: #e53935;
    color: #e53935;
  }
}

.work-cell-wrap {
  display: flex;
  align-items: center;
  gap: 4px;

  .revert-btn {
    flex-shrink: 0;
  }

  .delete-btn {
    opacity: 0;
    flex-shrink: 0;
    transition: opacity 0.15s;
  }
}

tr:hover .delete-btn {
  opacity: 1;
}

:deep(.th-main) {
  font-weight: 700 !important;
  font-size: 12px;
}

:deep(.th-smr) {
  font-weight: 600 !important;
  font-size: 11px;
  background-color: #f8f9fa !important;
}

:deep(.th-mat) {
  font-weight: 600 !important;
  font-size: 11px;
  background-color: #f0f4ff !important;
}

.th-group-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #7986cb;
  line-height: 1;
  margin-bottom: 2px;
}

.split-header {
  padding: 0 !important;

  .split-header-top {
    padding: 4px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .split-header-bottom {
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
}

.split-data {
  display: flex;
  flex-direction: column;

  .split-data-top {
    padding: 2px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .split-data-bottom {
    padding: 2px 0;
    color: #555;
  }
}

:deep(.hide-spin-buttons) {
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.action-add-btn {
  white-space: nowrap;
  font-size: 11px !important;
}

.pending-action-menu-btn {
  white-space: nowrap;
  font-size: 11px !important;
}

.action-refresh-price-btn {
  white-space: nowrap;
  font-size: 11px !important;
}

/* Баннер режима выбора СМР для multi-update цен */
.smr-selection-banner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 4px;
  color: #5d4037;
  font-size: 13px;
  font-weight: 500;
}

/* Чек-бокс с замком слева (в ячейке «Подъезд/Этаж») при режиме выбора СМР */
.smr-select-cell {
  display: flex;
  align-items: center;
  gap: 2px;
  justify-content: center;
  margin-bottom: 2px;
  padding-bottom: 2px;
  border-bottom: 1px dashed #e0e0e0;
}
.smr-select-lock {
  margin-left: 2px;
}

/* Баннер режима выбора ТМЦ для multi-update цен */
.tmc-selection-banner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  border-radius: 4px;
  color: #00695c;
  font-size: 13px;
  font-weight: 500;
}

/* Чек-бокс с замком слева в ячейке «Материал» при режиме выбора ТМЦ */
.tmc-select-cell {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 4px;
  padding-bottom: 2px;
  border-bottom: 1px dashed #b2dfdb;
}
.tmc-select-lock {
  margin-left: 2px;
}

:deep(.material-name-cell) {
  border-left: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.pending-addition-row {
  .pending-addition-cell {
    background-color: #e8f5e9 !important;
    padding: 6px 12px !important;
    border-left: 3px solid #4caf50;
  }
}

.pending-ma-remove-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  opacity: 0.6;
  &:hover { opacity: 1; }
}
.pending-ma-copy-btn {
  position: absolute;
  top: 2px;
  right: 26px;
  opacity: 0.6;
  &:hover { opacity: 1; }
}

.purchase-type-row {
  display: flex;
  gap: 4px;
  margin-top: 3px;
}

.purchase-type-tag {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  color: #9e9e9e;
  background: #fff;
  line-height: 1.4;
  transition: all 0.15s;
  user-select: none;

  &:hover {
    border-color: #90caf9;
    color: #1976d2;
  }

  &--active {
    background: #e3f2fd;
    color: #1565c0;
    border-color: #90caf9;
    font-weight: 600;
  }

  &--changed {
    &.purchase-type-tag--active {
      background: #fff3e0;
      color: #e65100;
      border-color: #ffb74d;
    }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.pending-addition-badge {
  font-size: 10px !important;
}

/* Групповое редактирование: тип закупа материала */
.bulk-pt-card {
  min-width: 720px;
  max-width: 900px;
  width: 100%;
}
.bulk-pt-stepper {
  padding-top: 0;
}
.bulk-pt-list {
  max-height: 420px;
  overflow-y: auto;
  border-radius: 4px;
}
.bulk-pt-item--active {
  background: #e3f2fd;
}
.bulk-pt-locations {
  max-height: 460px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.bulk-pt-entrance {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
  padding: 8px 12px;
}
.bulk-pt-entrance-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #e0e0e0;
}
.bulk-pt-entrance-title {
  font-weight: 600;
  font-size: 14px;
}
.bulk-pt-entrance-meta {
  margin-left: auto;
  font-size: 11px;
  color: #757575;
}
.bulk-pt-floors {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 4px 12px;
  padding-top: 6px;
}
.bulk-pt-floor {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 4px;
}
.bulk-pt-floor:hover:not(.bulk-pt-floor--locked) {
  background: #f1f8ff;
}
.bulk-pt-floor--locked {
  opacity: 0.55;
}
.bulk-pt-floor-label {
  font-size: 13px;
  min-width: 56px;
}
.bulk-pt-pt-badge {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid transparent;
  line-height: 1.4;
}
.bulk-pt-pt-badge--contractor {
  background: #e3f2fd;
  color: #1565c0;
  border-color: #90caf9;
}
.bulk-pt-pt-badge--team {
  background: #e8f5e9;
  color: #2e7d32;
  border-color: #a5d6a7;
}
.bulk-pt-pt-badge--mixed {
  background: #fff8e1;
  color: #f57f17;
  border-color: #ffe082;
}
.bulk-pt-pt-choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.bulk-pt-pt-choices--single {
  grid-template-columns: 1fr;
  max-width: 280px;
  margin: 0 auto;
}
.bulk-norm-scope-node {
  margin-left: 20px;
  margin-top: 4px;
}
.bulk-norm-scope-node--group {
  margin-left: 16px;
}
.bulk-norm-scope-node--work {
  margin-left: 16px;
}
.bulk-norm-scope-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 4px;
  border-radius: 4px;
}
.bulk-norm-scope-row:hover {
  background: #f1f8ff;
}
.bulk-norm-scope-label {
  font-size: 13px;
}
.bulk-norm-consumption-step {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.bulk-norm-consumption-apply-all .bulk-norm-custom-input--narrow {
  width: 200px;
  max-width: 200px;
}
.bulk-norm-consumption-list {
  max-height: 460px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.bulk-norm-consumption-item {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 12px;
  background: #fafafa;
}
.bulk-norm-consumption-work-head {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}
.bulk-norm-consumption-work-name {
  font-weight: 600;
  font-size: 14px;
}
.bulk-norm-consumption-work-meta {
  font-size: 11px;
  color: #757575;
}
.bulk-norm-preset-table {
  max-height: 280px;
}
.bulk-norm-preset-table :deep(.q-table__middle) {
  max-height: 240px;
}
.bulk-norm-preset-table :deep(tbody tr.bulk-norm-preset-row) {
  cursor: pointer;
}
.bulk-norm-preset-table :deep(tbody tr.bulk-norm-preset-row:hover td) {
  background: #f5f9ff;
}
.bulk-norm-preset-table :deep(tbody tr.bulk-norm-preset-row--active td) {
  background: #e3f2fd !important;
  font-weight: 600;
}
.bulk-norm-preset-table :deep(tbody tr.bulk-norm-preset-row--active) {
  box-shadow: inset 0 0 0 2px #1976d2;
}
.bulk-norm-custom-input {
  margin-top: 8px;
}
.bulk-norm-custom-input--active :deep(.q-field__control) {
  border-color: #1976d2;
}
.bulk-pt-pt-choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 14px;
  color: #424242;
}
.bulk-pt-pt-choice:hover {
  border-color: #90caf9;
  color: #1565c0;
}
.bulk-pt-pt-choice--active {
  border-color: #1976d2;
  background: #e3f2fd;
  color: #0d47a1;
  font-weight: 600;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
}
.bulk-pt-pt-choice-title {
  font-size: 14px;
}
.bulk-pt-actions {
  border-top: 1px solid #f0f0f0;
}
.bulk-pt-drafts-section {
  background: #f5f7fb;
  border-bottom: 1px solid #e0e0e0;
  padding: 10px 16px;
}
.bulk-pt-drafts-title {
  font-size: 12px;
  font-weight: 600;
  color: #5d4037;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.bulk-pt-drafts-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 140px;
  overflow-y: auto;
}
.bulk-pt-draft-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 12px;
}
.bulk-pt-draft-name {
  font-weight: 500;
  color: #212121;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
}
.bulk-pt-draft-count {
  margin-left: auto;
  color: #757575;
  font-size: 11px;
}

/* ─── Модалка «Скопировать на этажи» ─── */
.copy-floors-card {
  width: 100%;
}
.copy-floors-source {
  background: #f5f7fb;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 16px;
}
.copy-floors-source-line {
  display: flex;
  align-items: center;
  gap: 6px;
}
.copy-floors-source-name {
  font-weight: 600;
  font-size: 13px;
  color: #212121;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 460px;
}
.copy-floors-source-unit {
  font-size: 11px;
  color: #757575;
}
.copy-floors-source-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #555;
}
.copy-floors-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e0e0e0;
  margin-bottom: 8px;
}
.copy-floors-counter {
  font-size: 12px;
  color: #555;
  margin-left: auto;
}
.copy-floors-list {
  max-height: 460px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.copy-floors-entrance {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
  padding: 8px 12px;
}
.copy-floors-entrance-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #e0e0e0;
}
.copy-floors-entrance-title {
  font-weight: 600;
  font-size: 14px;
}
.copy-floors-block {
  margin-left: 6px;
  font-weight: 400;
  font-size: 11px;
  color: #757575;
}
.copy-floors-entrance-meta {
  margin-left: auto;
  font-size: 11px;
  color: #757575;
}
.copy-floors-floors {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 4px 12px;
  padding-top: 6px;
}
.copy-floors-floor {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
}
.copy-floors-floor:hover {
  background: #f1f8ff;
}
.copy-floors-floor-label {
  font-size: 13px;
}

.pending-addition-name {
  font-weight: 600;
  font-size: 12px;
  color: #2e7d32;
}

.actions-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.pending-mat-item {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 4px;
  background: #e8f5e9;
  border-radius: 4px;
  border: 1px solid #c8e6c9;
  max-width: 140px;
}

.pending-mat-name {
  font-size: 10px;
  font-weight: 500;
  color: #2e7d32;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
}
</style>
